import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import './tmi.css';
import './Packlists.css';
import './MasterProjects.css';
import { connect, ConnectedProps } from 'react-redux';
import { mapStateToProps, mapDispatchToProps } from '../../redux/reduxActions';
import { UserInfo } from '../../models/models';
import {
    Grid,
    GridColumn,
    GridItemChangeEvent,
    GridCellProps,
} from '@progress/kendo-react-grid';
import { orderBy, SortDescriptor, FilterDescriptor } from '@progress/kendo-data-query';
import { Checkbox } from '@progress/kendo-react-inputs';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import IconPickerDialog from './IconPickerDialog';

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

type PacklistTypesProps = PropsFromRedux & {
    menuItemId?: number;
    stateToLoad?: PacklistTypesState;
    onSaveState?: (menuItemId: number, state: PacklistTypesState) => void;
};

interface LookupListItem {
    value: string;
    display: string;
}

interface PacklistTypeRow {
    packlistTypeID: number;
    packlistTypeCode: string;
    description: string;
    allowMultiScan: boolean;
    iconAllGood: number | null;
    displayFormat: string;
    restrictToAllowedItemTypesOnly: boolean;
    lastChangedBy: string;
    lastChangedOn: Date | null;
}

interface PacklistTypeHierarchyNode {
    id: number;
    code: string;
    description: string;
}

interface PacklistTypesState {
    data: PacklistTypeRow[];
    loading: boolean;
    errorMsg: string;
    sort: SortDescriptor[];
    filter: FilterDescriptor[];
    filterText: string;
    filterTexts: { [key: string]: string };
    headerFocusField: string | null;
    editItemId: number | null;
    editViewOrderIds: number[] | null;
    originalItem: PacklistTypeRow | null;
    selectedIds: Set<number>;
    deleteConfirmItem: PacklistTypeRow | null;
    deleteInProgress: boolean;
    deleteErrorMsg: string;
    newDialogOpen: boolean;
    newDialogSaving: boolean;
    newDialogError: string;
    newDialogCode: string;
    newDialogDesc: string;
    newDialogAllowMultiScan: boolean;
    newDialogIconAllGood: number | null;
    newDialogDisplayFormat: string;
    newDialogRestrictToAllowedItemTypes: boolean;
    iconPickerOpen: boolean;
    iconPickerLoading: boolean;
    iconPickerError: string;
    iconPickerCodes: number[];
    iconPickerTargetMode: 'edit' | 'new' | null;
    iconPickerTargetPacklistTypeID: number | null;
    iconPickerSelectedCode: number | null;
    iconPickerTab: 'kendo' | 'unicode';
    iconPickerFilterText: string;
    menuDataItem: PacklistTypeRow | null;
    menuAnchor: HTMLElement | null;
    menuAnchorRect: { left: number; top: number; right: number; bottom: number; width: number; height: number } | null;
    menuPosition: { left: number; top: number } | null;
    clientQueriesDlgOpen: boolean;
    clientQueriesPacklistType: PacklistTypeRow | null;
    clientQueriesData: Array<{
        PackListTypeClientQueryID: number;
        ClientQueryID: number | null;
        display: string;
        DisplayText: string;
        SortOrder: number;
        isSelected: boolean;
        inEdit?: boolean;
    }>;
    clientQueriesDlgLoading: boolean;
    clientQueriesDlgError: string;
    clientQueriesSort: SortDescriptor[];
    clientQueriesFilter: FilterDescriptor[];
    clientQueriesFilterTexts: { [key: string]: string };
    clientQueriesFocusField?: string;
    clientQueriesSelectedId: number | null;
    clientQueriesEditingId: number | null;
    clientQueriesDropdownData: LookupListItem[];
    clientQueriesDropdownLoading: boolean;
    clientQueriesDeleteConfirmId: number | null;
    clientQueriesDeleteInProgress: boolean;
    clientQueriesDeleteError: string;
    attributesDlgOpen: boolean;
    attributesPacklistType: PacklistTypeRow | null;
    attributesData: Array<{
        PackListTypeAttributeID: number;
        PackListAttributeID: number | null;
        display: string;
        Description: string;
        GroupDesc: string;
        GroupOrder: number | null;
        SortOrder: number;
        TabName: string;
        MetaDataID: number | null;
        isSelected: boolean;
        inEdit?: boolean;
    }>;
    attributesDlgLoading: boolean;
    attributesDlgError: string;
    attributesSort: SortDescriptor[];
    attributesFilter: FilterDescriptor[];
    attributesFilterTexts: { [key: string]: string };
    attributesFocusField?: string;
    attributesSelectedId: number | null;
    attributesEditingId: number | null;
    attributesDropdownData: LookupListItem[];
    attributesDropdownLoading: boolean;
    attributesMetadataDropdownData: LookupListItem[];
    attributesMetadataDropdownLoading: boolean;
    attributesDeleteConfirmId: number | null;
    attributesDeleteInProgress: boolean;
    attributesDeleteError: string;
    milestonesDlgOpen: boolean;
    milestonesPacklistType: PacklistTypeRow | null;
    milestonesDropdownData: LookupListItem[];
    milestonesDropdownLoading: boolean;
    milestoneDlgLoading: boolean;
    milestoneDlgError: string;
    milestoneDlgFilterText: string;
    milestonesSelected: Set<string>;
    statusDlgOpen: boolean;
    statusPacklistType: PacklistTypeRow | null;
    statusData: Array<{
        PackListTypeStatusID: number;
        PackListStatusID: number | null;
        display: string;
        PackListTypeID: number;
        PacklistTypeStatusOrder: number | null;
        LastChangedBy: string;
        LastChangedOn: Date | null;
        isSelected: boolean;
        inEdit?: boolean;
    }>;
    statusDlgLoading: boolean;
    statusDlgError: string;
    statusSort: SortDescriptor[];
    statusFilter: FilterDescriptor[];
    statusFilterTexts: { [key: string]: string };
    statusFocusField?: string;
    statusSelectedId: number | null;
    statusEditingId: number | null;
    statusDropdownData: LookupListItem[];
    statusDropdownLoading: boolean;
    statusDeleteConfirmId: number | null;
    statusDeleteInProgress: boolean;
    statusDeleteError: string;
    allowedItemTypesDlgOpen: boolean;
    allowedItemTypesPacklistType: PacklistTypeRow | null;
    allowedItemTypesDropdownData: LookupListItem[];
    allowedItemTypesDropdownLoading: boolean;
    allowedItemTypesDlgLoading: boolean;
    allowedItemTypesDlgError: string;
    allowedItemTypesDlgFilterText: string;
    allowedItemTypesSelected: Set<string>;
    allowedPacklistTypesDlgOpen: boolean;
    allowedPacklistTypesPacklistType: PacklistTypeRow | null;
    allowedPacklistTypesData: Array<{
        PackListTypeAllowedPackListTypesID: number;
        PackListTypeID: number;
        AllowedPackListTypeID: number | null;
        display: string;
        AllowMultipleParentsOfSameType: boolean;
        LastChangedBy: string;
        LastChangedOn: Date | null;
        inEdit?: boolean;
    }>;
    allowedPacklistTypesDlgLoading: boolean;
    allowedPacklistTypesDlgError: string;
    allowedPacklistTypesSort: SortDescriptor[];
    allowedPacklistTypesFilter: FilterDescriptor[];
    allowedPacklistTypesFilterTexts: { [key: string]: string };
    allowedPacklistTypesFocusField?: string;
    allowedPacklistTypesSelectedId: number | null;
    allowedPacklistTypesEditingId: number | null;
    allowedPacklistTypesDropdownData: LookupListItem[];
    allowedPacklistTypesDropdownLoading: boolean;
    allowedPacklistTypesDeleteConfirmId: number | null;
    allowedPacklistTypesDeleteInProgress: boolean;
    allowedPacklistTypesDeleteError: string;
    hierarchyDlgOpen: boolean;
    hierarchyPacklistType: PacklistTypeRow | null;
    hierarchyLoading: boolean;
    hierarchyError: string;
    hierarchyNodes: { [key: number]: PacklistTypeHierarchyNode };
    hierarchyChildrenByParent: { [key: number]: number[] };
    hierarchyParentsByChild: { [key: number]: number[] };
    hierarchyExpanded: { [key: string]: boolean };
    uniqueGroupsDlgOpen: boolean;
    uniqueGroupsPacklistType: PacklistTypeRow | null;
    uniqueGroupsDropdownData: LookupListItem[];
    uniqueGroupsDropdownLoading: boolean;
    uniqueGroupsDlgLoading: boolean;
    uniqueGroupsDlgError: string;
    uniqueGroupsDlgFilterText: string;
    uniqueGroupsSelected: Set<string>;
    userRolesDlgOpen: boolean;
    userRolesPacklistType: PacklistTypeRow | null;
    userRolesDropdownData: LookupListItem[];
    userRolesDropdownLoading: boolean;
    userRolesDlgLoading: boolean;
    userRolesDlgError: string;
    userRolesDlgFilterText: string;
    userRolesSelected: Set<string>;
    attributeEditorDlgOpen: boolean;
    attributeEditorAttribute: {
        PackListTypeAttributeID: number;
        PackListAttributeID: number | null;
        display: string;
        Description: string;
        GroupDesc: string;
        GroupOrder: number | null;
        SortOrder: number;
        TabName: string;
        MetaDataID: number | null;
    } | null;
    attributeEditorUserRolesData: Array<{
        UserRolePackListAttributeID: number;
        PackListAttributeID: number;
        UserRoleID: number | null;
        display: string;
        Editable: boolean;
        LastChangedBy: string;
        LastChangedOn: Date | null;
        inEdit?: boolean;
    }>;
    attributeEditorUserRolesDropdownData: LookupListItem[];
    attributeEditorUserRolesDlgLoading: boolean;
    attributeEditorUserRolesDlgError: string;
    attributeEditorUserRolesDeleteConfirmId: number | null;
    attributeEditorUserRolesDeleteInProgress: boolean;
    attributeEditorUserRolesDeleteError: string;
    packlistItemAttributesDlgOpen: boolean;
    packlistItemAttributesPacklistType: PacklistTypeRow | null;
    packlistItemAttributesData: Array<{
        PackListTypeItemAttributeID: number;
        AttributeID: number | null;
        display: string;
        Description: string;
        GroupDesc: string;
        GroupOrder: number | null;
        SortOrder: number;
        TabName: string;
        ShowInDetails: boolean;
        MetaDataID: number | null;
        isSelected: boolean;
        inEdit?: boolean;
    }>;
    packlistItemAttributesDlgLoading: boolean;
    packlistItemAttributesDlgError: string;
    packlistItemAttributesSort: SortDescriptor[];
    packlistItemAttributesFilter: FilterDescriptor[];
    packlistItemAttributesFilterTexts: { [key: string]: string };
    packlistItemAttributesFocusField?: string;
    packlistItemAttributesSelectedId: number | null;
    packlistItemAttributesEditingId: number | null;
    packlistItemAttributesDropdownData: LookupListItem[];
    packlistItemAttributesDropdownLoading: boolean;
    packlistItemAttributesMetadataDropdownData: LookupListItem[];
    packlistItemAttributesMetadataDropdownLoading: boolean;
    packlistItemAttributesDeleteConfirmId: number | null;
    packlistItemAttributesDeleteInProgress: boolean;
    packlistItemAttributesDeleteError: string;
    packlistItemAttributesUserRolesDlgOpen: boolean;
    packlistItemAttributesUserRolesItemAttribute: {
        PackListTypeItemAttributeID: number;
        AttributeID: number | null;
        display: string;
        Description: string;
        GroupDesc: string;
        GroupOrder: number | null;
        SortOrder: number;
        TabName: string;
        ShowInDetails: boolean;
        MetaDataID: number | null;
    } | null;
    packlistItemAttributesUserRolesData: Array<{
        UserRolePacklistItemAttributeID: number;
        AttributeID: number;
        UserRoleID: number | null;
        display: string;
        Editable: boolean;
        LastChangedBy: string;
        LastChangedOn: Date | null;
        inEdit?: boolean;
    }>;
    packlistItemAttributesUserRolesDropdownData: LookupListItem[];
    packlistItemAttributesUserRolesDlgLoading: boolean;
    packlistItemAttributesUserRolesDlgError: string;
    packlistItemAttributesUserRolesDeleteConfirmId: number | null;
    packlistItemAttributesUserRolesDeleteInProgress: boolean;
    packlistItemAttributesUserRolesDeleteError: string;
}

const PacklistTypes: React.FC<PacklistTypesProps> = (props) => {
    const STORAGE_KEY = 'packlisttypes_state_v1';
    const EDIT_FIELD = 'inEdit';
    // Embedded per-row selection flag, computed into gridData below. Letting cached body cell
    // renderers read this directly off props.dataItem (rather than closing over state.selectedIds)
    // is what lets them keep a stable identity across renders - see bodyCellRenderersRef.
    const SELECTED_FIELD = 'inSelected';

    const savedRaw = (() => {
        try { return sessionStorage.getItem(STORAGE_KEY); } catch { return null; }
    })();
    const saved = savedRaw ? JSON.parse(savedRaw) : null;

    // When navigating away to another menu item and back, the parent App restores
    // the full previous state via stateToLoad/onSaveState, so the page doesn't need
    // to refilter/resort/reselect. On first visit (or a full page reload) there is
    // no stateToLoad, so fall back to the sessionStorage-seeded defaults (sessionStorage
    // is kept only as a fallback for a fresh page load/reload).
    const [state, setState] = useState<PacklistTypesState>(props.stateToLoad ?? {
        data: [],
        loading: false,
        errorMsg: '',
        sort: saved?.sort ?? [{ field: 'packlistTypeCode', dir: 'asc' }],
        filter: saved?.filter ?? [],
        filterText: saved?.filterText ?? '',
        filterTexts: saved?.filterTexts ?? {},
        headerFocusField: null,
        editItemId: null,
        editViewOrderIds: null,
        originalItem: null,
        selectedIds: new Set(saved?.selectedIds ?? []),
        deleteConfirmItem: null,
        deleteInProgress: false,
        deleteErrorMsg: '',
        newDialogOpen: false,
        newDialogSaving: false,
        newDialogError: '',
        newDialogCode: '',
        newDialogDesc: '',
        newDialogAllowMultiScan: false,
        newDialogIconAllGood: null,
        newDialogDisplayFormat: '',
        newDialogRestrictToAllowedItemTypes: false,
        iconPickerOpen: false,
        iconPickerLoading: false,
        iconPickerError: '',
        iconPickerCodes: [],
        iconPickerTargetMode: null,
        iconPickerTargetPacklistTypeID: null,
        iconPickerSelectedCode: null,
        iconPickerTab: 'kendo',
        iconPickerFilterText: '',
        menuDataItem: null,
        menuAnchor: null,
        menuAnchorRect: null,
        menuPosition: null,
        clientQueriesDlgOpen: false,
        clientQueriesPacklistType: null,
        clientQueriesData: [],
        clientQueriesDlgLoading: false,
        clientQueriesDlgError: '',
        clientQueriesSort: [{ field: 'display', dir: 'asc' }],
        clientQueriesFilter: [],
        clientQueriesFilterTexts: {},
        clientQueriesFocusField: undefined,
        clientQueriesSelectedId: null,
        clientQueriesEditingId: null,
        clientQueriesDropdownData: [],
        clientQueriesDropdownLoading: false,
        clientQueriesDeleteConfirmId: null,
        clientQueriesDeleteInProgress: false,
        clientQueriesDeleteError: '',
        attributesDlgOpen: false,
        attributesPacklistType: null,
        attributesData: [],
        attributesDlgLoading: false,
        attributesDlgError: '',
        attributesSort: [{ field: 'Description', dir: 'asc' }],
        attributesFilter: [],
        attributesFilterTexts: {},
        attributesFocusField: undefined,
        attributesSelectedId: null,
        attributesEditingId: null,
        attributesDropdownData: [],
        attributesDropdownLoading: false,
        attributesMetadataDropdownData: [],
        attributesMetadataDropdownLoading: false,
        attributesDeleteConfirmId: null,
        attributesDeleteInProgress: false,
        attributesDeleteError: '',
        milestonesDlgOpen: false,
        milestonesPacklistType: null,
        milestonesDropdownData: [],
        milestonesDropdownLoading: false,
        milestoneDlgLoading: false,
        milestoneDlgError: '',
        milestoneDlgFilterText: '',
        milestonesSelected: new Set(),
        statusDlgOpen: false,
        statusPacklistType: null,
        statusData: [],
        statusDlgLoading: false,
        statusDlgError: '',
        statusSort: [{ field: 'display', dir: 'asc' }],
        statusFilter: [],
        statusFilterTexts: {},
        statusFocusField: undefined,
        statusSelectedId: null,
        statusEditingId: null,
        statusDropdownData: [],
        statusDropdownLoading: false,
        statusDeleteConfirmId: null,
        statusDeleteInProgress: false,
        statusDeleteError: '',
        allowedItemTypesDlgOpen: false,
        allowedItemTypesPacklistType: null,
        allowedItemTypesDropdownData: [],
        allowedItemTypesDropdownLoading: false,
        allowedItemTypesDlgLoading: false,
        allowedItemTypesDlgError: '',
        allowedItemTypesDlgFilterText: '',
        allowedItemTypesSelected: new Set(),
        allowedPacklistTypesDlgOpen: false,
        allowedPacklistTypesPacklistType: null,
        allowedPacklistTypesData: [],
        allowedPacklistTypesDlgLoading: false,
        allowedPacklistTypesDlgError: '',
        allowedPacklistTypesSort: [{ field: 'display', dir: 'asc' }],
        allowedPacklistTypesFilter: [],
        allowedPacklistTypesFilterTexts: {},
        allowedPacklistTypesFocusField: undefined,
        allowedPacklistTypesSelectedId: null,
        allowedPacklistTypesEditingId: null,
        allowedPacklistTypesDropdownData: [],
        allowedPacklistTypesDropdownLoading: false,
        allowedPacklistTypesDeleteConfirmId: null,
        allowedPacklistTypesDeleteInProgress: false,
        allowedPacklistTypesDeleteError: '',
        hierarchyDlgOpen: false,
        hierarchyPacklistType: null,
        hierarchyLoading: false,
        hierarchyError: '',
        hierarchyNodes: {},
        hierarchyChildrenByParent: {},
        hierarchyParentsByChild: {},
        hierarchyExpanded: {},
        uniqueGroupsDlgOpen: false,
        uniqueGroupsPacklistType: null,
        uniqueGroupsDropdownData: [],
        uniqueGroupsDropdownLoading: false,
        uniqueGroupsDlgLoading: false,
        uniqueGroupsDlgError: '',
        uniqueGroupsDlgFilterText: '',
        uniqueGroupsSelected: new Set(),
        userRolesDlgOpen: false,
        userRolesPacklistType: null,
        userRolesDropdownData: [],
        userRolesDropdownLoading: false,
        userRolesDlgLoading: false,
        userRolesDlgError: '',
        userRolesDlgFilterText: '',
        userRolesSelected: new Set(),
        attributeEditorDlgOpen: false,
        attributeEditorAttribute: null,
        attributeEditorUserRolesData: [],
        attributeEditorUserRolesDropdownData: [],
        attributeEditorUserRolesDlgLoading: false,
        attributeEditorUserRolesDlgError: '',
        attributeEditorUserRolesDeleteConfirmId: null,
        attributeEditorUserRolesDeleteInProgress: false,
        attributeEditorUserRolesDeleteError: '',
        packlistItemAttributesDlgOpen: false,
        packlistItemAttributesPacklistType: null,
        packlistItemAttributesData: [],
        packlistItemAttributesDlgLoading: false,
        packlistItemAttributesDlgError: '',
        packlistItemAttributesSort: [{ field: 'Description', dir: 'asc' }],
        packlistItemAttributesFilter: [],
        packlistItemAttributesFilterTexts: {},
        packlistItemAttributesFocusField: undefined,
        packlistItemAttributesSelectedId: null,
        packlistItemAttributesEditingId: null,
        packlistItemAttributesDropdownData: [],
        packlistItemAttributesDropdownLoading: false,
        packlistItemAttributesMetadataDropdownData: [],
        packlistItemAttributesMetadataDropdownLoading: false,
        packlistItemAttributesDeleteConfirmId: null,
        packlistItemAttributesDeleteInProgress: false,
        packlistItemAttributesDeleteError: '',
        packlistItemAttributesUserRolesDlgOpen: false,
        packlistItemAttributesUserRolesItemAttribute: null,
        packlistItemAttributesUserRolesData: [],
        packlistItemAttributesUserRolesDropdownData: [],
        packlistItemAttributesUserRolesDlgLoading: false,
        packlistItemAttributesUserRolesDlgError: '',
        packlistItemAttributesUserRolesDeleteConfirmId: null,
        packlistItemAttributesUserRolesDeleteInProgress: false,
        packlistItemAttributesUserRolesDeleteError: '',
    });

    const stateRef = useRef<PacklistTypesState>(state);
    stateRef.current = state;

    const menuRef = useRef<HTMLDivElement | null>(null);
    const menuBlurTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const clientQueriesHeaderCellRenderersRef = useRef<{ [key: string]: (props: any) => JSX.Element }>({});
    const attributesHeaderCellRenderersRef = useRef<{ [key: string]: (props: any) => JSX.Element }>({});
    const statusHeaderCellRenderersRef = useRef<{ [key: string]: (props: any) => JSX.Element }>({});
    const HEADER_RENDERER_VERSION = 'v2';

    const saveState = useCallback(() => {
        try {
            const toSave = {
                sort: state.sort,
                filter: state.filter,
                filterText: state.filterText,
                filterTexts: state.filterTexts,
                selectedIds: Array.from(state.selectedIds ?? []),
            };
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
        } catch (e) {
            // ignore storage errors
        }
    }, [state.sort, state.filter, state.filterText, state.filterTexts, state.selectedIds]);

    useEffect(() => {
        saveState();
    }, [state.sort, state.filter, state.filterText, state.filterTexts, state.selectedIds, saveState]);

    useEffect(() => {
        fetchData();
        return () => {
            // cleanup on unmount
            if (props.onSaveState && props.menuItemId !== undefined) {
                props.onSaveState(props.menuItemId, stateRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchData = () => {
        setState(prev => ({ ...prev, loading: true, errorMsg: '' }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetPacklistTypes';
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                switch (res.callStatus) {
                    case 'OK':
                        setState(prev => ({
                            ...prev,
                            data: (res.results || []).map((item: any) => ({
                                ...item,
                                iconAllGood: parseIconCode(item?.iconAllGood),
                            })),
                            loading: false,
                        }));
                        break;
                    case 'UNAUTH':
                        const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                        props.updateUserInfo(uInf);
                        setState(prev => ({ ...prev, loading: false }));
                        break;
                    default:
                        setState(prev => ({ ...prev, errorMsg: 'Error fetching data: ' + res.callStatusMessage, loading: false }));
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, errorMsg: 'Error fetching data: ' + err.toString(), loading: false }));
            });
    }

    const handleHeaderFocus = useCallback((field: string) => {
        setState(prev => ({ ...prev, headerFocusField: field }));
    }, []);

    const handleHeaderBlur = useCallback(() => {
        setState(prev => ({ ...prev, headerFocusField: null }));
    }, []);

    const toggleSort = useCallback((field: string) => {
        setState(prev => {
            const current = prev.sort && prev.sort.length > 0 ? prev.sort[0] : undefined;
            let newSort: SortDescriptor[] = [];
            if (!current || current.field !== field) {
                newSort = [{ field, dir: 'asc' }];
            } else if (current.dir === 'asc') {
                newSort = [{ field, dir: 'desc' }];
            } else {
                newSort = [];
            }
            return { ...prev, sort: newSort };
        });
    }, []);

    const handleHeaderFilterChange = useCallback((field: string, e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const val = e.target.value;
        setState(prev => ({ ...prev, filterTexts: { ...prev.filterTexts, [field]: val } }));
    }, []);

    const parseIconCode = useCallback((value: unknown): number | null => {
        if (value === null || value === undefined) return null;
        const raw = String(value).trim();
        if (!raw) return null;

        const decimalMatch = raw.match(/^&#(\d+);?$/);
        if (decimalMatch) return Number(decimalMatch[1]);

        const hexEntityMatch = raw.match(/^&#x([0-9a-fA-F]+);?$/);
        if (hexEntityMatch) return parseInt(hexEntityMatch[1], 16);

        const slashHexMatch = raw.match(/^\\([0-9a-fA-F]+)$/);
        if (slashHexMatch) return parseInt(slashHexMatch[1], 16);

        const hexMatch = raw.match(/^0x([0-9a-fA-F]+)$/);
        if (hexMatch) return parseInt(hexMatch[1], 16);

        const simpleHexMatch = raw.match(/^e[0-9a-fA-F]{3,}$/i);
        if (simpleHexMatch) return parseInt(raw, 16);

        const numeric = Number(raw);
        return Number.isFinite(numeric) ? numeric : null;
    }, []);

    const toIconEntity = useCallback((value: unknown): string => {
        const code = parseIconCode(value);
        return code ? `&#${code};` : '';
    }, [parseIconCode]);

    const isWebComponentsPrivateUseCode = useCallback((code: number | null | undefined): boolean => {
        if (!code) return false;
        return code >= 0xe000 && code <= 0xf8ff;
    }, []);

    const LEGACY_APP_ICON_CODES = useRef<number[]>([
        10024,  // sparkle fallback used in menu logic
        9889,   // milestone scan
        127969, // home
        128064, // dashboards
        128195, // reports
        128203, // actions (example user called out)
        128373, // admin/test
    ]);

    const UNICODE_ICON_CODES = useMemo(() => {
        const ranges: Array<[number, number]> = [
            [0x2190, 0x21FF], // arrows
            [0x2300, 0x23FF], // technical
            [0x2460, 0x24FF], // enclosed alphanumerics
            [0x25A0, 0x25FF], // geometric shapes
            [0x2600, 0x26FF], // misc symbols
            [0x2700, 0x27BF], // dingbats
            [0x2B00, 0x2BFF], // arrows/symbols
            [0x1F300, 0x1F5FF], // symbols and pictographs (includes 128203)
            [0x1F600, 0x1F64F], // emoticons
            [0x1F680, 0x1F6FF], // transport/map
            [0x1F700, 0x1F77F], // alchemical
            [0x1F780, 0x1F7FF], // geometric extended
            [0x1F900, 0x1F9FF], // supplemental symbols and pictographs
            [0x1FA70, 0x1FAFF], // symbols and pictographs extended-a
        ];

        const codes: number[] = [];
        ranges.forEach(([start, end]) => {
            for (let c = start; c <= end; c++) {
                codes.push(c);
            }
        });
        return codes;
    }, []);

    const unicodePickerCodes = useMemo(() => {
        const set = new Set<number>(UNICODE_ICON_CODES);
        state.data.forEach(row => {
            const c = parseIconCode(row.iconAllGood);
            if (c && !isWebComponentsPrivateUseCode(c)) {
                set.add(c);
            }
        });
        const newCode = parseIconCode(state.newDialogIconAllGood);
        if (newCode && !isWebComponentsPrivateUseCode(newCode)) {
            set.add(newCode);
        }
        if (state.iconPickerSelectedCode && !isWebComponentsPrivateUseCode(state.iconPickerSelectedCode)) {
            set.add(state.iconPickerSelectedCode);
        }
        LEGACY_APP_ICON_CODES.current.forEach(c => {
            if (!isWebComponentsPrivateUseCode(c)) {
                set.add(c);
            }
        });
        return Array.from(set).sort((a, b) => a - b);
    }, [UNICODE_ICON_CODES, state.data, state.newDialogIconAllGood, state.iconPickerSelectedCode, parseIconCode, isWebComponentsPrivateUseCode]);

    const loadIconCodes = useCallback(async () => {
        setState(prev => ({ ...prev, iconPickerLoading: true, iconPickerError: '' }));
        try {
            const base = process.env.PUBLIC_URL || '';
            const resp = await fetch(`${base}/kendo-font-icons/index.css`);
            if (!resp.ok) {
                throw new Error(`HTTP ${resp.status}`);
            }
            const css = await resp.text();
            const matches = css.matchAll(/content:\s*"\\([a-fA-F0-9]+)"/g);
            const codeSet = new Set<number>();
            for (const m of matches) {
                const n = parseInt(m[1], 16);
                if (Number.isFinite(n) && n > 0) {
                    codeSet.add(n);
                }
            }
            const codes = Array.from(codeSet).sort((a, b) => a - b);
            if (codes.length === 0) {
                throw new Error('No icon codes discovered from font CSS');
            }
            setState(prev => {
                const merged = new Set<number>(codes);
                prev.data.forEach(row => {
                    const c = parseIconCode(row.iconAllGood);
                    if (c) merged.add(c);
                });
                const newCode = parseIconCode(prev.newDialogIconAllGood);
                if (newCode) merged.add(newCode);
                if (prev.iconPickerSelectedCode) merged.add(prev.iconPickerSelectedCode);
                LEGACY_APP_ICON_CODES.current.forEach(c => merged.add(c));
                return {
                    ...prev,
                    iconPickerCodes: Array.from(merged).sort((a, b) => a - b),
                    iconPickerLoading: false,
                    iconPickerError: '',
                };
            });
        } catch (err: any) {
            // Fallback range for WebComponentsIcons private-use block.
            const fallbackCodes: number[] = [];
            for (let i = 0xe000; i <= 0xe2ff; i++) {
                fallbackCodes.push(i);
            }
            setState(prev => {
                const merged = new Set<number>(fallbackCodes);
                prev.data.forEach(row => {
                    const c = parseIconCode(row.iconAllGood);
                    if (c) merged.add(c);
                });
                const newCode = parseIconCode(prev.newDialogIconAllGood);
                if (newCode) merged.add(newCode);
                if (prev.iconPickerSelectedCode) merged.add(prev.iconPickerSelectedCode);
                LEGACY_APP_ICON_CODES.current.forEach(c => merged.add(c));
                return {
                    ...prev,
                    iconPickerCodes: Array.from(merged).sort((a, b) => a - b),
                    iconPickerLoading: false,
                    iconPickerError: `Loaded fallback icon set: ${err?.toString?.() ?? err}`,
                };
            });
        }
    }, [parseIconCode]);

    const openIconPicker = useCallback((mode: 'edit' | 'new', dataItem?: PacklistTypeRow) => {
        const currentCode = mode === 'edit'
            ? (parseIconCode(dataItem?.iconAllGood) ?? null)
            : (parseIconCode(state.newDialogIconAllGood) ?? null);

        setState(prev => ({
            ...prev,
            ...(() => {
                const mergedSet = new Set<number>(prev.iconPickerCodes);
                prev.data.forEach(row => {
                    const c = parseIconCode(row.iconAllGood);
                    if (c) mergedSet.add(c);
                });
                const newCode = parseIconCode(prev.newDialogIconAllGood);
                if (newCode) mergedSet.add(newCode);
                LEGACY_APP_ICON_CODES.current.forEach(c => mergedSet.add(c));
                if (currentCode) mergedSet.add(currentCode);
                const mergedCodes = Array.from(mergedSet).sort((a, b) => a - b);
                return { iconPickerCodes: mergedCodes };
            })(),
            iconPickerOpen: true,
            iconPickerTargetMode: mode,
            iconPickerTargetPacklistTypeID: mode === 'edit' ? (dataItem?.packlistTypeID ?? null) : null,
            iconPickerSelectedCode: currentCode,
            iconPickerTab: currentCode && !isWebComponentsPrivateUseCode(currentCode) ? 'unicode' : 'kendo',
            iconPickerFilterText: '',
        }));

        if (state.iconPickerCodes.length === 0 && !state.iconPickerLoading) {
            void loadIconCodes();
        }
    }, [isWebComponentsPrivateUseCode, loadIconCodes, parseIconCode, state.newDialogIconAllGood, state.iconPickerCodes.length, state.iconPickerLoading]);

    const closeIconPicker = useCallback(() => {
        setState(prev => ({
            ...prev,
            iconPickerOpen: false,
            iconPickerTargetMode: null,
            iconPickerTargetPacklistTypeID: null,
            iconPickerFilterText: '',
        }));
    }, []);

    const applyIconPickerSelection = useCallback((selectedCode: number | null) => {
        if (state.iconPickerTargetMode === 'new') {
            setState(prev => ({
                ...prev,
                newDialogIconAllGood: selectedCode,
                iconPickerOpen: false,
                iconPickerTargetMode: null,
                iconPickerTargetPacklistTypeID: null,
                iconPickerFilterText: '',
            }));
            return;
        }

        if (state.iconPickerTargetMode === 'edit' && state.iconPickerTargetPacklistTypeID !== null) {
            const targetID = state.iconPickerTargetPacklistTypeID;
            setState(prev => {
                const targetRow = prev.data.find(item => item.packlistTypeID === targetID);
                if (!targetRow) {
                    return {
                        ...prev,
                        iconPickerOpen: false,
                        iconPickerTargetMode: null,
                        iconPickerTargetPacklistTypeID: null,
                        iconPickerFilterText: '',
                    };
                }

                const originalForRow = (prev.editItemId === targetID && prev.originalItem)
                    ? prev.originalItem
                    : { ...targetRow };

                return {
                    ...prev,
                    data: prev.data.map(item => item.packlistTypeID === targetID ? { ...item, iconAllGood: selectedCode } : item),
                    editItemId: targetID,
                    originalItem: originalForRow,
                    iconPickerOpen: false,
                    iconPickerTargetMode: null,
                    iconPickerTargetPacklistTypeID: null,
                    iconPickerFilterText: '',
                };
            });
        }
    }, [state.iconPickerTargetMode, state.iconPickerTargetPacklistTypeID]);

    const handleItemChange = (e: GridItemChangeEvent) => {
        const edited = e.dataItem;
        const inEdit = state.data.map((item: PacklistTypeRow) =>
            item.packlistTypeID === edited.packlistTypeID ? edited : item
        );
        setState(prev => ({ ...prev, data: inEdit }));
    }

    const keepEditInputFocus = useCallback((rowId: number, field: string, selectionStart: number | null, selectionEnd: number | null) => {
        requestAnimationFrame(() => {
            const selector = `input[data-edit-cell="${rowId}::${field}"]`;
            const input = document.querySelector(selector) as HTMLInputElement | null;
            if (!input) {
                return;
            }

            if (document.activeElement !== input) {
                input.focus();
            }

            if (selectionStart !== null && selectionEnd !== null) {
                try {
                    input.setSelectionRange(selectionStart, selectionEnd);
                } catch {
                    // Ignore selection errors for unsupported input types.
                }
            }
        });
    }, []);

    const handleEnterEdit = (dataItem: PacklistTypeRow) => {
        setState(prev => ({
            ...prev,
            editItemId: dataItem.packlistTypeID,
            editViewOrderIds: sortedData.map(item => item.packlistTypeID),
            originalItem: { ...dataItem },
        }));
    }

    const handleCancel = () => {
        if (state.originalItem) {
            const origItem = state.originalItem;
            setState(prev => ({
                ...prev,
                data: prev.data.map(item =>
                    item.packlistTypeID === origItem.packlistTypeID
                        ? origItem
                        : item
                ),
                editItemId: null,
                editViewOrderIds: null,
                originalItem: null,
            }));
        }
    }

    const handleSave = (dataItem: PacklistTypeRow) => {
        setState(prev => ({ ...prev, loading: true }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdatePacklistType';
        const payload = {
            packlistTypeID: dataItem.packlistTypeID,
            packlistTypeCode: dataItem.packlistTypeCode,
            description: dataItem.description,
            allowMultiScan: dataItem.allowMultiScan,
            iconAllGood: parseIconCode(dataItem.iconAllGood) ?? 0,
            displayFormat: dataItem.displayFormat,
            restrictToAllowedItemTypesOnly: dataItem.restrictToAllowedItemTypesOnly,
            lastChangedBy: String(props.userInf.username ?? props.userInf.userId ?? ''),
        };
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify(payload),
        })
            .then(async resp => {
                const contentType = resp.headers.get('content-type') || '';
                const body = contentType.includes('application/json') ? await resp.json() : await resp.text();
                if (!resp.ok) {
                    const err = typeof body === 'string' ? body : body?.callStatusMessage || JSON.stringify(body);
                    throw new Error(`HTTP ${resp.status}: ${err}`);
                }
                return body;
            })
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    setState(prev => ({ ...prev, loading: false, editItemId: null, editViewOrderIds: null }));
                } else if (res.callStatus !== 'OK') {
                    setState(prev => ({ ...prev, loading: false, errorMsg: 'Error saving: ' + res.callStatusMessage }));
                } else {
                    setState(prev => ({ ...prev, loading: false, editItemId: null, editViewOrderIds: null, originalItem: null }));
                    fetchData();
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, loading: false, errorMsg: 'Error saving: ' + err.toString() }));
            });
    }

    const openDeleteConfirm = (dataItem: PacklistTypeRow) => {
        setState(prev => ({ ...prev, deleteConfirmItem: dataItem, deleteErrorMsg: '' }));
    }

    const closeDeleteConfirm = () => {
        setState(prev => ({ ...prev, deleteConfirmItem: null, deleteErrorMsg: '' }));
    }

    const confirmDelete = () => {
        if (!state.deleteConfirmItem) return;
        setState(prev => ({ ...prev, deleteInProgress: true, deleteErrorMsg: '' }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/DeletePacklistType';
        const idToDelete = state.deleteConfirmItem.packlistTypeID;
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify(idToDelete),
        })
            .then(async resp => {
                const contentType = resp.headers.get('content-type') || '';
                const body = contentType.includes('application/json') ? await resp.json() : await resp.text();
                if (!resp.ok) {
                    const err = typeof body === 'string' ? body : body?.callStatusMessage || JSON.stringify(body);
                    throw new Error(`HTTP ${resp.status}: ${err}`);
                }
                return body;
            })
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    setState(prev => ({ ...prev, deleteInProgress: false, deleteConfirmItem: null }));
                } else if (res.callStatus !== 'OK') {
                    setState(prev => ({ ...prev, deleteInProgress: false, deleteErrorMsg: 'Error deleting: ' + res.callStatusMessage }));
                } else {
                    setState(prev => ({ ...prev, deleteInProgress: false, deleteConfirmItem: null }));
                    fetchData();
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, deleteInProgress: false, deleteErrorMsg: 'Error deleting: ' + err.toString() }));
            });
    }

    const openNewDialog = () => {
        setState(prev => ({
            ...prev,
            newDialogOpen: true,
            newDialogCode: '',
            newDialogDesc: '',
            newDialogAllowMultiScan: false,
            newDialogIconAllGood: null,
            newDialogDisplayFormat: '',
            newDialogRestrictToAllowedItemTypes: false,
            newDialogError: '',
        }));
    }

    const closeNewDialog = () => {
        setState(prev => ({ ...prev, newDialogOpen: false }));
    }

    const submitNew = () => {
        if (!state.newDialogCode.trim()) {
            setState(prev => ({ ...prev, newDialogError: 'Code is required' }));
            return;
        }
        setState(prev => ({ ...prev, newDialogSaving: true, newDialogError: '' }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdatePacklistType';
        const payload = {
            packlistTypeID: 0,
            packlistTypeCode: state.newDialogCode,
            description: state.newDialogDesc,
            allowMultiScan: state.newDialogAllowMultiScan,
            iconAllGood: parseIconCode(state.newDialogIconAllGood) ?? 0,
            displayFormat: state.newDialogDisplayFormat,
            restrictToAllowedItemTypesOnly: state.newDialogRestrictToAllowedItemTypes,
            lastChangedBy: String(props.userInf.username ?? props.userInf.userId ?? ''),
        };
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify(payload),
        })
            .then(async resp => {
                const contentType = resp.headers.get('content-type') || '';
                const body = contentType.includes('application/json') ? await resp.json() : await resp.text();
                if (!resp.ok) {
                    const err = typeof body === 'string' ? body : body?.callStatusMessage || JSON.stringify(body);
                    throw new Error(`HTTP ${resp.status}: ${err}`);
                }
                return body;
            })
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    setState(prev => ({ ...prev, newDialogSaving: false, newDialogOpen: false }));
                } else if (res.callStatus !== 'OK') {
                    setState(prev => ({ ...prev, newDialogSaving: false, newDialogError: 'Error creating: ' + res.callStatusMessage }));
                } else {
                    setState(prev => ({ ...prev, newDialogSaving: false, newDialogOpen: false }));
                    fetchData();
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, newDialogSaving: false, newDialogError: 'Error creating: ' + err.toString() }));
            });
    }

    const openRowMenu = (dataItem: PacklistTypeRow, anchor: HTMLElement) => {
        if (menuBlurTimeoutRef.current) clearTimeout(menuBlurTimeoutRef.current);
        try {
            const ar = anchor.getBoundingClientRect();
            const anchorRect = { left: Math.round(ar.left), top: Math.round(ar.top), right: Math.round(ar.right), bottom: Math.round(ar.bottom), width: Math.round(ar.width), height: Math.round(ar.height) };
            setState(prev => ({ ...prev, menuDataItem: dataItem, menuAnchor: anchor, menuAnchorRect: anchorRect, menuPosition: null }));
        } catch {
            setState(prev => ({ ...prev, menuDataItem: dataItem, menuAnchor: anchor, menuAnchorRect: null, menuPosition: null }));
        }
    }

    const closeRowMenu = () => {
        setState(prev => ({ ...prev, menuDataItem: null, menuAnchor: null }));
    }

    const handleMenuBlur = () => {
        if (menuBlurTimeoutRef.current) clearTimeout(menuBlurTimeoutRef.current);
        menuBlurTimeoutRef.current = setTimeout(() => closeRowMenu(), 100);
    }

    // useCallback (with no external deps, since setState's functional form is used) keeps this
    // handler's identity stable across renders, so cached body cell renderers below can safely
    // close over it directly without going through a ref.
    const handleRowClick = useCallback((packlistTypeID: number, e: React.MouseEvent) => {
        e.stopPropagation();
        setState(prev => {
            const newSelectedIds = new Set(prev.selectedIds);
            if (newSelectedIds.has(packlistTypeID)) {
                newSelectedIds.delete(packlistTypeID);
            } else {
                newSelectedIds.clear();
                newSelectedIds.add(packlistTypeID);
            }
            return { ...prev, selectedIds: newSelectedIds };
        });
    }, []);

    const openClientQueriesDialog = (packlistType: PacklistTypeRow) => {
        closeRowMenu();
        setState(prev => ({ ...prev, clientQueriesDlgOpen: true, clientQueriesPacklistType: packlistType, clientQueriesDlgLoading: true, clientQueriesDlgError: '', clientQueriesSort: [{ field: 'display', dir: 'asc' }], clientQueriesFilter: [], clientQueriesFilterTexts: {}, clientQueriesFocusField: undefined, clientQueriesDropdownLoading: true }));
        
        // Fetch both selected queries and dropdown options in parallel
        const selectedUrl = `${props.userInf.currProject.apiUrl}/api/TMI/GetPacklistTypeClientQueries?packlistTypeID=${packlistType.packlistTypeID}`;
        const dropdownUrl = `${props.userInf.currProject.apiUrl}/api/TMI/GetClientQueriesForDropDown`;
        
        Promise.all([
            fetch(selectedUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
            }).then(resp => resp.json()),
            fetch(dropdownUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
            }).then(resp => resp.json()),
        ])
            .then(([selectedRes, dropdownRes]) => {
                let clientQueriesData: Array<{
                    PackListTypeClientQueryID: number;
                    ClientQueryID: number;
                    display: string;
                    DisplayText: string;
                    SortOrder: number;
                    isSelected: boolean;
                }> = [];
                let dropdownData: LookupListItem[] = [];
                let hasError = false;

                // First, process dropdown data so we can use it for lookups
                if (dropdownRes.callStatus === 'OK' && Array.isArray(dropdownRes.results)) {
                    dropdownData = dropdownRes.results.map((item: any) => ({
                        value: item.value || item.id?.toString(),
                        display: item.display || item.name,
                    }));
                }

                if (selectedRes.callStatus === 'OK' && Array.isArray(selectedRes.results)) {
                    const selectedQueries = selectedRes.results as Array<{
                        packListTypeClientQueryID: number;
                        packListTypeID: number;
                        clientQueryID: number;
                        displayText: string | null;
                        sortOrder: number;
                        lastChangedBy: string | null;
                        lastChangedOn: string | null;
                    }>;
                    
                    const mappedData: Array<{
                        PackListTypeClientQueryID: number;
                        ClientQueryID: number;
                        display: string;
                        DisplayText: string;
                        SortOrder: number;
                        isSelected: boolean;
                    }> = selectedQueries.map(query => {
                        // Look up the display name from dropdown data using ClientQueryID as the value
                        const displayName = dropdownData.find(d => d.value === query.clientQueryID.toString())?.display || `Query ${query.clientQueryID}`;
                        return {
                            PackListTypeClientQueryID: query.packListTypeClientQueryID,
                            ClientQueryID: query.clientQueryID,
                            display: displayName,
                            DisplayText: query.displayText || '',
                            SortOrder: query.sortOrder,
                            isSelected: true,
                        };
                    });
                    clientQueriesData = mappedData;
                } else {
                    hasError = true;
                }

                if (hasError) {
                    setState(prev => ({ ...prev, clientQueriesDlgLoading: false, clientQueriesDlgError: 'Error loading client queries', clientQueriesDropdownLoading: false }));
                } else {
                    setState(prev => ({ ...prev, clientQueriesData, clientQueriesDropdownData: dropdownData, clientQueriesDlgLoading: false, clientQueriesDropdownLoading: false, clientQueriesAddingNew: false, clientQueriesNewQueryId: null, clientQueriesNewDisplayText: '', clientQueriesNewSortOrder: 1 }));
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, clientQueriesDlgLoading: false, clientQueriesDlgError: 'Error loading client queries: ' + err.toString(), clientQueriesDropdownLoading: false }));
            });
    }

    const closeClientQueriesDialog = () => {
        setState(prev => ({ ...prev, clientQueriesDlgOpen: false, clientQueriesPacklistType: null, clientQueriesData: [], clientQueriesSort: [{ field: 'display', dir: 'asc' }], clientQueriesFilter: [], clientQueriesFilterTexts: {}, clientQueriesFocusField: undefined, clientQueriesEditId: null, clientQueriesEditRowIndex: null, clientQueriesEditOriginal: null, clientQueriesDropdownData: [], clientQueriesAddingNew: false, clientQueriesNewQueryId: null, clientQueriesNewDisplayText: '', clientQueriesNewSortOrder: 1 }));
    }

    const saveClientQuery = (clientQueryId: number) => {
        const query = state.clientQueriesData.find(q => q.PackListTypeClientQueryID === clientQueryId);
        if (!query || !state.clientQueriesPacklistType) return;

        // Validate that a query has been selected
        if (!query.ClientQueryID) {
            setState(prev => ({ ...prev, clientQueriesDlgError: 'Please select a Query before saving.' }));
            return;
        }

        // Read DisplayText from the uncontrolled input
        const displayTextInput = document.querySelector(`input[data-field="DisplayText"]`) as HTMLInputElement;
        const displayText = displayTextInput?.value || query.DisplayText || '';

        setState(prev => ({ ...prev, clientQueriesDlgLoading: true, clientQueriesDlgError: '' }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdatePackListTypeClientQueries';
        
        const payload = {
            PackListTypeClientQueryID: 0,
            PackListTypeID: state.clientQueriesPacklistType.packlistTypeID,
            ClientQueryID: query.ClientQueryID,
            DisplayText: displayText,
            SortOrder: query.SortOrder,
            LastChangedBy: props.userInf.username,
            LastChangedOn: null,
        };
        
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify(payload),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'OK') {
                    setState(prev => ({
                        ...prev,
                        clientQueriesDlgLoading: false,
                        clientQueriesEditingId: null,
                        clientQueriesData: prev.clientQueriesData.map(q =>
                            q.PackListTypeClientQueryID === clientQueryId
                                ? { ...q, DisplayText: displayText, inEdit: false }
                                : q
                        ),
                    }));
                } else {
                    setState(prev => ({
                        ...prev,
                        clientQueriesDlgLoading: false,
                        clientQueriesDlgError: 'Error saving: ' + res.callStatusMessage,
                    }));
                }
            })
            .catch(err => {
                setState(prev => ({
                    ...prev,
                    clientQueriesDlgLoading: false,
                    clientQueriesDlgError: 'Error saving: ' + err.toString(),
                }));
            });
    }

    const openClientQueryDeleteConfirm = (clientQueryId: number) => {
        setState(prev => ({ ...prev, clientQueriesDeleteConfirmId: clientQueryId, clientQueriesDeleteError: '' }));
    }

    const closeClientQueryDeleteConfirm = () => {
        setState(prev => ({ ...prev, clientQueriesDeleteConfirmId: null, clientQueriesDeleteInProgress: false, clientQueriesDeleteError: '' }));
    }

    const confirmClientQueryDelete = () => {
        if (state.clientQueriesDeleteConfirmId === null) return;

        const idToDelete = state.clientQueriesDeleteConfirmId;
        const rowToDelete = state.clientQueriesData.find(q => q.PackListTypeClientQueryID === idToDelete);
        if (!rowToDelete) return;

        setState(prev => ({ ...prev, clientQueriesDeleteInProgress: true, clientQueriesDeleteError: '' }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/DeletePacklistTypeClientQuery';
        
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify(idToDelete)

        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'OK') {
                    setState(prev => ({
                        ...prev,
                        clientQueriesData: prev.clientQueriesData.filter(q => q.PackListTypeClientQueryID !== idToDelete),
                        clientQueriesDeleteInProgress: false,
                        clientQueriesDeleteConfirmId: null,
                        clientQueriesEditId: null,
                        clientQueriesEditOriginal: null,
                    }));
                } else {
                    setState(prev => ({
                        ...prev,
                        clientQueriesDeleteInProgress: false,
                        clientQueriesDeleteError: 'Error deleting: ' + res.callStatusMessage,
                    }));
                }
            })
            .catch(err => {
                setState(prev => ({
                    ...prev,
                    clientQueriesDeleteInProgress: false,
                    clientQueriesDeleteError: 'Error deleting: ' + err.toString(),
                }));
            });
    }

    const handleClientQueriesSortChange = useCallback((field: string) => {
        setState(prev => {
            const current = prev.clientQueriesSort && prev.clientQueriesSort.length > 0 ? prev.clientQueriesSort[0] : undefined;
            let newSort: SortDescriptor[] = [];
            if (!current || current.field !== field) {
                newSort = [{ field, dir: 'asc' }];
            } else if (current.dir === 'asc') {
                newSort = [{ field, dir: 'desc' }];
            } else {
                newSort = [];
            }
            return { ...prev, clientQueriesSort: newSort };
        });
    }, []);

    const handleClientQueriesFilterChange = useCallback((field: string, e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setState(prev => ({ ...prev, clientQueriesFilterTexts: { ...prev.clientQueriesFilterTexts, [field]: val } }));
    }, []);

    const buildClientQueriesHeaderCellRenderer = useCallback((field: string, title: string, width?: number | string) => {
        const cacheKey = `${HEADER_RENDERER_VERSION}_clientQueries_${field}`;
        if (!clientQueriesHeaderCellRenderersRef.current[cacheKey]) {
            clientQueriesHeaderCellRenderersRef.current[cacheKey] = (props: any) => {
                const val = stateRef.current.clientQueriesFilterTexts?.[field] ?? '';
                const focused = stateRef.current.clientQueriesFocusField === field;
                const sortDir = (stateRef.current.clientQueriesSort && stateRef.current.clientQueriesSort.length > 0 && stateRef.current.clientQueriesSort[0].field === field) ? (stateRef.current.clientQueriesSort[0].dir as string) : undefined;
                return (
                    <th className="tmi-header-filter">
                        <div className="k-link" onClick={() => handleClientQueriesSortChange(field)} aria-hidden="true" />
                        <div className={`tmi-floating-wrapper ${val ? 'has-value' : ''} ${focused ? 'focused' : ''}`}>
                            <label onClick={() => handleClientQueriesSortChange(field)} style={{ whiteSpace: 'normal', wordWrap: 'break-word', overflow: 'visible', cursor: 'pointer', userSelect: 'none', pointerEvents: 'auto', position: 'relative', zIndex: 10, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span>{title}</span>
                                <span className="tmi-sort-glyph-inline">{sortDir === 'asc' ? '▲' : sortDir === 'desc' ? '▼' : '↕'}</span>
                            </label>
                            <input
                                type="text"
                                className="k-input k-input-sm"
                                value={val}
                                onChange={(e) => handleClientQueriesFilterChange(field, e)}
                                onFocus={() => setState(prev => ({ ...prev, clientQueriesFocusField: field }))}
                                onBlur={() => setState(prev => ({ ...prev, clientQueriesFocusField: undefined }))}
                                onMouseDown={(e) => { e.stopPropagation(); }}
                            />
                        </div>
                    </th>
                );
            };
        }
        return clientQueriesHeaderCellRenderersRef.current[cacheKey];
    }, [handleClientQueriesSortChange, handleClientQueriesFilterChange]);

    const openAttributesDialog = (packlistType: PacklistTypeRow) => {
        closeRowMenu();
        setState(prev => ({ ...prev, attributesDlgOpen: true, attributesPacklistType: packlistType, attributesDlgLoading: true, attributesDlgError: '', attributesSort: [{ field: 'Description', dir: 'asc' }], attributesFilter: [], attributesFilterTexts: {}, attributesFocusField: undefined, attributesDropdownLoading: true, attributesMetadataDropdownLoading: true }));
        
        // Fetch selected attributes, attribute dropdown, and metadata dropdown in parallel
        const selectedUrl = `${props.userInf.currProject.apiUrl}/api/TMI/GetPacklistTypeAttributes?packlistTypeID=${packlistType.packlistTypeID}`;
        const dropdownUrl = `${props.userInf.currProject.apiUrl}/api/TMI/GetPacklistAttributesForDropDown`;
        const metadataUrl = `${props.userInf.currProject.apiUrl}/api/TMI/GetMetaDataForDropDown`;
        
        Promise.all([
            fetch(selectedUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
            }).then(resp => resp.json()),
            fetch(dropdownUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
            }).then(resp => resp.json()),
            fetch(metadataUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
            }).then(resp => resp.json()),
        ])
            .then(([selectedRes, dropdownRes, metadataRes]) => {
                let attributesData: Array<{
                    PackListTypeAttributeID: number;
                    PackListAttributeID: number;
                    display: string;
                    Description: string;
                    GroupDesc: string;
                    GroupOrder: number;
                    SortOrder: number;
                    TabName: string;
                    MetaDataID: number | null;
                    isSelected: boolean;
                }> = [];
                let dropdownData: LookupListItem[] = [];
                let metadataData: LookupListItem[] = [];
                let hasError = false;

                // Process dropdown data so we can use it for lookups
                if (dropdownRes.callStatus === 'OK' && Array.isArray(dropdownRes.results)) {
                    dropdownData = dropdownRes.results.map((item: any) => ({
                        value: item.value || item.id?.toString(),
                        display: item.display || item.name,
                    }));
                }

                // Process metadata dropdown data
                if (metadataRes.callStatus === 'OK' && Array.isArray(metadataRes.results)) {
                    metadataData = metadataRes.results.map((item: any) => ({
                        value: item.value || item.id?.toString(),
                        display: item.display || item.name,
                    }));
                }

                if (selectedRes.callStatus === 'OK' && Array.isArray(selectedRes.results)) {
                    const selectedAttributes = selectedRes.results as Array<{
                        packListTypeAttributeID: number;
                        packListTypeID: number;
                        packListAttributeID: number;
                        description: string | null;
                        groupDesc: string | null;
                        groupOrder: number;
                        sortOrder: number;
                        tabName: string | null;
                        metaDataID: number | null;
                        lastChangedBy: string | null;
                        lastChangedOn: string | null;
                    }>;
                    
                    const mappedData: Array<{
                        PackListTypeAttributeID: number;
                        PackListAttributeID: number;
                        display: string;
                        Description: string;
                        GroupDesc: string;
                        GroupOrder: number;
                        SortOrder: number;
                        TabName: string;
                        MetaDataID: number | null;
                        isSelected: boolean;
                    }> = selectedAttributes.map(attr => {
                        // Use description if available, otherwise look up the attribute name from dropdown data using PackListAttributeID
                        const displayName = attr.description || dropdownData.find(d => d.value === attr.packListAttributeID.toString())?.display || (attr.packListAttributeID > 0 ? `Attribute ${attr.packListAttributeID}` : '(No Attribute)');
                        return {
                            PackListTypeAttributeID: attr.packListTypeAttributeID,
                            PackListAttributeID: attr.packListAttributeID,
                            display: displayName,
                            Description: attr.description || '',
                            GroupDesc: attr.groupDesc || '',
                            GroupOrder: attr.groupOrder,
                            SortOrder: attr.sortOrder,
                            TabName: attr.tabName || '',
                            MetaDataID: attr.metaDataID || null,
                            isSelected: true,
                        };
                    });
                    attributesData = mappedData;
                } else {
                    hasError = true;
                }

                if (hasError) {
                    setState(prev => ({ ...prev, attributesDlgLoading: false, attributesDlgError: 'Error loading attributes', attributesDropdownLoading: false, attributesMetadataDropdownLoading: false }));
                } else {
                    setState(prev => ({ ...prev, attributesData, attributesDropdownData: dropdownData, attributesMetadataDropdownData: metadataData, attributesDlgLoading: false, attributesDropdownLoading: false, attributesMetadataDropdownLoading: false, attributesAddingNew: false, attributesNewAttributeId: null, attributesNewGroupDesc: '', attributesNewGroupOrder: 0, attributesNewSortOrder: 1, attributesNewTabName: '', attributesNewMetaDataId: null }));
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, attributesDlgLoading: false, attributesDlgError: 'Error loading attributes: ' + err.toString(), attributesDropdownLoading: false, attributesMetadataDropdownLoading: false }));
            });
    }

    const closeAttributesDialog = () => {
        setState(prev => ({ ...prev, attributesDlgOpen: false, attributesPacklistType: null, attributesData: [], attributesSort: [{ field: 'Description', dir: 'asc' }], attributesFilter: [], attributesFilterTexts: {}, attributesFocusField: undefined, attributesEditId: null, attributesEditRowIndex: null, attributesEditOriginal: null, attributesDropdownData: [], attributesMetadataDropdownData: [], attributesAddingNew: false, attributesNewAttributeId: null, attributesNewGroupDesc: '', attributesNewGroupOrder: 0, attributesNewSortOrder: 1, attributesNewTabName: '', attributesNewMetaDataId: null }));
    }

    const openPacklistItemAttributesDialog = (packlistType: PacklistTypeRow) => {
        closeRowMenu();
        setState(prev => ({ ...prev, packlistItemAttributesDlgOpen: true, packlistItemAttributesPacklistType: packlistType, packlistItemAttributesDlgLoading: true, packlistItemAttributesDlgError: '', packlistItemAttributesSort: [{ field: 'Description', dir: 'asc' }], packlistItemAttributesFilter: [], packlistItemAttributesFilterTexts: {}, packlistItemAttributesFocusField: undefined, packlistItemAttributesDropdownLoading: true, packlistItemAttributesMetadataDropdownLoading: true }));
        
        // Fetch selected item attributes, attribute dropdown, and metadata dropdown in parallel
        const selectedUrl = `${props.userInf.currProject.apiUrl}/api/TMI/GetPackListTypeItemAttributes?packListTypeID=${packlistType.packlistTypeID}`;
        const dropdownUrl = `${props.userInf.currProject.apiUrl}/api/TMI/GetAttributesForDropDown`;
        const metadataUrl = `${props.userInf.currProject.apiUrl}/api/TMI/GetMetaDataForDropDown`;
        
        Promise.all([
            fetch(selectedUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
            }).then(resp => resp.json()),
            fetch(dropdownUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
            }).then(resp => resp.json()),
            fetch(metadataUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
            }).then(resp => resp.json()),
        ])
            .then(([selectedRes, dropdownRes, metadataRes]) => {
                let packlistItemAttributesData: Array<{
                    PackListTypeItemAttributeID: number;
                    AttributeID: number;
                    display: string;
                    Description: string;
                    GroupDesc: string;
                    GroupOrder: number;
                    SortOrder: number;
                    TabName: string;
                    ShowInDetails: boolean;
                    MetaDataID: number | null;
                    isSelected: boolean;
                }> = [];
                let dropdownData: LookupListItem[] = [];
                let metadataData: LookupListItem[] = [];
                let hasError = false;

                // Process dropdown data so we can use it for lookups
                if (dropdownRes.callStatus === 'OK' && Array.isArray(dropdownRes.results)) {
                    dropdownData = dropdownRes.results.map((item: any) => ({
                        value: item.value || item.id?.toString(),
                        display: item.display || item.name,
                    }));
                }

                // Process metadata dropdown data
                if (metadataRes.callStatus === 'OK' && Array.isArray(metadataRes.results)) {
                    metadataData = metadataRes.results.map((item: any) => ({
                        value: item.value || item.id?.toString(),
                        display: item.display || item.name,
                    }));
                }

                if (selectedRes.callStatus === 'OK' && Array.isArray(selectedRes.results)) {
                    const selectedAttributes = selectedRes.results as Array<{
                        packListTypeItemAttributeID: number;
                        packListTypeID: number;
                        attributeID: number;
                        description: string | null;
                        groupDesc: string | null;
                        groupOrder: number;
                        sortOrder: number;
                        tabName: string | null;
                        showInDetails: boolean;
                        metaDataID: number | null;
                        lastChangedBy: string | null;
                        lastChangedOn: string | null;
                    }>;
                    
                    const mappedData: Array<{
                        PackListTypeItemAttributeID: number;
                        AttributeID: number;
                        display: string;
                        Description: string;
                        GroupDesc: string;
                        GroupOrder: number;
                        SortOrder: number;
                        TabName: string;
                        ShowInDetails: boolean;
                        MetaDataID: number | null;
                        isSelected: boolean;
                    }> = selectedAttributes.map(attr => {
                        // Use description if available, otherwise look up the attribute name from dropdown data using AttributeID
                        const displayName = attr.description || dropdownData.find(d => d.value === attr.attributeID.toString())?.display || (attr.attributeID > 0 ? `Attribute ${attr.attributeID}` : '(No Attribute)');
                        return {
                            PackListTypeItemAttributeID: attr.packListTypeItemAttributeID,
                            AttributeID: attr.attributeID,
                            display: displayName,
                            Description: attr.description || '',
                            GroupDesc: attr.groupDesc || '',
                            GroupOrder: attr.groupOrder,
                            SortOrder: attr.sortOrder,
                            TabName: attr.tabName || '',
                            ShowInDetails: attr.showInDetails || false,
                            MetaDataID: attr.metaDataID || null,
                            isSelected: true,
                        };
                    });
                    packlistItemAttributesData = mappedData;
                } else {
                    hasError = true;
                }

                if (hasError) {
                    setState(prev => ({ ...prev, packlistItemAttributesDlgLoading: false, packlistItemAttributesDlgError: 'Error loading item attributes', packlistItemAttributesDropdownLoading: false, packlistItemAttributesMetadataDropdownLoading: false }));
                } else {
                    setState(prev => ({ ...prev, packlistItemAttributesData, packlistItemAttributesDropdownData: dropdownData, packlistItemAttributesMetadataDropdownData: metadataData, packlistItemAttributesDlgLoading: false, packlistItemAttributesDropdownLoading: false, packlistItemAttributesMetadataDropdownLoading: false }));
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, packlistItemAttributesDlgLoading: false, packlistItemAttributesDlgError: 'Error loading item attributes: ' + err.toString(), packlistItemAttributesDropdownLoading: false, packlistItemAttributesMetadataDropdownLoading: false }));
            });
    }

    const closePacklistItemAttributesDialog = () => {
        setState(prev => ({ ...prev, packlistItemAttributesDlgOpen: false, packlistItemAttributesPacklistType: null, packlistItemAttributesData: [], packlistItemAttributesSort: [{ field: 'Description', dir: 'asc' }], packlistItemAttributesFilter: [], packlistItemAttributesFilterTexts: {}, packlistItemAttributesFocusField: undefined, packlistItemAttributesEditingId: null, packlistItemAttributesDropdownData: [], packlistItemAttributesMetadataDropdownData: [], packlistItemAttributesDeleteConfirmId: null, packlistItemAttributesDeleteInProgress: false, packlistItemAttributesDeleteError: '' }));
    }

    const savePacklistItemAttributeEdit = (attributeId: number) => {
        const attribute = state.packlistItemAttributesData.find(a => a.PackListTypeItemAttributeID === attributeId);
        if (!attribute || !state.packlistItemAttributesPacklistType) return;

        // Validate that an attribute has been selected
        if (!attribute.AttributeID) {
            setState(prev => ({ ...prev, packlistItemAttributesDlgError: 'Please select an Attribute before saving.' }));
            return;
        }

        // Read values from the uncontrolled inputs using data-field attributes
        const descInput = document.querySelector(`[data-item-id="${attributeId}"][data-field="PIADescription"]`) as HTMLInputElement;
        const groupDescInput = document.querySelector(`[data-item-id="${attributeId}"][data-field="PIAGroupDesc"]`) as HTMLInputElement;
        const tabNameInput = document.querySelector(`[data-item-id="${attributeId}"][data-field="PIATabName"]`) as HTMLInputElement;
        const showInDetailsCheckbox = document.querySelector(`[data-item-id="${attributeId}"][data-field="PIAShowInDetails"]`) as HTMLInputElement;

        const description = descInput?.value || attribute.Description || '';
        const groupDesc = groupDescInput?.value || attribute.GroupDesc || '';
        const tabName = tabNameInput?.value || attribute.TabName || '';
        const showInDetails = showInDetailsCheckbox?.checked || attribute.ShowInDetails || false;

        // Set loading state while saving
        setState(prev => ({ ...prev, packlistItemAttributesDlgLoading: true, packlistItemAttributesDlgError: '' }));

        const url = props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdatePackListTypeItemAttribute';
        
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify({
                packListTypeItemAttributeID: attributeId,
                packListTypeID: state.packlistItemAttributesPacklistType.packlistTypeID,
                attributeID: attribute.AttributeID,
                description: description,
                groupDesc: groupDesc,
                groupOrder: attribute.GroupOrder ?? 0,
                sortOrder: attribute.SortOrder ?? 1,
                tabName: tabName,
                showInDetails: showInDetails,
                metaDataID: attribute.MetaDataID,
            })
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'OK') {
                    const newAttributeId = res.result || attributeId;
                    setState(prev => ({
                        ...prev,
                        packlistItemAttributesDlgLoading: false,
                        packlistItemAttributesEditingId: null,
                        packlistItemAttributesData: prev.packlistItemAttributesData.map(a =>
                            a.PackListTypeItemAttributeID === attributeId
                                ? { ...a, PackListTypeItemAttributeID: newAttributeId, Description: description, GroupDesc: groupDesc, TabName: tabName, ShowInDetails: showInDetails, inEdit: false }
                                : a
                        ),
                    }));
                } else {
                    setState(prev => ({
                        ...prev,
                        packlistItemAttributesDlgLoading: false,
                        packlistItemAttributesDlgError: 'Error saving item attribute: ' + (res.callStatusMessage || 'Unknown error'),
                    }));
                }
            })
            .catch(err => {
                setState(prev => ({
                    ...prev,
                    packlistItemAttributesDlgLoading: false,
                    packlistItemAttributesDlgError: 'Error saving item attribute: ' + err.toString(),
                }));
            });
    }

    const openPacklistItemAttributeDeleteConfirm = (attributeId: number) => {
        setState(prev => ({ ...prev, packlistItemAttributesDeleteConfirmId: attributeId, packlistItemAttributesDeleteError: '' }));
    }

    const closePacklistItemAttributeDeleteConfirm = () => {
        setState(prev => ({ ...prev, packlistItemAttributesDeleteConfirmId: null, packlistItemAttributesDeleteInProgress: false, packlistItemAttributesDeleteError: '' }));
    }

    const confirmPacklistItemAttributeDelete = () => {
        if (state.packlistItemAttributesDeleteConfirmId === null) return;

        const idToDelete = state.packlistItemAttributesDeleteConfirmId;
        const rowToDelete = state.packlistItemAttributesData.find(a => a.PackListTypeItemAttributeID === idToDelete);
        if (!rowToDelete) return;

        setState(prev => ({ ...prev, packlistItemAttributesDeleteInProgress: true, packlistItemAttributesDeleteError: '' }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/DeletePacklistTypeItemAttribute';
        
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify(idToDelete)

        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'OK') {
                    setState(prev => ({
                        ...prev,
                        packlistItemAttributesData: prev.packlistItemAttributesData.filter(a => a.PackListTypeItemAttributeID !== idToDelete),
                        packlistItemAttributesDeleteInProgress: false,
                        packlistItemAttributesDeleteConfirmId: null,
                        packlistItemAttributesEditingId: null,
                    }));
                } else {
                    setState(prev => ({
                        ...prev,
                        packlistItemAttributesDeleteInProgress: false,
                        packlistItemAttributesDeleteError: 'Error deleting: ' + res.callStatusMessage,
                    }));
                }
            })
            .catch(err => {
                setState(prev => ({
                    ...prev,
                    packlistItemAttributesDeleteInProgress: false,
                    packlistItemAttributesDeleteError: 'Error deleting: ' + err.toString(),
                }));
            });
    }

    const openPacklistItemAttributesUserRolesDialog = (itemAttribute: any) => {
        setState(prev => ({
            ...prev,
            packlistItemAttributesUserRolesDlgOpen: true,
            packlistItemAttributesUserRolesItemAttribute: itemAttribute,
            packlistItemAttributesUserRolesDlgLoading: true,
            packlistItemAttributesUserRolesDlgError: '',
            packlistItemAttributesUserRolesData: [],
            packlistItemAttributesUserRolesDropdownData: [],
        }));

        // Fetch user roles assigned to this item attribute and the dropdown of available user roles
        const itemAttributeUserRolesUrl = `${props.userInf.currProject.apiUrl}/api/TMI/GetUserRolePacklistItemAttributes?attributeID=${itemAttribute.AttributeID}`;
        const dropdownUrl = `${props.userInf.currProject.apiUrl}/api/TMI/GetUserRolesForDropDown`;

        Promise.all([
            fetch(itemAttributeUserRolesUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
            }).then(resp => resp.json()),
            fetch(dropdownUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
            }).then(resp => resp.json()),
        ])
            .then(([itemAttributeUserRolesRes, dropdownRes]) => {
                const dropdownData = (dropdownRes.results || []).map((role: any) => ({
                    value: role.value?.toString() || '',
                    display: role.display || '',
                }));

                const mappedData = (itemAttributeUserRolesRes.results || []).map((role: any) => {
                    // Look up the display name from the dropdown data using userRoleID
                    const userRoleId = role.userRoleID?.toString() || '';
                    const displayName = dropdownData.find(d => d.value === userRoleId)?.display || '';
                    
                    return {
                        UserRolePacklistItemAttributeID: role.userRolePackListItemAttributeID,
                        AttributeID: role.attributeID,
                        UserRoleID: role.userRoleID,
                        display: displayName,
                        Editable: role.editable || false,
                        LastChangedBy: role.lastChangedBy || '',
                        LastChangedOn: role.lastChangedOn ? new Date(role.lastChangedOn) : null,
                    };
                });

                setState(prev => ({
                    ...prev,
                    packlistItemAttributesUserRolesDlgLoading: false,
                    packlistItemAttributesUserRolesData: mappedData,
                    packlistItemAttributesUserRolesDropdownData: dropdownData,
                }));
            })
            .catch(err => {
                setState(prev => ({
                    ...prev,
                    packlistItemAttributesUserRolesDlgLoading: false,
                    packlistItemAttributesUserRolesDlgError: 'Error loading user roles: ' + err.message,
                }));
            });
    }

    const closePacklistItemAttributesUserRolesDialog = () => {
        setState(prev => ({
            ...prev,
            packlistItemAttributesUserRolesDlgOpen: false,
            packlistItemAttributesUserRolesItemAttribute: null,
            packlistItemAttributesUserRolesData: [],
            packlistItemAttributesUserRolesDropdownData: [],
            packlistItemAttributesUserRolesDlgLoading: false,
            packlistItemAttributesUserRolesDlgError: '',
        }));
    }

    const openPacklistItemAttributesUserRoleDeleteConfirm = (userRolePacklistItemAttributeId: number) => {
        setState(prev => ({
            ...prev,
            packlistItemAttributesUserRolesDeleteConfirmId: userRolePacklistItemAttributeId,
            packlistItemAttributesUserRolesDeleteError: '',
        }));
    }

    const closePacklistItemAttributesUserRoleDeleteConfirm = () => {
        setState(prev => ({
            ...prev,
            packlistItemAttributesUserRolesDeleteConfirmId: null,
            packlistItemAttributesUserRolesDeleteInProgress: false,
            packlistItemAttributesUserRolesDeleteError: '',
        }));
    }

    const savePacklistItemAttributesUserRoleEdit = (userRoleId: number) => {
        const userRole = state.packlistItemAttributesUserRolesData.find(r => r.UserRolePacklistItemAttributeID === userRoleId);
        if (!userRole || userRole.UserRoleID === null) {
            setState(prev => ({ ...prev, packlistItemAttributesUserRolesDlgError: 'Please select a User Role before saving.' }));
            return;
        }

        setState(prev => ({ ...prev, packlistItemAttributesUserRolesDlgLoading: true }));

        const payload = {
            userRolePacklistItemAttributeID: userRole.UserRolePacklistItemAttributeID,
            attributeID: userRole.AttributeID,
            userRoleID: userRole.UserRoleID,
            editable: userRole.Editable,
        };

        fetch(`${props.userInf.currProject.apiUrl}/api/TMI/AddOrUpdateUserRolePacklistItemAttribute`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify(payload),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'OK') {
                    setState(prev => ({
                        ...prev,
                        packlistItemAttributesUserRolesData: prev.packlistItemAttributesUserRolesData.map(item =>
                            item.UserRolePacklistItemAttributeID === userRoleId
                                ? { ...item, inEdit: false, UserRolePacklistItemAttributeID: res.result || item.UserRolePacklistItemAttributeID }
                                : item
                        ),
                        packlistItemAttributesUserRolesDlgLoading: false,
                    }));
                } else {
                    setState(prev => ({
                        ...prev,
                        packlistItemAttributesUserRolesDlgError: 'Error saving: ' + res.callStatusMessage,
                        packlistItemAttributesUserRolesDlgLoading: false,
                    }));
                }
            })
            .catch(err => {
                setState(prev => ({
                    ...prev,
                    packlistItemAttributesUserRolesDlgError: 'Error saving: ' + err.message,
                    packlistItemAttributesUserRolesDlgLoading: false,
                }));
            });
    };

    const confirmPacklistItemAttributesUserRoleDelete = () => {
        if (state.packlistItemAttributesUserRolesDeleteConfirmId === null) return;

        const idToDelete = state.packlistItemAttributesUserRolesDeleteConfirmId;
        setState(prev => ({ ...prev, packlistItemAttributesUserRolesDeleteInProgress: true }));

        fetch(`${props.userInf.currProject.apiUrl}/api/TMI/DeleteUserRolePacklistItemAttribute`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify(idToDelete),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'OK') {
                    setState(prev => ({
                        ...prev,
                        packlistItemAttributesUserRolesDeleteInProgress: false,
                        packlistItemAttributesUserRolesDeleteConfirmId: null,
                        packlistItemAttributesUserRolesData: prev.packlistItemAttributesUserRolesData.filter(
                            r => r.UserRolePacklistItemAttributeID !== idToDelete
                        ),
                    }));
                } else {
                    setState(prev => ({
                        ...prev,
                        packlistItemAttributesUserRolesDeleteInProgress: false,
                        packlistItemAttributesUserRolesDeleteError: 'Error deleting: ' + res.callStatusMessage,
                    }));
                }
            })
            .catch(err => {
                setState(prev => ({
                    ...prev,
                    packlistItemAttributesUserRolesDeleteInProgress: false,
                    packlistItemAttributesUserRolesDeleteError: 'Error: ' + err.message,
                }));
            });
    }


    const saveAttributeEdit = (attributeId: number) => {
        const attribute = state.attributesData.find(a => a.PackListTypeAttributeID === attributeId);
        if (!attribute || !state.attributesPacklistType) return;

        // Validate that an attribute has been selected
        if (!attribute.PackListAttributeID) {
            setState(prev => ({ ...prev, attributesDlgError: 'Please select an Attribute before saving.' }));
            return;
        }

        // Read values from the uncontrolled inputs
        const descInput = document.querySelector(`input[data-field="Description"]`) as HTMLInputElement;
        const groupDescInput = document.querySelector(`input[data-field="GroupDesc"]`) as HTMLInputElement;
        const tabNameInput = document.querySelector(`input[data-field="TabName"]`) as HTMLInputElement;

        const description = descInput?.value || attribute.Description || '';
        const groupDesc = groupDescInput?.value || attribute.GroupDesc || '';
        const tabName = tabNameInput?.value || attribute.TabName || '';

        // Set loading state while saving
        setState(prev => ({ ...prev, attributesDlgLoading: true, attributesDlgError: '' }));

        const url = props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdatePackListTypeAttribute';
        
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify({
                packListTypeAttributeID: attributeId,
                packListTypeID: state.attributesPacklistType.packlistTypeID,
                packListAttributeID: attribute.PackListAttributeID,
                description: description,
                groupDesc: groupDesc,
                groupOrder: attribute.GroupOrder ?? 0,
                sortOrder: attribute.SortOrder ?? 1,
                tabName: tabName,
                metaDataID: attribute.MetaDataID,
            })
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'OK') {
                    const newAttributeId = res.result || attributeId;
                    setState(prev => ({
                        ...prev,
                        attributesDlgLoading: false,
                        attributesEditingId: null,
                        attributesData: prev.attributesData.map(a =>
                            a.PackListTypeAttributeID === attributeId
                                ? { ...a, PackListTypeAttributeID: newAttributeId, Description: description, GroupDesc: groupDesc, TabName: tabName, inEdit: false }
                                : a
                        ),
                    }));
                } else {
                    setState(prev => ({
                        ...prev,
                        attributesDlgLoading: false,
                        attributesDlgError: 'Error saving attribute: ' + (res.callStatusMessage || 'Unknown error'),
                    }));
                }
            })
            .catch(err => {
                setState(prev => ({
                    ...prev,
                    attributesDlgLoading: false,
                    attributesDlgError: 'Error saving attribute: ' + err.toString(),
                }));
            });
    }

    const openAttributeDeleteConfirm = (attributeId: number) => {
        setState(prev => ({ ...prev, attributesDeleteConfirmId: attributeId, attributesDeleteError: '' }));
    }

    const closeAttributeDeleteConfirm = () => {
        setState(prev => ({ ...prev, attributesDeleteConfirmId: null, attributesDeleteInProgress: false, attributesDeleteError: '' }));
    }

    const confirmAttributeDelete = () => {
        if (state.attributesDeleteConfirmId === null) return;

        const idToDelete = state.attributesDeleteConfirmId;
        const rowToDelete = state.attributesData.find(a => a.PackListTypeAttributeID === idToDelete);
        if (!rowToDelete) return;

        setState(prev => ({ ...prev, attributesDeleteInProgress: true, attributesDeleteError: '' }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/DeletePacklistTypeAttribute';
        
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify(idToDelete)

        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'OK') {
                    setState(prev => ({
                        ...prev,
                        attributesData: prev.attributesData.filter(a => a.PackListTypeAttributeID !== idToDelete),
                        attributesDeleteInProgress: false,
                        attributesDeleteConfirmId: null,
                        attributesEditId: null,
                        attributesEditOriginal: null,
                    }));
                } else {
                    setState(prev => ({
                        ...prev,
                        attributesDeleteInProgress: false,
                        attributesDeleteError: 'Error deleting: ' + res.callStatusMessage,
                    }));
                }
            })
            .catch(err => {
                setState(prev => ({
                    ...prev,
                    attributesDeleteInProgress: false,
                    attributesDeleteError: 'Error deleting: ' + err.toString(),
                }));
            });
    }

    const deleteAttribute = (attributeId: number) => {
        openAttributeDeleteConfirm(attributeId);
    }

    const openAttributeEditorDialog = (attribute: any) => {
        setState(prev => ({
            ...prev,
            attributeEditorDlgOpen: true,
            attributeEditorAttribute: attribute,
            attributeEditorUserRolesDlgLoading: true,
            attributeEditorUserRolesDlgError: '',
            attributeEditorUserRolesData: [],
            attributeEditorUserRolesDropdownData: [],
        }));

        // Fetch user roles assigned to this attribute and the dropdown of available user roles
        const attributeUserRolesUrl = `${props.userInf.currProject.apiUrl}/api/TMI/GetUserRolePacklistAttributes?packListAttributeID=${attribute.PackListAttributeID}`;
        const dropdownUrl = `${props.userInf.currProject.apiUrl}/api/TMI/GetUserRolesForDropDown`;

        Promise.all([
            fetch(attributeUserRolesUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
            }).then(resp => resp.json()),
            fetch(dropdownUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
            }).then(resp => resp.json()),
        ])
            .then(([attributeUserRolesRes, dropdownRes]) => {
                let userRolesData: any[] = [];
                let dropdownData: LookupListItem[] = [];

                // Process dropdown data
                if (dropdownRes.callStatus === 'OK' && Array.isArray(dropdownRes.results)) {
                    dropdownData = dropdownRes.results.map((item: any) => ({
                        value: item.value || item.id?.toString(),
                        display: item.display || item.name,
                    }));
                }

                // Process assigned user roles
                if (attributeUserRolesRes.callStatus === 'OK' && Array.isArray(attributeUserRolesRes.results)) {
                    userRolesData = attributeUserRolesRes.results.map((item: any) => ({
                        UserRolePackListAttributeID: item.userRolePackListAttributeID || 0,
                        PackListAttributeID: item.packListAttributeID,
                        UserRoleID: item.userRoleID,
                        display: dropdownData.find(d => d.value === item.userRoleID?.toString())?.display || `Role ${item.userRoleID}`,
                        Editable: item.editable || false,
                        LastChangedBy: item.lastChangedBy || '',
                        LastChangedOn: item.lastChangedOn ? new Date(item.lastChangedOn) : null,
                    }));
                }

                setState(prev => ({
                    ...prev,
                    attributeEditorUserRolesData: userRolesData,
                    attributeEditorUserRolesDropdownData: dropdownData,
                    attributeEditorUserRolesDlgLoading: false,
                }));
            })
            .catch(err => {
                setState(prev => ({
                    ...prev,
                    attributeEditorUserRolesDlgLoading: false,
                    attributeEditorUserRolesDlgError: err.toString(),
                }));
            });
    }

    const closeAttributeEditorDialog = () => {
        setState(prev => ({
            ...prev,
            attributeEditorDlgOpen: false,
            attributeEditorAttribute: null,
            attributeEditorUserRolesData: [],
            attributeEditorUserRolesDropdownData: [],
            attributeEditorUserRolesDlgLoading: false,
            attributeEditorUserRolesDlgError: '',
        }));
    }

    const openAttributeEditorUserRoleDeleteConfirm = (userRolePacklistAttributeId: number) => {
        setState(prev => ({
            ...prev,
            attributeEditorUserRolesDeleteConfirmId: userRolePacklistAttributeId,
            attributeEditorUserRolesDeleteError: '',
        }));
    }

    const closeAttributeEditorUserRoleDeleteConfirm = () => {
        setState(prev => ({
            ...prev,
            attributeEditorUserRolesDeleteConfirmId: null,
            attributeEditorUserRolesDeleteInProgress: false,
            attributeEditorUserRolesDeleteError: '',
        }));
    }

    const confirmAttributeEditorUserRoleDelete = () => {
        if (state.attributeEditorUserRolesDeleteConfirmId === null) return;

        const idToDelete = state.attributeEditorUserRolesDeleteConfirmId;
        setState(prev => ({ ...prev, attributeEditorUserRolesDeleteInProgress: true }));

        fetch(`${props.userInf.currProject.apiUrl}/api/TMI/DeleteUserRolePacklistAttribute`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify(idToDelete),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'OK') {
                    setState(prev => ({
                        ...prev,
                        attributeEditorUserRolesData: prev.attributeEditorUserRolesData.filter(
                            item => item.UserRolePackListAttributeID !== idToDelete
                        ),
                        attributeEditorUserRolesDeleteInProgress: false,
                        attributeEditorUserRolesDeleteConfirmId: null,
                    }));
                } else {
                    setState(prev => ({
                        ...prev,
                        attributeEditorUserRolesDeleteInProgress: false,
                        attributeEditorUserRolesDeleteError: 'Error deleting: ' + res.callStatusMessage,
                    }));
                }
            })
            .catch(err => {
                setState(prev => ({
                    ...prev,
                    attributeEditorUserRolesDeleteInProgress: false,
                    attributeEditorUserRolesDeleteError: 'Error deleting: ' + err.toString(),
                }));
            });
    }

    const handleAttributesSortChange = useCallback((field: string) => {
        setState(prev => {
            const current = prev.attributesSort && prev.attributesSort.length > 0 ? prev.attributesSort[0] : undefined;
            let newSort: SortDescriptor[] = [];
            if (!current || current.field !== field) {
                newSort = [{ field, dir: 'asc' }];
            } else if (current.dir === 'asc') {
                newSort = [{ field, dir: 'desc' }];
            } else {
                newSort = [];
            }
            return { ...prev, attributesSort: newSort };
        });
    }, []);

    const handleAttributesFilterChange = useCallback((field: string, event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setState(prev => ({ ...prev, attributesFilterTexts: { ...prev.attributesFilterTexts, [field]: value } }));
    }, []);

    const buildAttributesHeaderCellRenderer = useCallback((field: string, title: string, width?: number | string) => {
        const cacheKey = `${HEADER_RENDERER_VERSION}_attributes_${field}`;
        if (!attributesHeaderCellRenderersRef.current[cacheKey]) {
            attributesHeaderCellRenderersRef.current[cacheKey] = (props: any) => {
                const val = stateRef.current.attributesFilterTexts?.[field] ?? '';
                const focused = stateRef.current.attributesFocusField === field;
                const sortDir = (stateRef.current.attributesSort && stateRef.current.attributesSort.length > 0 && stateRef.current.attributesSort[0].field === field) ? (stateRef.current.attributesSort[0].dir as string) : undefined;
                return (
                    <th className="tmi-header-filter">
                        <div className="k-link" onClick={() => handleAttributesSortChange(field)} aria-hidden="true" />
                        <div className={`tmi-floating-wrapper ${val ? 'has-value' : ''} ${focused ? 'focused' : ''}`}>
                            <label onClick={() => handleAttributesSortChange(field)} style={{ whiteSpace: 'normal', wordWrap: 'break-word', overflow: 'visible', cursor: 'pointer', userSelect: 'none', pointerEvents: 'auto', position: 'relative', zIndex: 10, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span>{title}</span>
                                <span className="tmi-sort-glyph-inline">{sortDir === 'asc' ? '▲' : sortDir === 'desc' ? '▼' : '↕'}</span>
                            </label>
                            <input
                                type="text"
                                className="k-input k-input-sm"
                                value={val}
                                onChange={(e) => handleAttributesFilterChange(field, e)}
                                onFocus={() => setState(prev => ({ ...prev, attributesFocusField: field }))}
                                onBlur={() => setState(prev => ({ ...prev, attributesFocusField: undefined }))}
                                onMouseDown={(e) => { e.stopPropagation(); }}
                            />
                        </div>
                    </th>
                );
            };
        }
        return attributesHeaderCellRenderersRef.current[cacheKey];
    }, [handleAttributesSortChange, handleAttributesFilterChange]);

    // ============ MILESTONES DIALOG FUNCTIONS ============

    const openMilestonesDialog = (packlistType: PacklistTypeRow) => {
        closeRowMenu();
        setState(prev => ({
            ...prev,
            milestonesDlgOpen: true,
            milestonesPacklistType: packlistType,
            milestonesSelected: new Set(),
            milestoneDlgLoading: true,
            milestoneDlgError: '',
            milestoneDlgFilterText: '',
        }));

        const selectedUrl = `${props.userInf.currProject.apiUrl}/api/TMI/GetMilestonePackListTypes?packListTypeID=${packlistType.packlistTypeID}`;
        const dropdownUrl = `${props.userInf.currProject.apiUrl}/api/TMI/GetMilestonesForDropDown`;
        
        Promise.all([
            fetch(selectedUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
            }).then(resp => resp.json()),
            fetch(dropdownUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
            }).then(resp => resp.json()),
        ])
            .then(([dataRes, dropdownRes]) => {
                let dropdownData: LookupListItem[] = [];
                let selectedIds = new Set<string>();
                let hasError = false;

                if (dropdownRes.callStatus === 'OK' && Array.isArray(dropdownRes.results)) {
                    dropdownData = dropdownRes.results.map((item: any) => ({
                        value: item.value || item.id?.toString(),
                        display: item.display || item.name,
                    }));
                }

                if (dataRes.callStatus === 'OK' && Array.isArray(dataRes.results)) {
                    // Handle both cases: plain IDs or objects with milestoneID field
                    selectedIds = new Set(
                        (dataRes.results as any[]).map((item: any) => {
                            const id = typeof item === 'object' && item !== null 
                                ? (item.milestoneID?.toString() || item.id?.toString() || '')
                                : String(item);
                            return id;
                        }).filter(id => id) // Filter out empty strings
                    );
                } else {
                    hasError = true;
                }

                setState(prev => ({
                    ...prev,
                    milestonesDropdownData: dropdownData,
                    milestonesSelected: selectedIds,
                    milestoneDlgLoading: false,
                    milestoneDlgError: hasError ? (dataRes.callStatusMessage || 'Error loading milestones') : '',
                }));
            })
            .catch(err => {
                setState(prev => ({
                    ...prev,
                    milestoneDlgLoading: false,
                    milestoneDlgError: 'Error loading milestones: ' + err.toString(),
                }));
            });
    };

    const closeMilestonesDialog = () => {
        setState(prev => ({
            ...prev,
            milestonesDlgOpen: false,
            milestonesPacklistType: null,
            milestonesDropdownData: [],
            milestonesSelected: new Set(),
            milestoneDlgLoading: false,
            milestoneDlgError: '',
            milestoneDlgFilterText: '',
        }));
    };

    const toggleMilestoneSelection = (milestoneId: string) => {
        setState(prev => {
            const newSelected = new Set(prev.milestonesSelected);
            if (newSelected.has(milestoneId)) {
                newSelected.delete(milestoneId);
            } else {
                newSelected.add(milestoneId);
            }
            
            // Auto-save on toggle
            if (prev.milestonesPacklistType) {
                const url = props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdateMilestonePackListTypes';
                fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + props.userInf.token,
                    },
                    body: JSON.stringify({
                        selected: prev.milestonesPacklistType.packlistTypeID,
                        checked: Array.from(newSelected).map(id => parseInt(id, 10)),
                    }),
                })
                    .then(resp => resp.json())
                    .then(res => {
                        if (res.callStatus === 'UNAUTH') {
                            const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                            props.updateUserInfo(uInf);
                        } else if (res.callStatus !== 'OK') {
                            setState(s => ({
                                ...s,
                                milestoneDlgError: 'Error saving: ' + res.callStatusMessage,
                            }));
                        }
                    })
                    .catch(err => {
                        setState(s => ({
                            ...s,
                            milestoneDlgError: 'Error saving: ' + err.toString(),
                        }));
                    });
            }
            
            return { ...prev, milestonesSelected: newSelected };
        });
    };

    const handleMilestonesFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setState(prev => ({ ...prev, milestoneDlgFilterText: e.target.value }));
    };



    const openStatusDialog = (packlistType: PacklistTypeRow) => {
        closeRowMenu();
        setState(prev => ({ ...prev, statusDlgOpen: true, statusPacklistType: packlistType, statusDlgLoading: true, statusDlgError: '', statusSort: [{ field: 'display', dir: 'asc' }], statusFilter: [], statusFilterTexts: {}, statusFocusField: undefined, statusDropdownLoading: true }));
        
        const dataUrl = `${props.userInf.currProject.apiUrl}/api/TMI/GetPacklistTypeStatus?packListTypeID=${packlistType.packlistTypeID}`;
        const dropdownUrl = `${props.userInf.currProject.apiUrl}/api/TMI/GetPackListStatusForDropDown`;
        
        Promise.all([
            fetch(dataUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
            }).then(resp => resp.json()),
            fetch(dropdownUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
            }).then(resp => resp.json()),
        ])
            .then(([dataRes, dropdownRes]) => {
                let statusData: Array<{
                    PackListTypeStatusID: number;
                    PackListStatusID: number;
                    display: string;
                    PackListTypeID: number;
                    PacklistTypeStatusOrder: number | null;
                    LastChangedBy: string;
                    LastChangedOn: Date | null;
                    isSelected: boolean;
                }> = [];
                let dropdownData: LookupListItem[] = [];
                let hasError = false;

                if (dropdownRes.callStatus === 'OK' && Array.isArray(dropdownRes.results)) {
                    dropdownData = dropdownRes.results.map((item: any) => ({
                        value: item.value || item.id?.toString(),
                        display: item.display || item.name,
                    }));
                }

                if (dataRes.callStatus === 'OK' && Array.isArray(dataRes.results)) {
                    statusData = dataRes.results.map((item: any) => {
                        const displayName = dropdownData.find(d => d.value === item.packListStatusID?.toString())?.display || (item.packListStatusID ? `Status ${item.packListStatusID}` : '(No Status)');
                        return {
                            PackListTypeStatusID: item.packListTypeStatusID,
                            PackListStatusID: item.packListStatusID,
                            display: displayName,
                            PackListTypeID: item.packListTypeID,
                            PacklistTypeStatusOrder: item.packlistTypeStatusOrder || null,
                            LastChangedBy: item.lastChangedBy || '',
                            LastChangedOn: item.lastChangedOn ? new Date(item.lastChangedOn) : null,
                            isSelected: true,
                        };
                    });
                } else {
                    hasError = true;
                }

                setState(prev => ({
                    ...prev,
                    statusData: statusData,
                    statusDropdownData: dropdownData,
                    statusDlgLoading: false,
                    statusDropdownLoading: false,
                    statusDlgError: hasError ? (dataRes.callStatusMessage || 'Error loading statuses') : '',
                }));
            })
            .catch(err => {
                setState(prev => ({
                    ...prev,
                    statusDlgLoading: false,
                    statusDropdownLoading: false,
                    statusDlgError: 'Error loading statuses: ' + err.toString(),
                }));
            });
    };

    const closeStatusDialog = () => {
        setState(prev => ({
            ...prev,
            statusDlgOpen: false,
            statusPacklistType: null,
            statusData: [],
            statusDlgLoading: false,
            statusDlgError: '',
            statusSort: [{ field: 'display', dir: 'asc' }],
            statusFilter: [],
            statusFilterTexts: {},
            statusFocusField: undefined,
            statusEditId: null,
            statusEditRowIndex: null,
            statusEditOriginal: null,
            statusAddingNew: false,
            statusNewStatusId: null,
            statusNewOrder: null,
            statusDeleteConfirmId: null,
            statusDeleteInProgress: false,
            statusDeleteError: '',
        }));
    };



    const saveStatusEdit = (statusPackListTypeId: number) => {
        const status = state.statusData.find(s => s.PackListTypeStatusID === statusPackListTypeId);
        if (!status) return;

        // Validate that a status has been selected
        if (!status.PackListStatusID) {
            setState(prev => ({ ...prev, statusDlgError: 'Please select a Status before saving.' }));
            return;
        }

        // Read PacklistTypeStatusOrder from the uncontrolled input
        const orderInput = document.querySelector(`input[data-field="PacklistTypeStatusOrder"]`) as HTMLInputElement;
        const packlistTypeStatusOrder = orderInput?.value ? parseInt(orderInput.value, 10) : null;

        setState(prev => ({ ...prev, statusDlgLoading: true, statusDlgError: '' }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdatePackListTypeStatus';
        
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify({
                packListTypeStatusID: statusPackListTypeId,
                packListStatusID: status.PackListStatusID,
                packListTypeID: status.PackListTypeID,
                packlistTypeStatusOrder: packlistTypeStatusOrder,
            })
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'OK') {
                    setState(prev => ({
                        ...prev,
                        statusEditingId: null,
                        statusDlgLoading: false,
                        statusData: prev.statusData.map(s =>
                            s.PackListTypeStatusID === statusPackListTypeId
                                ? { ...s, PacklistTypeStatusOrder: packlistTypeStatusOrder, inEdit: false }
                                : s
                        ),
                    }));
                } else {
                    setState(prev => ({
                        ...prev,
                        statusDlgLoading: false,
                        statusDlgError: 'Error saving: ' + (res.callStatusMessage || 'Unknown error'),
                    }));
                }
            })
            .catch(err => {
                setState(prev => ({
                    ...prev,
                    statusDlgLoading: false,
                    statusDlgError: 'Error saving: ' + err.toString(),
                }));
            });
    };

    const openStatusDeleteConfirm = (statusId: number) => {
        setState(prev => ({ ...prev, statusDeleteConfirmId: statusId, statusDeleteError: '' }));
    }

    const closeStatusDeleteConfirm = () => {
        setState(prev => ({ ...prev, statusDeleteConfirmId: null, statusDeleteInProgress: false, statusDeleteError: '' }));
    }

    const confirmStatusDelete = () => {
        if (state.statusDeleteConfirmId === null) return;
        
        setState(prev => ({ ...prev, statusDeleteInProgress: true, statusDeleteError: '' }));

        const url = props.userInf.currProject.apiUrl + '/api/TMI/DeletePackListTypeStatus';
        
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify(state.statusDeleteConfirmId)
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'OK') {
                    setState(prev => ({
                        ...prev,
                        statusDeleteInProgress: false,
                        statusDeleteConfirmId: null,
                        statusData: prev.statusData.filter(s => s.PackListTypeStatusID !== state.statusDeleteConfirmId),
                    }));
                } else {
                    setState(prev => ({
                        ...prev,
                        statusDeleteInProgress: false,
                        statusDeleteError: 'Error deleting status: ' + (res.callStatusMessage || 'Unknown error'),
                    }));
                }
            })
            .catch(err => {
                setState(prev => ({
                    ...prev,
                    statusDeleteInProgress: false,
                    statusDeleteError: 'Error deleting status: ' + err.toString(),
                }));
            });
    }

    const handleStatusSortChange = useCallback((field: string) => {
        setState(prev => {
            const current = prev.statusSort && prev.statusSort.length > 0 ? prev.statusSort[0] : undefined;
            let newSort: SortDescriptor[] = [];
            if (!current || current.field !== field) {
                newSort = [{ field, dir: 'asc' }];
            } else if (current.dir === 'asc') {
                newSort = [{ field, dir: 'desc' }];
            } else {
                newSort = [];
            }
            return { ...prev, statusSort: newSort };
        });
    }, []);

    const handleStatusFilterChange = useCallback((field: string, e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setState(prev => ({ ...prev, statusFilterTexts: { ...prev.statusFilterTexts, [field]: val } }));
    }, []);

    const buildStatusHeaderCellRenderer = useCallback((field: string, title: string, width?: number | string) => {
        const cacheKey = `${HEADER_RENDERER_VERSION}_status_${field}`;
        if (!statusHeaderCellRenderersRef.current[cacheKey]) {
            statusHeaderCellRenderersRef.current[cacheKey] = (props: any) => {
                const val = stateRef.current.statusFilterTexts?.[field] ?? '';
                const focused = stateRef.current.statusFocusField === field;
                const sortDir = (stateRef.current.statusSort && stateRef.current.statusSort.length > 0 && stateRef.current.statusSort[0].field === field) ? (stateRef.current.statusSort[0].dir as string) : undefined;
                return (
                    <th className="tmi-header-filter">
                        <div className="k-link" onClick={() => handleStatusSortChange(field)} aria-hidden="true" />
                        <div className={`tmi-floating-wrapper ${val ? 'has-value' : ''} ${focused ? 'focused' : ''}`}>
                            <label onClick={() => handleStatusSortChange(field)} style={{ whiteSpace: 'normal', wordWrap: 'break-word', overflow: 'visible', cursor: 'pointer', userSelect: 'none', pointerEvents: 'auto', position: 'relative', zIndex: 10, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span>{title}</span>
                                <span className="tmi-sort-glyph-inline">{sortDir === 'asc' ? '▲' : sortDir === 'desc' ? '▼' : '↕'}</span>
                            </label>
                            <input
                                type="text"
                                className="k-input k-input-sm"
                                value={val}
                                onChange={(e) => handleStatusFilterChange(field, e)}
                                onFocus={() => setState(prev => ({ ...prev, statusFocusField: field }))}
                                onBlur={() => setState(prev => ({ ...prev, statusFocusField: undefined }))}
                                onMouseDown={(e) => { e.stopPropagation(); }}
                            />
                        </div>
                    </th>
                );
            };
        }
        return statusHeaderCellRenderersRef.current[cacheKey];
    }, [handleStatusSortChange, handleStatusFilterChange]);

    const openAllowedItemTypesDialog = (packlistType: PacklistTypeRow) => {
        setState(prev => ({
            ...prev,
            allowedItemTypesDlgOpen: true,
            allowedItemTypesPacklistType: packlistType,
            allowedItemTypesDlgLoading: true,
            allowedItemTypesDlgError: '',
            allowedItemTypesDlgFilterText: '',
            allowedItemTypesDropdownLoading: true,
            allowedItemTypesSelected: new Set(),
        }));

        // Load the dropdown data (all available item types)
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetItemTypesForDropDown';
        fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'OK') {
                    const dropdownData: LookupListItem[] = (res.results || []).map((item: any) => ({
                        value: item.value || '',
                        display: item.display || `Item Type ${item.value}`,
                    }));
                    setState(prev => ({ ...prev, allowedItemTypesDropdownData: dropdownData, allowedItemTypesDropdownLoading: false }));
                } else {
                    setState(prev => ({ ...prev, allowedItemTypesDropdownLoading: false, allowedItemTypesDlgError: 'Failed to load item types' }));
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, allowedItemTypesDropdownLoading: false, allowedItemTypesDlgError: 'Error loading item types: ' + err.toString() }));
            });

        // Load the currently selected item types for this packlist type
        const url2 = props.userInf.currProject.apiUrl + '/api/TMI/GetPackListTypeAllowedItemTypes?packListTypeID=' + packlistType.packlistTypeID;
        fetch(url2, {
            method: 'GET',
            headers: {
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'OK') {
                    const selectedIds: Set<string> = new Set((res.results || []).map((item: any) => (typeof item === 'object' ? item.allowedItemTypeID : item)?.toString() || ''));
                    setState(prev => ({ ...prev, allowedItemTypesSelected: selectedIds, allowedItemTypesDlgLoading: false }));
                } else {
                    setState(prev => ({ ...prev, allowedItemTypesDlgLoading: false, allowedItemTypesDlgError: 'Failed to load selected item types' }));
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, allowedItemTypesDlgLoading: false, allowedItemTypesDlgError: 'Error loading selected item types: ' + err.toString() }));
            });
    };

    const closeAllowedItemTypesDialog = () => {
        setState(prev => ({
            ...prev,
            allowedItemTypesDlgOpen: false,
            allowedItemTypesPacklistType: null,
            allowedItemTypesDropdownData: [],
            allowedItemTypesDropdownLoading: false,
            allowedItemTypesDlgLoading: false,
            allowedItemTypesDlgError: '',
            allowedItemTypesDlgFilterText: '',
            allowedItemTypesSelected: new Set(),
        }));
    };

    const handleAllowedItemTypesFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setState(prev => ({ ...prev, allowedItemTypesDlgFilterText: e.target.value }));
    };

    const toggleItemTypeSelection = (itemTypeId: string) => {
        setState(prev => {
            const newSelected = new Set(prev.allowedItemTypesSelected);
            if (newSelected.has(itemTypeId)) {
                newSelected.delete(itemTypeId);
            } else {
                newSelected.add(itemTypeId);
            }
            
            // Auto-save on toggle
            if (prev.allowedItemTypesPacklistType) {
                const url = props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdatePackListTypeAllowedItemTypes';
                fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + props.userInf.token,
                    },
                    body: JSON.stringify({
                        selected: prev.allowedItemTypesPacklistType.packlistTypeID,
                        checked: Array.from(newSelected).map(id => parseInt(id, 10)),
                    }),
                })
                    .then(resp => resp.json())
                    .then(res => {
                        if (res.callStatus === 'UNAUTH') {
                            const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                            props.updateUserInfo(uInf);
                        } else if (res.callStatus !== 'OK') {
                            setState(s => ({
                                ...s,
                                allowedItemTypesDlgError: 'Error saving: ' + res.callStatusMessage,
                            }));
                        }
                    })
                    .catch(err => {
                        setState(s => ({
                            ...s,
                            allowedItemTypesDlgError: 'Error saving: ' + err.toString(),
                        }));
                    });
            }
            
            return { ...prev, allowedItemTypesSelected: newSelected };
        });
    };



    const openAllowedPacklistTypesDialog = (packlistType: PacklistTypeRow) => {
        closeRowMenu();
        setState(prev => ({ 
            ...prev, 
            allowedPacklistTypesDlgOpen: true, 
            allowedPacklistTypesPacklistType: packlistType, 
            allowedPacklistTypesDlgLoading: true, 
            allowedPacklistTypesDlgError: '', 
            allowedPacklistTypesSort: [{ field: 'display', dir: 'asc' }], 
            allowedPacklistTypesFilter: [], 
            allowedPacklistTypesFilterTexts: {}, 
            allowedPacklistTypesFocusField: undefined, 
            allowedPacklistTypesDropdownLoading: true 
        }));
        
        // Fetch both selected records and dropdown options in parallel
        const selectedUrl = `${props.userInf.currProject.apiUrl}/api/TMI/GetPackListTypeAllowedPackListTypes?packListTypeID=${packlistType.packlistTypeID}`;
        const dropdownUrl = `${props.userInf.currProject.apiUrl}/api/TMI/GetPacklistTypesForDropDown`;
        
        Promise.all([
            fetch(selectedUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
            }).then(resp => resp.json()),
            fetch(dropdownUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
            }).then(resp => resp.json()),
        ])
            .then(([selectedRes, dropdownRes]) => {
                let allowedPacklistTypesData: Array<{
                    PackListTypeAllowedPackListTypesID: number;
                    PackListTypeID: number;
                    AllowedPackListTypeID: number;
                    display: string;
                    AllowMultipleParentsOfSameType: boolean;
                    LastChangedBy: string;
                    LastChangedOn: Date | null;
                }> = [];
                let dropdownData: LookupListItem[] = [];
                let hasError = false;

                // First, process dropdown data
                if (dropdownRes.callStatus === 'OK' && Array.isArray(dropdownRes.results)) {
                    dropdownData = dropdownRes.results.map((item: any) => ({
                        value: item.value || item.id?.toString(),
                        display: item.display || item.name,
                    }));
                }

                if (selectedRes.callStatus === 'OK' && Array.isArray(selectedRes.results)) {
                    const selectedRecords = selectedRes.results as Array<{
                        packListTypeAllowedPackListTypesID: number;
                        packListTypeID: number;
                        allowedPackListTypeID: number;
                        allowMultipleParentsOfSameType: boolean;
                        lastChangedBy: string | null;
                        lastChangedOn: string | null;
                    }>;
                    
                    const mappedData: Array<{
                        PackListTypeAllowedPackListTypesID: number;
                        PackListTypeID: number;
                        AllowedPackListTypeID: number;
                        display: string;
                        AllowMultipleParentsOfSameType: boolean;
                        LastChangedBy: string;
                        LastChangedOn: Date | null;
                    }> = selectedRecords.map(record => {
                        // Look up the display name from dropdown data
                        const displayName = dropdownData.find(d => d.value === record.allowedPackListTypeID.toString())?.display || `PackList Type ${record.allowedPackListTypeID}`;
                        return {
                            PackListTypeAllowedPackListTypesID: record.packListTypeAllowedPackListTypesID,
                            PackListTypeID: record.packListTypeID,
                            AllowedPackListTypeID: record.allowedPackListTypeID,
                            display: displayName,
                            AllowMultipleParentsOfSameType: record.allowMultipleParentsOfSameType,
                            LastChangedBy: record.lastChangedBy || '',
                            LastChangedOn: record.lastChangedOn ? new Date(record.lastChangedOn) : null,
                        };
                    });
                    allowedPacklistTypesData = mappedData;
                } else {
                    hasError = true;
                }

                if (hasError) {
                    setState(prev => ({ ...prev, allowedPacklistTypesDlgLoading: false, allowedPacklistTypesDlgError: 'Error loading allowed packlist types', allowedPacklistTypesDropdownLoading: false }));
                } else {
                    setState(prev => ({ 
                        ...prev, 
                        allowedPacklistTypesData, 
                        allowedPacklistTypesDropdownData: dropdownData, 
                        allowedPacklistTypesDlgLoading: false, 
                        allowedPacklistTypesDropdownLoading: false, 
                        allowedPacklistTypesAddingNew: false, 
                        allowedPacklistTypesNewPacklistTypeId: null, 
                        allowedPacklistTypesNewAllowMultiple: false 
                    }));
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, allowedPacklistTypesDlgLoading: false, allowedPacklistTypesDlgError: 'Error loading allowed packlist types: ' + err.toString(), allowedPacklistTypesDropdownLoading: false }));
            });
    }

    const closeAllowedPacklistTypesDialog = () => {
        setState(prev => ({ 
            ...prev, 
            allowedPacklistTypesDlgOpen: false, 
            allowedPacklistTypesPacklistType: null, 
            allowedPacklistTypesData: [], 
            allowedPacklistTypesSort: [{ field: 'display', dir: 'asc' }], 
            allowedPacklistTypesFilter: [], 
            allowedPacklistTypesFilterTexts: {}, 
            allowedPacklistTypesFocusField: undefined, 
            allowedPacklistTypesEditId: null, 
            allowedPacklistTypesEditRowIndex: null, 
            allowedPacklistTypesEditOriginal: null, 
            allowedPacklistTypesDropdownData: [], 
            allowedPacklistTypesAddingNew: false, 
            allowedPacklistTypesNewPacklistTypeId: null, 
            allowedPacklistTypesNewAllowMultiple: false 
        }));
    }

    const saveAllowedPacklistType = (packListTypeAllowedPackListTypesId: number, allowedPackListTypeID: number, allowMultipleParentsOfSameType: boolean) => {
        if (!state.allowedPacklistTypesPacklistType) return;

        // Validate that a packlist type has been selected
        if (!allowedPackListTypeID) {
            setState(prev => ({ ...prev, allowedPacklistTypesDlgError: 'Please select a Packlist Type before saving.' }));
            return;
        }

        setState(prev => ({ ...prev, allowedPacklistTypesDlgLoading: true, allowedPacklistTypesDlgError: '' }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdatePackListTypeAllowedPackListTypes';
        
        const payload = {
            PackListTypeAllowedPackListTypesID: packListTypeAllowedPackListTypesId,
            PackListTypeID: state.allowedPacklistTypesPacklistType.packlistTypeID,
            AllowedPackListTypeID: allowedPackListTypeID,
            AllowMultipleParentsOfSameType: allowMultipleParentsOfSameType,
            LastChangedBy: props.userInf.displayName,
            LastChangedOn: null,
        };
        
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify(payload),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'OK') {
                    setState(prev => ({
                        ...prev,
                        allowedPacklistTypesDlgLoading: false,
                    }));
                } else {
                    setState(prev => ({
                        ...prev,
                        allowedPacklistTypesDlgLoading: false,
                        allowedPacklistTypesDlgError: 'Error saving: ' + res.callStatusMessage,
                    }));
                }
            })
            .catch(err => {
                setState(prev => ({
                    ...prev,
                    allowedPacklistTypesDlgLoading: false,
                    allowedPacklistTypesDlgError: 'Error saving: ' + err.toString(),
                }));
            });
    }

    const openAllowedPacklistTypeDeleteConfirm = (packListTypeAllowedPackListTypesId: number) => {
        setState(prev => ({ ...prev, allowedPacklistTypesDeleteConfirmId: packListTypeAllowedPackListTypesId, allowedPacklistTypesDeleteError: '' }));
    }

    const closeAllowedPacklistTypeDeleteConfirm = () => {
        setState(prev => ({ ...prev, allowedPacklistTypesDeleteConfirmId: null, allowedPacklistTypesDeleteInProgress: false, allowedPacklistTypesDeleteError: '' }));
    }

    const confirmAllowedPacklistTypeDelete = () => {
        if (state.allowedPacklistTypesDeleteConfirmId === null) return;

        const idToDelete = state.allowedPacklistTypesDeleteConfirmId;
        const rowToDelete = state.allowedPacklistTypesData.find(r => r.PackListTypeAllowedPackListTypesID === idToDelete);
        if (!rowToDelete) return;

        setState(prev => ({ ...prev, allowedPacklistTypesDeleteInProgress: true, allowedPacklistTypesDeleteError: '' }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/DeletePackListTypeAllowedPackListTypes';
        
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify(idToDelete)
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'OK') {
                    setState(prev => ({
                        ...prev,
                        allowedPacklistTypesData: prev.allowedPacklistTypesData.filter(r => r.PackListTypeAllowedPackListTypesID !== idToDelete),
                        allowedPacklistTypesDeleteInProgress: false,
                        allowedPacklistTypesDeleteConfirmId: null,
                        allowedPacklistTypesEditId: null,
                        allowedPacklistTypesEditOriginal: null,
                    }));
                } else {
                    setState(prev => ({
                        ...prev,
                        allowedPacklistTypesDeleteInProgress: false,
                        allowedPacklistTypesDeleteError: 'Error deleting: ' + res.callStatusMessage,
                    }));
                }
            })
            .catch(err => {
                setState(prev => ({
                    ...prev,
                    allowedPacklistTypesDeleteInProgress: false,
                    allowedPacklistTypesDeleteError: 'Error deleting: ' + err.toString(),
                }));
            });
    }

    const deleteAllowedPacklistType = (packListTypeAllowedPackListTypesId: number) => {
        openAllowedPacklistTypeDeleteConfirm(packListTypeAllowedPackListTypesId);
    }

    const fetchPacklistHierarchyRelations = useCallback(async (packlistTypeID: number, mode: 'ancestors' | 'descendants') => {
        const endpoint = mode === 'ancestors'
            ? '/api/TMI/GetPacklistTypeAncestors'
            : '/api/TMI/GetPackListTypeAllowedPackListTypes';
        const url = `${props.userInf.currProject.apiUrl}${endpoint}?packListTypeID=${packlistTypeID}`;
        const resp = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        });
        const res = await resp.json();

        if (res.callStatus === 'UNAUTH') {
            const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
            props.updateUserInfo(uInf);
            throw new Error('Not authorised');
        }

        if (res.callStatus !== 'OK') {
            throw new Error(res.callStatusMessage || `Error loading ${mode}`);
        }

        return Array.isArray(res.results) ? res.results : [];
    }, [props]);

    const getHierarchyRecordNumber = useCallback((record: any, keys: string[]): number | null => {
        for (const key of keys) {
            const n = Number(record?.[key]);
            if (Number.isFinite(n)) return n;
        }
        return null;
    }, []);

    const getHierarchyRecordString = useCallback((record: any, keys: string[]): string => {
        for (const key of keys) {
            const value = record?.[key];
            if (value === null || value === undefined) continue;
            const s = String(value).trim();
            if (s) return s;
        }
        return '';
    }, []);

    const buildHierarchyEdge = useCallback((
        record: any,
        focusId: number,
        mode: 'ancestors' | 'descendants',
    ): { parentId: number; childId: number } | null => {
        const packListTypeID = getHierarchyRecordNumber(record, ['packListTypeID', 'PackListTypeID']);
        const allowedPackListTypeID = getHierarchyRecordNumber(record, ['allowedPackListTypeID', 'AllowedPackListTypeID']);
        if (packListTypeID === null || allowedPackListTypeID === null) return null;

        if (mode === 'descendants') {
            if (packListTypeID === focusId) return { parentId: focusId, childId: allowedPackListTypeID };
            if (allowedPackListTypeID === focusId) return { parentId: focusId, childId: packListTypeID };
            return { parentId: packListTypeID, childId: allowedPackListTypeID };
        }

        if (allowedPackListTypeID === focusId) return { parentId: packListTypeID, childId: focusId };
        if (packListTypeID === focusId) return { parentId: allowedPackListTypeID, childId: focusId };
        return { parentId: packListTypeID, childId: allowedPackListTypeID };
    }, [getHierarchyRecordNumber]);

    const closeHierarchyDialog = useCallback(() => {
        setState(prev => ({
            ...prev,
            hierarchyDlgOpen: false,
            hierarchyPacklistType: null,
            hierarchyLoading: false,
            hierarchyError: '',
            hierarchyNodes: {},
            hierarchyChildrenByParent: {},
            hierarchyParentsByChild: {},
            hierarchyExpanded: {},
        }));
    }, []);

    const openHierarchyDialog = useCallback((packlistType: PacklistTypeRow) => {
        setState(prev => ({
            ...prev,
            hierarchyDlgOpen: true,
            hierarchyPacklistType: packlistType,
            hierarchyLoading: true,
            hierarchyError: '',
            hierarchyNodes: {},
            hierarchyChildrenByParent: {},
            hierarchyParentsByChild: {},
            hierarchyExpanded: {
                [`ancestors_${packlistType.packlistTypeID}`]: true,
                [`descendants_${packlistType.packlistTypeID}`]: true,
            },
        }));

        void (async () => {
            try {
                const rootId = packlistType.packlistTypeID;
                const nodes = new Map<number, PacklistTypeHierarchyNode>();
                const childrenByParent = new Map<number, Set<number>>();
                const parentsByChild = new Map<number, Set<number>>();

                nodes.set(rootId, {
                    id: rootId,
                    code: packlistType.packlistTypeCode,
                    description: packlistType.description || '',
                });

                const addNode = (
                    id: number,
                    code: string | null | undefined,
                    description: string | null | undefined,
                ) => {
                    const existing = nodes.get(id);
                    const nextCode = (code || '').trim();
                    const nextDescription = (description || '').trim();
                    if (!existing) {
                        nodes.set(id, {
                            id,
                            code: nextCode || `PackList Type ${id}`,
                            description: nextDescription,
                        });
                        return;
                    }
                    if (!existing.code && nextCode) existing.code = nextCode;
                    if (!existing.description && nextDescription) existing.description = nextDescription;
                };

                const addEdge = (parentId: number, childId: number) => {
                    if (!childrenByParent.has(parentId)) childrenByParent.set(parentId, new Set<number>());
                    if (!parentsByChild.has(childId)) parentsByChild.set(childId, new Set<number>());
                    childrenByParent.get(parentId)!.add(childId);
                    parentsByChild.get(childId)!.add(parentId);
                };

                const resolveDescriptionForNode = (record: any, nodeId: number) => {
                    const packId = getHierarchyRecordNumber(record, ['packListTypeID', 'PackListTypeID']);
                    const allowedId = getHierarchyRecordNumber(record, ['allowedPackListTypeID', 'AllowedPackListTypeID']);
                    if (packId !== null && packId === nodeId) {
                        return getHierarchyRecordString(record, ['packListTypeDesc', 'PackListTypeDesc', 'packListTypeDescription', 'PackListTypeDescription']);
                    }
                    if (allowedId !== null && allowedId === nodeId) {
                        return getHierarchyRecordString(record, ['allowedPackListTypeDesc', 'AllowedPackListTypeDesc', 'allowedPackListTypeDescription', 'AllowedPackListTypeDescription']);
                    }
                    return '';
                };

                const descendantVisited = new Set<number>();
                const descendantQueue: number[] = [rootId];
                while (descendantQueue.length > 0) {
                    const currentId = descendantQueue.shift()!;
                    if (descendantVisited.has(currentId)) continue;
                    descendantVisited.add(currentId);

                    const records = await fetchPacklistHierarchyRelations(currentId, 'descendants');
                    records.forEach((record: any) => {
                        const edge = buildHierarchyEdge(record, currentId, 'descendants');
                        if (!edge) return;

                        addNode(edge.parentId, '', resolveDescriptionForNode(record, edge.parentId));
                        addNode(edge.childId, '', resolveDescriptionForNode(record, edge.childId));
                        addEdge(edge.parentId, edge.childId);

                        if (!descendantVisited.has(edge.childId)) {
                            descendantQueue.push(edge.childId);
                        }
                    });
                }

                const ancestorVisited = new Set<number>();
                const ancestorQueue: number[] = [rootId];
                while (ancestorQueue.length > 0) {
                    const currentId = ancestorQueue.shift()!;
                    if (ancestorVisited.has(currentId)) continue;
                    ancestorVisited.add(currentId);

                    const records = await fetchPacklistHierarchyRelations(currentId, 'ancestors');
                    records.forEach((record: any) => {
                        const edge = buildHierarchyEdge(record, currentId, 'ancestors');
                        if (!edge) return;

                        addNode(edge.parentId, '', resolveDescriptionForNode(record, edge.parentId));
                        addNode(edge.childId, '', resolveDescriptionForNode(record, edge.childId));
                        addEdge(edge.parentId, edge.childId);

                        if (!ancestorVisited.has(edge.parentId)) {
                            ancestorQueue.push(edge.parentId);
                        }
                    });
                }

                const nodesObj: { [key: number]: PacklistTypeHierarchyNode } = {};
                nodes.forEach((value, key) => {
                    nodesObj[key] = value;
                });

                const childrenObj: { [key: number]: number[] } = {};
                childrenByParent.forEach((value, key) => {
                    childrenObj[key] = Array.from(value.values());
                });

                const parentObj: { [key: number]: number[] } = {};
                parentsByChild.forEach((value, key) => {
                    parentObj[key] = Array.from(value.values());
                });

                const expandedObj: { [key: string]: boolean } = {
                    [`ancestors_${rootId}`]: true,
                    [`descendants_${rootId}`]: true,
                };

                Object.keys(nodesObj).forEach(idKey => {
                    const id = Number(idKey);
                    if ((childrenObj[id] || []).length > 0) {
                        expandedObj[`descendants_${id}`] = true;
                    }
                    if ((parentObj[id] || []).length > 0) {
                        expandedObj[`ancestors_${id}`] = true;
                    }
                });

                setState(prev => ({
                    ...prev,
                    hierarchyLoading: false,
                    hierarchyError: '',
                    hierarchyNodes: nodesObj,
                    hierarchyChildrenByParent: childrenObj,
                    hierarchyParentsByChild: parentObj,
                    hierarchyExpanded: expandedObj,
                }));
            } catch (err: any) {
                setState(prev => ({
                    ...prev,
                    hierarchyLoading: false,
                    hierarchyError: 'Error loading hierarchy: ' + (err?.toString?.() ?? String(err)),
                }));
            }
        })();
    }, [buildHierarchyEdge, fetchPacklistHierarchyRelations, getHierarchyRecordNumber, getHierarchyRecordString]);

    const hierarchyNodeLabel = useCallback((id: number) => {
        const node = state.hierarchyNodes[id];
        if (!node) return `PackList Type (${id})`;
        const description = node.description || 'PackList Type';
        return `${description} (${id})`;
    }, [state.hierarchyNodes]);

    const renderHierarchyTree = useCallback((
        nodeId: number,
        mode: 'ancestors' | 'descendants',
        visitedPath: number[],
    ): JSX.Element | null => {
        const linkedIds = mode === 'ancestors'
            ? (state.hierarchyParentsByChild[nodeId] || [])
            : (state.hierarchyChildrenByParent[nodeId] || []);

        if (linkedIds.length === 0) return null;

        const sortedLinkedIds = [...linkedIds].sort((a, b) => {
            const aLabel = hierarchyNodeLabel(a).toLowerCase();
            const bLabel = hierarchyNodeLabel(b).toLowerCase();
            return aLabel.localeCompare(bLabel);
        });

        return (
            <ul style={{ margin: '4px 0 0 20px', paddingLeft: '14px' }}>
                {sortedLinkedIds.map(linkedId => {
                    const isCycle = visitedPath.includes(linkedId);
                    const key = `${mode}_${linkedId}`;
                    const childLinks = mode === 'ancestors'
                        ? (state.hierarchyParentsByChild[linkedId] || [])
                        : (state.hierarchyChildrenByParent[linkedId] || []);
                    const hasChildren = childLinks.length > 0;
                    const isExpanded = state.hierarchyExpanded[key] !== false;
                    return (
                        <li key={`${mode}_${nodeId}_${linkedId}`} style={{ marginBottom: '4px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                {hasChildren ? (
                                    <button
                                        type="button"
                                        className="k-button k-button-sm k-rounded-sm"
                                        style={{ minWidth: '22px', height: '22px', padding: 0, lineHeight: 1 }}
                                        onClick={() => {
                                            setState(prev => ({
                                                ...prev,
                                                hierarchyExpanded: {
                                                    ...prev.hierarchyExpanded,
                                                    [key]: prev.hierarchyExpanded[key] === false,
                                                },
                                            }));
                                        }}
                                        title={isExpanded ? 'Collapse node' : 'Expand node'}
                                    >
                                        {isExpanded ? '-' : '+'}
                                    </button>
                                ) : (
                                    <span style={{ display: 'inline-block', width: '22px' }} />
                                )}
                                <span style={{ fontWeight: isCycle ? 700 : 400 }}>
                                    {hierarchyNodeLabel(linkedId)}{isCycle ? ' [circular]' : ''}
                                </span>
                            </div>
                            {!isCycle && isExpanded && renderHierarchyTree(linkedId, mode, [...visitedPath, linkedId])}
                        </li>
                    );
                })}
            </ul>
        );
    }, [hierarchyNodeLabel, state.hierarchyChildrenByParent, state.hierarchyExpanded, state.hierarchyParentsByChild]);

    const expandAllHierarchyNodes = useCallback(() => {
        setState(prev => {
            const expanded: { [key: string]: boolean } = {};
            Object.keys(prev.hierarchyNodes).forEach(idKey => {
                const id = Number(idKey);
                if ((prev.hierarchyChildrenByParent[id] || []).length > 0) {
                    expanded[`descendants_${id}`] = true;
                }
                if ((prev.hierarchyParentsByChild[id] || []).length > 0) {
                    expanded[`ancestors_${id}`] = true;
                }
            });
            return {
                ...prev,
                hierarchyExpanded: expanded,
            };
        });
    }, []);

    const collapseAllHierarchyNodes = useCallback(() => {
        setState(prev => {
            const collapsed: { [key: string]: boolean } = {};
            Object.keys(prev.hierarchyNodes).forEach(idKey => {
                const id = Number(idKey);
                if ((prev.hierarchyChildrenByParent[id] || []).length > 0) {
                    collapsed[`descendants_${id}`] = false;
                }
                if ((prev.hierarchyParentsByChild[id] || []).length > 0) {
                    collapsed[`ancestors_${id}`] = false;
                }
            });
            return {
                ...prev,
                hierarchyExpanded: collapsed,
            };
        });
    }, []);

    const openUniqueGroupsDialog = (packlistType: PacklistTypeRow) => {
        setState(prev => ({
            ...prev,
            uniqueGroupsDlgOpen: true,
            uniqueGroupsPacklistType: packlistType,
            uniqueGroupsDlgLoading: true,
            uniqueGroupsDlgError: '',
            uniqueGroupsDlgFilterText: '',
            uniqueGroupsDropdownLoading: true,
            uniqueGroupsSelected: new Set(),
        }));

        // Load the dropdown data (all available unique groups)
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetUniqueGroupsForDropDown';
        fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'OK') {
                    const dropdownData: LookupListItem[] = (res.results || []).map((item: any) => ({
                        value: item.value || '',
                        display: item.display || `Unique Group ${item.value}`,
                    }));
                    setState(prev => ({ ...prev, uniqueGroupsDropdownData: dropdownData, uniqueGroupsDropdownLoading: false }));
                } else {
                    setState(prev => ({ ...prev, uniqueGroupsDropdownLoading: false, uniqueGroupsDlgError: 'Failed to load unique groups' }));
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, uniqueGroupsDropdownLoading: false, uniqueGroupsDlgError: 'Error loading unique groups: ' + err.toString() }));
            });

        // Load the currently selected unique groups for this packlist type
        const url2 = props.userInf.currProject.apiUrl + '/api/TMI/GetUniqueGroupPackListTypes';
        fetch(url2, {
            method: 'GET',
            headers: {
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'OK') {
                    const selectedIds: Set<string> = new Set((res.results || []).map((item: any) => (typeof item === 'string' ? item : item)?.toString() || ''));
                    setState(prev => ({ ...prev, uniqueGroupsSelected: selectedIds, uniqueGroupsDlgLoading: false }));
                } else {
                    setState(prev => ({ ...prev, uniqueGroupsDlgLoading: false, uniqueGroupsDlgError: 'Failed to load selected unique groups' }));
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, uniqueGroupsDlgLoading: false, uniqueGroupsDlgError: 'Error loading selected unique groups: ' + err.toString() }));
            });
    };

    const closeUniqueGroupsDialog = () => {
        setState(prev => ({
            ...prev,
            uniqueGroupsDlgOpen: false,
            uniqueGroupsPacklistType: null,
            uniqueGroupsDropdownData: [],
            uniqueGroupsDropdownLoading: false,
            uniqueGroupsDlgLoading: false,
            uniqueGroupsDlgError: '',
            uniqueGroupsDlgFilterText: '',
            uniqueGroupsSelected: new Set(),
        }));
    };

    const handleUniqueGroupsFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setState(prev => ({ ...prev, uniqueGroupsDlgFilterText: e.target.value }));
    };

    const toggleUniqueGroupSelection = (uniqueGroupId: string) => {
        setState(prev => {
            const newSelected = new Set(prev.uniqueGroupsSelected);
            if (newSelected.has(uniqueGroupId)) {
                newSelected.delete(uniqueGroupId);
            } else {
                newSelected.add(uniqueGroupId);
            }
            
            // Auto-save on toggle
            if (prev.uniqueGroupsPacklistType) {
                const url = props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdateUniqueGroupPackListType';
                fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + props.userInf.token,
                    },
                    body: JSON.stringify({
                        selected: prev.uniqueGroupsPacklistType.packlistTypeID,
                        checked: Array.from(newSelected),
                    }),
                })
                    .then(resp => resp.json())
                    .then(res => {
                        if (res.callStatus === 'UNAUTH') {
                            const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                            props.updateUserInfo(uInf);
                        } else if (res.callStatus !== 'OK') {
                            setState(s => ({
                                ...s,
                                uniqueGroupsDlgError: 'Error saving: ' + res.callStatusMessage,
                            }));
                        }
                    })
                    .catch(err => {
                        setState(s => ({
                            ...s,
                            uniqueGroupsDlgError: 'Error saving: ' + err.toString(),
                        }));
                    });
            }
            
            return { ...prev, uniqueGroupsSelected: newSelected };
        });
    };

    const openUserRolesDialog = (packlistType: PacklistTypeRow) => {
        setState(prev => ({
            ...prev,
            userRolesDlgOpen: true,
            userRolesPacklistType: packlistType,
            userRolesDlgLoading: true,
            userRolesDlgError: '',
            userRolesDlgFilterText: '',
            userRolesDropdownLoading: true,
            userRolesSelected: new Set(),
        }));

        // Load the dropdown data (all available user roles)
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetUserRolesForDropDown';
        fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'OK') {
                    const dropdownData: LookupListItem[] = (res.results || []).map((item: any) => ({
                        value: item.value || '',
                        display: item.display || `User Role ${item.value}`,
                    }));
                    setState(prev => ({ ...prev, userRolesDropdownData: dropdownData, userRolesDropdownLoading: false }));
                } else {
                    setState(prev => ({ ...prev, userRolesDropdownLoading: false, userRolesDlgError: 'Failed to load user roles' }));
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, userRolesDropdownLoading: false, userRolesDlgError: 'Error loading user roles: ' + err.toString() }));
            });

        // Load the currently selected user roles for this packlist type
        const url2 = props.userInf.currProject.apiUrl + '/api/TMI/GetUserRolePackListTypes?packListTypeID=' + packlistType.packlistTypeID;
        fetch(url2, {
            method: 'GET',
            headers: {
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'OK') {
                    const selectedIds: Set<string> = new Set((res.results || []).map((item: any) => item?.toString() || ''));
                    setState(prev => ({ ...prev, userRolesSelected: selectedIds, userRolesDlgLoading: false }));
                } else {
                    setState(prev => ({ ...prev, userRolesDlgLoading: false, userRolesDlgError: 'Failed to load selected user roles' }));
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, userRolesDlgLoading: false, userRolesDlgError: 'Error loading selected user roles: ' + err.toString() }));
            });
    };

    const closeUserRolesDialog = () => {
        setState(prev => ({
            ...prev,
            userRolesDlgOpen: false,
            userRolesPacklistType: null,
            userRolesDropdownData: [],
            userRolesDropdownLoading: false,
            userRolesDlgLoading: false,
            userRolesDlgError: '',
            userRolesDlgFilterText: '',
            userRolesSelected: new Set(),
        }));
    };

    const handleUserRolesFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setState(prev => ({ ...prev, userRolesDlgFilterText: e.target.value }));
    };

    const toggleUserRoleSelection = (userRoleId: string) => {
        setState(prev => {
            const newSelected = new Set(prev.userRolesSelected);
            if (newSelected.has(userRoleId)) {
                newSelected.delete(userRoleId);
            } else {
                newSelected.add(userRoleId);
            }
            
            // Auto-save on toggle
            if (prev.userRolesPacklistType) {
                const url = props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdateUserRolePackListType';
                fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + props.userInf.token,
                    },
                    body: JSON.stringify({
                        selected: prev.userRolesPacklistType.packlistTypeID,
                        checked: Array.from(newSelected).map(id => parseInt(id, 10)),
                    }),
                })
                    .then(resp => resp.json())
                    .then(res => {
                        if (res.callStatus === 'UNAUTH') {
                            const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                            props.updateUserInfo(uInf);
                        } else if (res.callStatus !== 'OK') {
                            setState(s => ({
                                ...s,
                                userRolesDlgError: 'Error saving: ' + res.callStatusMessage,
                            }));
                        }
                    })
                    .catch(err => {
                        setState(s => ({
                            ...s,
                            userRolesDlgError: 'Error saving: ' + err.toString(),
                        }));
                    });
            }
            
            return { ...prev, userRolesSelected: newSelected };
        });
    };

    const handleMenuItemClick = (action: string) => {
        const item = state.menuDataItem;
        if (!item) return;
        
        // Handle different menu item actions
        // These handlers will be implemented to open appropriate dialogs or navigate
        console.log(`Menu action: ${action} for PacklistType ID: ${item.packlistTypeID}`);
        
        switch (action) {
            case 'editIcon':
                openIconPicker('edit', item);
                break;
            case 'attributes':
                openAttributesDialog(item);
                break;
            case 'clientQueries':
                openClientQueriesDialog(item);
                break;
            case 'milestones':
                openMilestonesDialog(item);
                break;
            case 'status':
                openStatusDialog(item);
                break;
            case 'allowedItemTypes':
                openAllowedItemTypesDialog(item);
                break;
            case 'allowedPacklistTypes':
                openAllowedPacklistTypesDialog(item);
                break;
            case 'showHierarchy':
                openHierarchyDialog(item);
                break;
            case 'uniqueGroups':
                openUniqueGroupsDialog(item);
                break;
            case 'userRoles':
                openUserRolesDialog(item);
                break;
            case 'packlistItemAttributes':
                openPacklistItemAttributesDialog(item);
                break;
        }
        
        closeRowMenu();
    }

    // Menu position calculation effect
    useEffect(() => {
        if (!state.menuAnchor || state.menuPosition !== null) return;
        const el = menuRef.current;
        if (!el) return;
        const timer = setTimeout(() => {
            try {
                const menuRect = el.getBoundingClientRect();
                const snap = state.menuAnchorRect;
                const actualAnchorRect = snap ? snap : (state.menuAnchor ? state.menuAnchor.getBoundingClientRect() as any : { left: 0, right: 0, top: 0, bottom: 0, width: 0, height: 0 });
                const scrollX = window.scrollX || window.pageXOffset || 0;
                const scrollY = window.scrollY || window.pageYOffset || 0;
                const winW = window.innerWidth;
                const winH = window.innerHeight;
                const menuW = Math.round(menuRect.width);
                const menuH = Math.round(menuRect.height);

                let left = Math.round(actualAnchorRect.left + scrollX);
                let top = Math.round(actualAnchorRect.bottom + scrollY);

                if (left + menuW > scrollX + winW) left = Math.round(scrollX + Math.max(8, winW - menuW - 8));
                if (left < scrollX + 8) left = Math.round(scrollX + 8);

                if (top + menuH > scrollY + winH) {
                    const flipped = Math.round(actualAnchorRect.top + scrollY - menuH + 2);
                    if (flipped >= scrollY + 8) top = flipped; else top = Math.round(scrollY + Math.max(8, winH - menuH - 8));
                    left = Math.round(actualAnchorRect.left + scrollX);
                    if (left + menuW > scrollX + winW) left = Math.round(scrollX + Math.max(8, winW - menuW - 8));
                    if (left < scrollX + 8) left = Math.round(scrollX + 8);
                }

                setState(prev => ({ ...prev, menuPosition: { left, top } }));
            } catch (e) {
                // ignore measurement errors
            }
        }, 10);
        return () => clearTimeout(timer);
    }, [state.menuAnchor, state.menuPosition, state.menuAnchorRect]);

    // Menu blur handler setup
    useEffect(() => {
        const handleDocumentClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (menuRef.current && !menuRef.current.contains(target) &&
                state.menuAnchor && !state.menuAnchor.contains(target)) {
                closeRowMenu();
            }
        };
        if (state.menuDataItem) {
            document.addEventListener('click', handleDocumentClick);
            return () => document.removeEventListener('click', handleDocumentClick);
        }
    }, [state.menuDataItem, state.menuAnchor]);

    // Live ref holding the latest render's handlers/derived values. Several handlers referenced by
    // body cell renderers (handleSave, handleCancel, handleEnterEdit, openDeleteConfirm,
    // openRowMenu, openIconPicker, openNewDialog, fetchData) are plain consts (or useCallback with
    // deps that change) recreated every render, so cached renderers must call through this ref
    // instead of closing over them directly - that keeps the cached renderers permanently stable
    // while always invoking the freshest logic (equivalent behavior to before).
    const liveRef = useRef({ handleSave, handleCancel, handleEnterEdit, openDeleteConfirm, openRowMenu, openIconPicker, openNewDialog, fetchData, loading: state.loading });
    liveRef.current = { handleSave, handleCancel, handleEnterEdit, openDeleteConfirm, openRowMenu, openIconPicker, openNewDialog, fetchData, loading: state.loading };

    // Cache body (non-header) cell renderers in a ref so GridColumn `cell` props keep a stable
    // function identity across renders. Previously these were plain consts (or inline arrow
    // functions duplicated per column) recreated on every render and closing over `state`/handlers
    // directly; Kendo's Grid then saw a "new" cell renderer on every keystroke and remounted the
    // <input> DOM node, dropping focus after a single character (the data-edit-cell /
    // keepEditInputFocus calls below were a band-aid for this). They now read live values via
    // SELECTED_FIELD/EDIT_FIELD (embedded on gridData) and `liveRef`/stable `handleRowClick`
    // instead of closing over `state`, so the cached functions never need to change identity.
    const bodyCellRenderersRef = useRef<{ [key: string]: (props: any) => JSX.Element }>({});

    // Generic cache-by-key helper for body cell renderers in the smaller sub-dialog grids below
    // (Client Queries/Attributes/Packlist Item Attributes). `factory` is only invoked the first
    // time a given `key` is requested - the resulting renderer function is cached in
    // bodyCellRenderersRef and reused on every later render, so it keeps a stable identity even
    // though a new `factory` closure is created each render at the call site. Renderers built this
    // way must read live values via `stateRef.current` rather than closing over `state` directly.
    const getOrBuildBodyCellRenderer = useCallback((key: string, factory: () => (props: any) => JSX.Element) => {
        if (!bodyCellRenderersRef.current[key]) {
            bodyCellRenderersRef.current[key] = factory();
        }
        return bodyCellRenderersRef.current[key];
    }, []);

    if (!bodyCellRenderersRef.current.textCell) {
        bodyCellRenderersRef.current.textCell = (props: GridCellProps) => {
            const val = props.dataItem[props.field!] ?? '';
            const isSelected: boolean = props.dataItem[SELECTED_FIELD];
            return (
                <td
                    className={`tmi-dialog-grid-text-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}
                    onClick={(e) => handleRowClick(props.dataItem.packlistTypeID, e)}
                    style={{ cursor: 'pointer' }}
                    title={String(val)}
                >
                    {val}
                </td>
            );
        };
    }
    const textCell = bodyCellRenderersRef.current.textCell;

    if (!bodyCellRenderersRef.current.dateCell) {
        bodyCellRenderersRef.current.dateCell = (props: GridCellProps) => {
            const val = props.dataItem[props.field!];
            const display = val ? new Date(val).toLocaleString() : '';
            const isSelected: boolean = props.dataItem[SELECTED_FIELD];
            return (
                <td
                    className={`tmi-dialog-grid-text-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}
                    onClick={(e) => handleRowClick(props.dataItem.packlistTypeID, e)}
                    style={{ cursor: 'pointer' }}
                    title={display}
                >
                    {display}
                </td>
            );
        };
    }
    const dateCell = bodyCellRenderersRef.current.dateCell;

    if (!bodyCellRenderersRef.current.BooleanCell) {
        bodyCellRenderersRef.current.BooleanCell = (props: GridCellProps) => {
            const val = props.dataItem[props.field!];
            const isSelected: boolean = props.dataItem[SELECTED_FIELD];
            const isEditing: boolean = props.dataItem[EDIT_FIELD];
            return (
                <td
                    className={`tmi-dialog-grid-center-cell ${isSelected && !isEditing ? 'tmi-dialog-grid-cell-selected' : ''}`}
                    onClick={(e) => handleRowClick(props.dataItem.packlistTypeID, e)}
                    style={{ cursor: 'pointer' }}
                >
                    {val ? '✓' : ''}
                </td>
            );
        };
    }
    const BooleanCell = bodyCellRenderersRef.current.BooleanCell;

    if (!bodyCellRenderersRef.current.idCell) {
        bodyCellRenderersRef.current.idCell = (props: GridCellProps) => {
            const val = props.dataItem[props.field!] ?? '';
            const isSelected: boolean = props.dataItem[SELECTED_FIELD];
            const isEditing: boolean = props.dataItem[EDIT_FIELD];
            return (
                <td
                    className={`tmi-dialog-grid-center-cell ${isSelected && !isEditing ? 'tmi-dialog-grid-cell-selected' : ''}`}
                    onClick={(e) => handleRowClick(props.dataItem.packlistTypeID, e)}
                    style={{ cursor: 'pointer' }}
                    title={String(val)}
                >
                    {val}
                </td>
            );
        };
    }
    const idCell = bodyCellRenderersRef.current.idCell;

    // Shared editable text cell used by the Code/Description/Display Format columns. Previously
    // each column defined its own near-identical inline cell function; they are consolidated here
    // since they only differ by field name (props.field!).
    if (!bodyCellRenderersRef.current.editableTextCell) {
        bodyCellRenderersRef.current.editableTextCell = (props: GridCellProps) => {
            const field = props.field!;
            const isEditing: boolean = props.dataItem[EDIT_FIELD];
            if (isEditing) {
                return (
                    <td
                        className="tmi-dialog-grid-text-cell"
                        onClick={(e) => handleRowClick(props.dataItem.packlistTypeID, e)}
                    >
                        <input
                            data-edit-cell={`${props.dataItem.packlistTypeID}::${field}`}
                            type="text"
                            className="k-input k-input-md"
                            value={props.dataItem[field] ?? ''}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                            onChange={(e) => {
                                const input = e.currentTarget;
                                const selectionStart = input.selectionStart;
                                const selectionEnd = input.selectionEnd;
                                const updated = { ...props.dataItem, [field]: e.target.value };
                                setState(prev => ({
                                    ...prev,
                                    data: prev.data.map(item =>
                                        item.packlistTypeID === updated.packlistTypeID ? updated : item
                                    ),
                                }));
                                keepEditInputFocus(updated.packlistTypeID, field, selectionStart, selectionEnd);
                            }}
                            style={{ width: '100%' }}
                        />
                    </td>
                );
            }
            return textCell(props);
        };
    }
    const editableTextCell = bodyCellRenderersRef.current.editableTextCell;

    // Editable checkbox cells for the Multi Scan / Restrict Items columns. Kept as two separate
    // renderers (rather than consolidated like editableTextCell) because their onChange handlers
    // differ in the underlying code (allowMultiScan stores the raw Checkbox onChange argument;
    // restrictToAllowedItemTypesOnly stores `e.value`) - preserved here unchanged to avoid altering
    // existing behavior.
    if (!bodyCellRenderersRef.current.allowMultiScanCell) {
        bodyCellRenderersRef.current.allowMultiScanCell = (props: GridCellProps) => {
            const isEditing: boolean = props.dataItem[EDIT_FIELD];
            if (isEditing) {
                return (
                    <td
                        className="tmi-dialog-grid-center-cell"
                        onClick={(e) => handleRowClick(props.dataItem.packlistTypeID, e)}
                        style={{ cursor: 'pointer' }}
                    >
                        <Checkbox
                            checked={props.dataItem.allowMultiScan}
                            onChange={(checked) => {
                                const updated = { ...props.dataItem, allowMultiScan: checked };
                                setState(prev => ({
                                    ...prev,
                                    data: prev.data.map(item =>
                                        item.packlistTypeID === updated.packlistTypeID ? updated : item
                                    ),
                                }));
                            }}
                        />
                    </td>
                );
            }
            return BooleanCell(props);
        };
    }
    const allowMultiScanCell = bodyCellRenderersRef.current.allowMultiScanCell;

    if (!bodyCellRenderersRef.current.restrictToAllowedItemTypesOnlyCell) {
        bodyCellRenderersRef.current.restrictToAllowedItemTypesOnlyCell = (props: GridCellProps) => {
            const isEditing: boolean = props.dataItem[EDIT_FIELD];
            if (isEditing) {
                return (
                    <td
                        className="tmi-dialog-grid-center-cell"
                        onClick={(e) => handleRowClick(props.dataItem.packlistTypeID, e)}
                        style={{ cursor: 'pointer' }}
                    >
                        <Checkbox
                            checked={props.dataItem.restrictToAllowedItemTypesOnly}
                            onChange={(e: any) => {
                                const updated = { ...props.dataItem, restrictToAllowedItemTypesOnly: e.value };
                                setState(prev => ({
                                    ...prev,
                                    data: prev.data.map(item =>
                                        item.packlistTypeID === updated.packlistTypeID ? updated : item
                                    ),
                                }));
                            }}
                        />
                    </td>
                );
            }
            return BooleanCell(props);
        };
    }
    const restrictToAllowedItemTypesOnlyCell = bodyCellRenderersRef.current.restrictToAllowedItemTypesOnlyCell;

    if (!bodyCellRenderersRef.current.iconCell) {
        bodyCellRenderersRef.current.iconCell = (props: GridCellProps) => {
            const isEditing: boolean = props.dataItem[EDIT_FIELD];
            const isSelected: boolean = props.dataItem[SELECTED_FIELD];
            const iconEntity = toIconEntity(props.dataItem.iconAllGood);
            const parsedCode = parseIconCode(props.dataItem.iconAllGood);
            return (
                <td
                    className={`tmi-dialog-grid-center-cell ${isSelected && !isEditing ? 'tmi-dialog-grid-cell-selected' : ''}`}
                    onClick={(e) => handleRowClick(props.dataItem.packlistTypeID, e)}
                    style={{ cursor: 'pointer' }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                        <span
                            className={isWebComponentsPrivateUseCode(parsedCode) ? 'k-icon k-font-icon' : ''}
                            title={parsedCode ? `Code ${parsedCode}` : 'No icon selected'}
                            dangerouslySetInnerHTML={{ __html: iconEntity || '&#9633;' }}
                            style={{
                                fontSize: '18px',
                                lineHeight: 1,
                                fontFamily: isWebComponentsPrivateUseCode(parsedCode) ? 'WebComponentsIcons' : 'inherit',
                            }}
                        />
                        {isEditing && (
                            <button
                                className="k-button k-button-sm k-rounded-md"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    liveRef.current.openIconPicker('edit', props.dataItem);
                                }}
                                title="Select icon"
                            >
                                Pick
                            </button>
                        )}
                    </div>
                </td>
            );
        };
    }
    const iconCell = bodyCellRenderersRef.current.iconCell;

    if (!bodyCellRenderersRef.current.HamburgerHeaderCell) {
        bodyCellRenderersRef.current.HamburgerHeaderCell = () => {
            return (
                <th className="tmi-header-action-cell tmi-command-column">
                    <button className="k-button k-button-sm k-rounded-md tmi-header-action-button" onClick={() => liveRef.current.fetchData()} disabled={liveRef.current.loading} title="Refresh">
                        <span className="k-icon k-font-icon k-i-reload" />
                    </button>
                </th>
            );
        };
    }
    const HamburgerHeaderCell = bodyCellRenderersRef.current.HamburgerHeaderCell;

    if (!bodyCellRenderersRef.current.HamburgerCell) {
        bodyCellRenderersRef.current.HamburgerCell = (props: GridCellProps) => {
            const { dataItem } = props;
            const inEdit: boolean = dataItem[EDIT_FIELD];
            return (
                <td
                    style={{ textAlign: 'center', padding: '2px' }}
                >
                    {inEdit ? (
                        <button
                            className="k-button k-button-sm k-rounded-md k-button-solid-success"
                            title="Save"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleRowClick(dataItem.packlistTypeID, e);
                                liveRef.current.handleSave(dataItem);
                            }}
                        >
                            <span className="k-icon k-font-icon k-i-check" />
                        </button>
                    ) : (
                        <button
                            className="k-button k-button-sm k-rounded-md"
                            title="Actions"
                            onClick={e => {
                                e.stopPropagation();
                                liveRef.current.openRowMenu(dataItem, e.currentTarget);
                            }}
                        >
                            <span className="k-icon k-font-icon k-i-more-vertical" />
                        </button>
                    )}
                </td>
            );
        };
    }
    const HamburgerCell = bodyCellRenderersRef.current.HamburgerCell;

    if (!bodyCellRenderersRef.current.CommandCell) {
        bodyCellRenderersRef.current.CommandCell = (props: GridCellProps) => {
            const { dataItem } = props;
            const inEdit: boolean = dataItem[EDIT_FIELD];
            return (
                <td className="packlists-command-cell tmi-command-column tmi-command-cell">
                    {inEdit ? (
                        <>
                            <button className="k-button k-button-sm k-rounded-md k-button-solid-secondary tmi-dialog-action-button tmi-dialog-action-button-first" onClick={(e) => { handleRowClick(dataItem.packlistTypeID, e); liveRef.current.handleCancel(); }} title="Cancel">
                                <span className="k-icon k-font-icon k-i-cancel" />
                            </button>
                            <button className="k-button k-button-sm k-rounded-md k-button-solid-error tmi-dialog-action-button tmi-dialog-action-button-next" onClick={(e) => { handleRowClick(dataItem.packlistTypeID, e); liveRef.current.openDeleteConfirm(dataItem); }} title="Delete">
                                <span className="k-icon k-font-icon k-i-trash" />
                            </button>
                        </>
                    ) : (
                        <>
                            <button className="k-button k-button-sm k-rounded-md k-button-solid-base tmi-dialog-action-button tmi-dialog-action-button-first" onClick={(e) => { handleRowClick(dataItem.packlistTypeID, e); liveRef.current.handleEnterEdit(dataItem); }} title="Edit">
                                <span className="k-icon k-font-icon k-i-edit" />
                            </button>
                            <button className="k-button k-button-sm k-rounded-md k-button-solid-error tmi-dialog-action-button tmi-dialog-action-button-next" onClick={(e) => { handleRowClick(dataItem.packlistTypeID, e); liveRef.current.openDeleteConfirm(dataItem); }} title="Delete">
                                <span className="k-icon k-font-icon k-i-trash" />
                            </button>
                        </>
                    )}
                </td>
            );
        };
    }
    const CommandCell = bodyCellRenderersRef.current.CommandCell;

    if (!bodyCellRenderersRef.current.CommandHeaderCell) {
        bodyCellRenderersRef.current.CommandHeaderCell = () => {
            return (
                <th className="tmi-command-header-cell tmi-command-column">
                    <button
                        className="k-button k-button-sm k-rounded-md k-button-solid-primary"
                        title="New PacklistType"
                        onClick={() => liveRef.current.openNewDialog()}
                    >
                        <span className="k-icon k-font-icon k-i-plus" />
                    </button>
                </th>
            );
        };
    }
    const CommandHeaderCell = bodyCellRenderersRef.current.CommandHeaderCell;

    // Cache header cell renderers in ref
    const headerCellRenderersRef = useRef<{ [key: string]: (props: any) => JSX.Element }>({});

    // Build header cell renderer with sort and filter
    const buildHeaderCellRenderer = useCallback((field: string, title: string) => {
        const cacheKey = `${HEADER_RENDERER_VERSION}_text_${field}`;
        if (!headerCellRenderersRef.current[cacheKey]) {
            headerCellRenderersRef.current[cacheKey] = (props: any) => {
                const val = stateRef.current.filterTexts?.[field] ?? '';
                const focused = stateRef.current.headerFocusField === field;
                const sortDir = (stateRef.current.sort && stateRef.current.sort.length > 0 && stateRef.current.sort[0].field === field) ? (stateRef.current.sort[0].dir as string) : undefined;
                return (
                    <th className="tmi-header-filter">
                        <div className="k-link" onClick={() => toggleSort(field)} aria-hidden="true" />
                        <div className={`tmi-floating-wrapper ${val ? 'has-value' : ''} ${focused ? 'focused' : ''}`}>
                            <label onClick={() => toggleSort(field)} style={{ whiteSpace: 'normal', wordWrap: 'break-word', overflow: 'visible', cursor: 'pointer', userSelect: 'none', pointerEvents: 'auto', position: 'relative', zIndex: 10, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span>{title}</span>
                                <span className="tmi-sort-glyph-inline">{sortDir === 'asc' ? '▲' : sortDir === 'desc' ? '▼' : '↕'}</span>
                            </label>
                            <input
                                type="text"
                                className="k-input k-input-sm"
                                value={val}
                                onChange={(e) => handleHeaderFilterChange(field, e)}
                                onFocus={() => handleHeaderFocus(field)}
                                onBlur={() => handleHeaderBlur()}
                                onMouseDown={(e) => { e.stopPropagation(); }}
                            />
                        </div>
                    </th>
                );
            };
        }
        return headerCellRenderersRef.current[cacheKey];
    }, [toggleSort, handleHeaderFocus, handleHeaderBlur, handleHeaderFilterChange]);

    // Build boolean header cell renderer
    const buildBooleanHeaderCellRenderer = useCallback((field: string, title: string) => {
        const cacheKey = `${HEADER_RENDERER_VERSION}_bool_${field}`;
        if (!headerCellRenderersRef.current[cacheKey]) {
            headerCellRenderersRef.current[cacheKey] = (props: any) => {
                const val = stateRef.current.filterTexts?.[field] ?? '';
                const focused = stateRef.current.headerFocusField === field;
                return (
                    <th className="tmi-header-filter">
                        <div className="k-link" aria-hidden="true" />
                        <div className={`tmi-select-floating ${val ? 'has-value' : ''} ${focused ? 'focused' : ''}`}>
                            <label onClick={() => toggleSort(field)} style={{ whiteSpace: 'normal', wordWrap: 'break-word', overflow: 'visible', cursor: 'pointer', userSelect: 'none', pointerEvents: 'auto', position: 'relative', zIndex: 10, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span>{title}</span>
                                <span className="tmi-sort-glyph-inline">↕</span>
                            </label>
                            <select
                                className="k-input k-input-sm"
                                value={val}
                                onChange={(e) => handleHeaderFilterChange(field, e as unknown as React.ChangeEvent<HTMLInputElement>)}
                                onFocus={() => handleHeaderFocus(field)}
                                onBlur={() => handleHeaderBlur()}
                                onMouseDown={(e) => { e.stopPropagation(); }}
                            >
                                <option value="">All</option>
                                <option value="true">Yes</option>
                                <option value="false">No</option>
                            </select>
                        </div>
                    </th>
                );
            };
        }
        return headerCellRenderersRef.current[cacheKey];
    }, [handleHeaderFilterChange, handleHeaderFocus, handleHeaderBlur, toggleSort]);

    // Build header cell renderer for simple (non-filterable) columns
    const createSimpleHeaderCell = useCallback((field: string, title: string) => {
        const cacheKey = `${HEADER_RENDERER_VERSION}_simple_${field}`;
        if (!headerCellRenderersRef.current[cacheKey]) {
            headerCellRenderersRef.current[cacheKey] = (props: any) => {
                const sortDir = (stateRef.current.sort && stateRef.current.sort.length > 0 && stateRef.current.sort[0].field === field) ? (stateRef.current.sort[0].dir as string) : undefined;
                const onSimpleHeaderMouseDown = (e: React.MouseEvent<HTMLElement>) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleSort(field);
                };
                const blockSimpleHeaderClick = (e: React.MouseEvent<HTMLElement>) => {
                    e.preventDefault();
                    e.stopPropagation();
                };
                return (
                    <th className="tmi-header-filter">
                        <div className="k-link" onMouseDown={onSimpleHeaderMouseDown} onClick={blockSimpleHeaderClick} aria-hidden="true" />
                        <div 
                            onMouseDown={onSimpleHeaderMouseDown}
                            onClick={blockSimpleHeaderClick}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '4px',
                                padding: '4px 6px',
                                boxSizing: 'border-box',
                                fontSize: '12px',
                                lineHeight: '1.3',
                                whiteSpace: 'normal',
                                wordWrap: 'break-word',
                                flex: 1,
                                cursor: 'pointer',
                                userSelect: 'none'
                            }}>
                            <span onMouseDown={onSimpleHeaderMouseDown} onClick={blockSimpleHeaderClick}>{title}</span>
                            <span className="tmi-sort-glyph-inline" onMouseDown={onSimpleHeaderMouseDown} onClick={blockSimpleHeaderClick}>{sortDir === 'asc' ? '▲' : sortDir === 'desc' ? '▼' : '↕'}</span>
                        </div>
                    </th>
                );
            };
        }
        return headerCellRenderersRef.current[cacheKey];
    }, [toggleSort]);

    // Destructure state properties for convenience
    const {
        data,
        loading,
        errorMsg,
        sort,
        filterTexts,
        editItemId,
    } = state;

    // Filter data based on per-column filters in filterTexts
    const filters = filterTexts || {};
    const filteredData = Object.keys(filters).length === 0 || Object.values(filters).every(v => !v)
        ? data
        : data.filter(item => {
            return Object.entries(filters).every(([f, q]) => {
                if (!q) return true;
                const val = (item as any)[f];
                const ql = q.trim().toLowerCase();
                // For boolean fields, check exact match
                if (ql === 'true' || ql === 'false') {
                    return String(val ?? '').toLowerCase() === ql;
                }
                return String(val ?? '').toLowerCase().includes(ql);
            });
        });

    const sortedData = orderBy(filteredData, sort);

    const visibleData = state.editItemId !== null && state.editViewOrderIds
        ? (() => {
            const byId = new Map(filteredData.map(item => [item.packlistTypeID, item] as const));
            const frozen = state.editViewOrderIds
                .map(id => byId.get(id))
                .filter((item): item is PacklistTypeRow => Boolean(item));

            const frozenIds = new Set(frozen.map(item => item.packlistTypeID));
            const appended = filteredData.filter(item => !frozenIds.has(item.packlistTypeID));
            return [...frozen, ...appended];
        })()
        : sortedData;

    // Extend data items with edit/selected flags so cached body cell renderers (see
    // bodyCellRenderersRef above) can read them off props.dataItem instead of closing over state.
    const gridData = visibleData.map(item => ({
        ...item,
        [EDIT_FIELD]: editItemId === item.packlistTypeID,
        [SELECTED_FIELD]: state.selectedIds.has(item.packlistTypeID),
    }));

    // Store current state in ref so cached renderers always access latest state (like class component this.state)
    stateRef.current = state;

    return (
        <div className="packlists-container">
            {errorMsg && <div className="packlists-error">{errorMsg}</div>}
            <Grid
                className="tmi-grid PacklistTypes-grid"
                style={{ flex: 1, minHeight: 0 }}
                data={gridData}
                dataItemKey="packlistTypeID"
                resizable
                editField={EDIT_FIELD}
                onItemChange={handleItemChange}
            >
                <GridColumn headerCell={HamburgerHeaderCell} cell={HamburgerCell} width={40} filterable={false} sortable={false} headerClassName="wrap-header" />
                <GridColumn field="packlistTypeID" title="ID" width={60} sortable={true} cell={idCell} headerCell={createSimpleHeaderCell('packlistTypeID', 'ID')} headerClassName="wrap-header" resizable={true} />
                <GridColumn 
                    field="packlistTypeCode" 
                    title="Code" 
                    headerCell={buildHeaderCellRenderer('packlistTypeCode', 'Code')}
                    headerClassName="wrap-header"
                    resizable={true}
                    cell={editableTextCell}
                />
                <GridColumn 
                    field="description" 
                    title="Description" 
                    headerCell={buildHeaderCellRenderer('description', 'Description')}
                    headerClassName="wrap-header"
                    resizable={true}
                    cell={editableTextCell}
                />
                <GridColumn 
                    field="allowMultiScan" 
                    title="Multi Scan" 
                    width={100}
                    headerCell={buildBooleanHeaderCellRenderer('allowMultiScan', 'Multi Scan')}
                    headerClassName="wrap-header"
                    resizable={true}
                    cell={allowMultiScanCell}
                />
                <GridColumn
                    field="iconAllGood"
                    title="Icon"
                    width={130}
                    headerCell={createSimpleHeaderCell('iconAllGood', 'Icon')}
                    headerClassName="wrap-header"
                    resizable={true}
                    cell={iconCell}
                />
                <GridColumn 
                    field="displayFormat" 
                    title="Display Format" 
                    headerCell={buildHeaderCellRenderer('displayFormat', 'Display Format')}
                    headerClassName="wrap-header"
                    resizable={true}
                    cell={editableTextCell}
                />
                <GridColumn 
                    field="restrictToAllowedItemTypesOnly" 
                    title="Restrict Items To Allowed Item Types Only" 
                    width={100}
                    headerCell={buildBooleanHeaderCellRenderer('restrictToAllowedItemTypesOnly', 'Restrict Items To Allowed Item Types Only')}
                    headerClassName="wrap-header"
                    resizable={true}
                    cell={restrictToAllowedItemTypesOnlyCell}
                />
                <GridColumn field="lastChangedBy" title="Changed By" headerCell={createSimpleHeaderCell('lastChangedBy', 'Changed By')} cell={textCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field="lastChangedOn" title="Changed On" headerCell={createSimpleHeaderCell('lastChangedOn', 'Changed On')} cell={dateCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn headerCell={CommandHeaderCell} cell={CommandCell} width={80} locked={true} resizable={false} headerClassName="wrap-header tmi-command-column" />
            </Grid>

            {/* Delete Confirmation Dialog */}
            {state.deleteConfirmItem && (
                <Dialog title="Confirm Delete" onClose={closeDeleteConfirm}>
                    <p>Delete Packlist Type '{state.deleteConfirmItem.packlistTypeCode}'?</p>
                    {state.deleteErrorMsg && <div className="packlists-error">{state.deleteErrorMsg}</div>}
                    <DialogActionsBar>
                        <button className="k-button k-button-solid-error" onClick={confirmDelete} disabled={state.deleteInProgress}>
                            Delete
                        </button>
                        <button className="k-button k-button-solid-base" onClick={closeDeleteConfirm} disabled={state.deleteInProgress}>
                            Cancel
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {/* New PacklistType Dialog */}
            {state.newDialogOpen && (
                <Dialog title="New Packlist Type" onClose={closeNewDialog}>
                    <div style={{ padding: '16px' }}>
                        <div style={{ marginBottom: '16px' }}>
                            <label>Code *</label>
                            <input
                                autoFocus={!state.iconPickerOpen}
                                type="text"
                                className="k-input k-input-md"
                                value={state.newDialogCode}
                                onChange={(e) => setState(prev => ({ ...prev, newDialogCode: e.target.value }))}
                                style={{ width: '100%', marginTop: '4px' }}
                            />
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <label>Description</label>
                            <input
                                type="text"
                                className="k-input k-input-md"
                                value={state.newDialogDesc}
                                onChange={(e) => setState(prev => ({ ...prev, newDialogDesc: e.target.value }))}
                                style={{ width: '100%', marginTop: '4px' }}
                            />
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <label>Display Format</label>
                            <input
                                type="text"
                                className="k-input k-input-md"
                                value={state.newDialogDisplayFormat}
                                onChange={(e) => setState(prev => ({ ...prev, newDialogDisplayFormat: e.target.value }))}
                                style={{ width: '100%', marginTop: '4px' }}
                            />
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <Checkbox
                                checked={state.newDialogAllowMultiScan}
                                onChange={(e: any) => setState(prev => ({ ...prev, newDialogAllowMultiScan: e.value }))}
                                label="Allow Multi Scan"
                            />
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <label>Icon</label>
                            <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span
                                    className={isWebComponentsPrivateUseCode(state.newDialogIconAllGood) ? 'k-icon k-font-icon' : ''}
                                    dangerouslySetInnerHTML={{ __html: toIconEntity(state.newDialogIconAllGood) || '&#9633;' }}
                                    style={{
                                        fontSize: '20px',
                                        width: '26px',
                                        textAlign: 'center',
                                        fontFamily: isWebComponentsPrivateUseCode(state.newDialogIconAllGood) ? 'WebComponentsIcons' : 'inherit',
                                        lineHeight: 1,
                                    }}
                                />
                                <button className="k-button k-button-sm k-rounded-md" type="button" onClick={() => openIconPicker('new')}>
                                    Choose Icon
                                </button>
                                <span style={{ fontSize: '12px', color: '#555' }}>{state.newDialogIconAllGood ?? 'None selected'}</span>
                            </div>
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <Checkbox
                                checked={state.newDialogRestrictToAllowedItemTypes}
                                onChange={(e: any) => setState(prev => ({ ...prev, newDialogRestrictToAllowedItemTypes: e.value }))}
                                label="Restrict to Allowed Item Types"
                            />
                        </div>
                        {state.newDialogError && <div className="packlists-error">{state.newDialogError}</div>}
                    </div>
                    <DialogActionsBar>
                        <button className="k-button k-button-solid-primary" onClick={submitNew} disabled={state.newDialogSaving}>
                            Create
                        </button>
                        <button className="k-button k-button-solid-base" onClick={closeNewDialog} disabled={state.newDialogSaving}>
                            Cancel
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}

            <IconPickerDialog
                open={state.iconPickerOpen}
                title="Select Packlist Type Icon"
                value={toIconEntity(state.iconPickerSelectedCode)}
                onApply={applyIconPickerSelection}
                onClose={closeIconPicker}
            />

            {/* Client Queries Dialog */}
            {state.clientQueriesDlgOpen && (
                <Dialog
                    title={`Client Queries for ${state.clientQueriesPacklistType?.packlistTypeCode}`}
                    onClose={closeClientQueriesDialog}
                    width={1100}
                    height={600}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0', height: '460px' }}>
                        <div className="tmi-dialog-list-container tmi-grid-container">
                            {state.clientQueriesDlgError && (
                                <div className="tmi-dialog-list-error">
                                    {state.clientQueriesDlgError}
                                </div>
                            )}
                            {state.clientQueriesDlgLoading ? (
                                <div className="tmi-dialog-list-loading">
                                    <span className="k-icon k-font-icon k-i-loading" />
                                    Loading client queries...
                                </div>
                            ) : (
                                <>
                                    {(() => {
                                        // Apply filtering and sorting
                                        const filters = state.clientQueriesFilterTexts;
                                        const filteredData = state.clientQueriesData.filter(item => {
                                            return Object.entries(filters).every(([f, q]) => {
                                                if (!q) return true;
                                                const val = (item as any)[f];
                                                const ql = q.trim().toLowerCase();
                                                return String(val ?? '').toLowerCase().includes(ql);
                                            });
                                        });
                                        const sortedData = orderBy(filteredData, state.clientQueriesSort);

                                        return (
                                            <Grid
                                                data={sortedData}
                                                className="ClientQueries-grid"
                                                onRowClick={(e) => setState(prev => ({ ...prev, clientQueriesSelectedId: e.dataItem.PackListTypeClientQueryID }))}
                                            >
                                                <GridColumn
                                                    field="display"
                                                    title="Query Name"
                                                    width={300}
                                                    resizable={true}
                                                    headerCell={buildClientQueriesHeaderCellRenderer('display', 'Query Name')}
                                                    cell={(props) => {
                                                        const isSelected = state.clientQueriesSelectedId === props.dataItem.PackListTypeClientQueryID;
                                                        return (
                                                            <td className={`tmi-dialog-grid-text-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}>
                                                                {props.dataItem.inEdit ? (
                                                                    <select
                                                                        className="k-input k-input-sm"
                                                                        value={props.dataItem.ClientQueryID ?? ''}
                                                                        onChange={(e) => {
                                                                            const selectedQueryId = e.target.value ? parseInt(e.target.value, 10) : null;
                                                                            const selectedQuery = selectedQueryId ? state.clientQueriesDropdownData.find(q => parseInt(q.value, 10) === selectedQueryId) : null;
                                                                            setState(prev => ({
                                                                                ...prev,
                                                                                clientQueriesData: prev.clientQueriesData.map(q =>
                                                                                    q.PackListTypeClientQueryID === props.dataItem.PackListTypeClientQueryID
                                                                                        ? { ...q, ClientQueryID: selectedQueryId, display: selectedQuery?.display || `Query ${selectedQueryId}` }
                                                                                        : q
                                                                                ),
                                                                            }))
                                                                        }}
                                                                        style={{ width: '100%' }}
                                                                    >
                                                                        <option value="">-- Select a Query --</option>
                                                                        {state.clientQueriesDropdownData
                                                                            .filter(q => {
                                                                                const queryId = parseInt(q.value, 10);
                                                                                const isCurrentQuery = props.dataItem.ClientQueryID === queryId;
                                                                                const isAlreadyAdded = state.clientQueriesData.some(row => row.ClientQueryID === queryId && row.PackListTypeClientQueryID !== props.dataItem.PackListTypeClientQueryID);
                                                                                return isCurrentQuery || !isAlreadyAdded;
                                                                            })
                                                                            .map((query) => (
                                                                                <option key={query.value} value={query.value}>
                                                                                    {query.display}
                                                                                </option>
                                                                            ))}
                                                                    </select>
                                                                ) : (
                                                                    props.dataItem.display
                                                                )}
                                                            </td>
                                                        );
                                                    }}
                                                />
                                                <GridColumn
                                                    field="DisplayText"
                                                    title="Display Text"
                                                    resizable={true}
                                                    headerCell={buildClientQueriesHeaderCellRenderer('DisplayText', 'Display Text')}
                                                    cell={(props) => {
                                                        const isSelected = state.clientQueriesSelectedId === props.dataItem.PackListTypeClientQueryID;
                                                        return (
                                                            <td className={`tmi-dialog-grid-text-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}>
                                                                {props.dataItem.inEdit ? (
                                                                    <input
                                                                        key={`displaytext-${props.dataItem.PackListTypeClientQueryID}-edit`}
                                                                        type="text"
                                                                        className="k-input k-input-sm"
                                                                        defaultValue={props.dataItem.DisplayText}
                                                                        data-field="DisplayText"
                                                                        style={{ width: '100%' }}
                                                                    />
                                                                ) : (
                                                                    props.dataItem.DisplayText
                                                                )}
                                                            </td>
                                                        );
                                                    }}
                                                />
                                                <GridColumn
                                                    field="SortOrder"
                                                    title="Sort Order"
                                                    width={100}
                                                    resizable={true}
                                                    headerCell={buildClientQueriesHeaderCellRenderer('SortOrder', 'Sort Order')}
                                                    cell={getOrBuildBodyCellRenderer('clientQueriesSortOrderCell', () => (props: GridCellProps) => {
                                                        const isSelected = stateRef.current.clientQueriesSelectedId === props.dataItem.PackListTypeClientQueryID;
                                                        return (
                                                            <td className={`tmi-dialog-grid-center-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}>
                                                                {props.dataItem.inEdit ? (
                                                                    <input
                                                                        type="number"
                                                                        className="k-input k-input-sm"
                                                                        value={props.dataItem.SortOrder}
                                                                        onChange={(e) =>
                                                                            setState(prev => ({
                                                                                ...prev,
                                                                                clientQueriesData: prev.clientQueriesData.map(q =>
                                                                                    q.PackListTypeClientQueryID === props.dataItem.PackListTypeClientQueryID
                                                                                        ? { ...q, SortOrder: parseInt(e.target.value, 10) || 1 }
                                                                                        : q
                                                                                ),
                                                                            }))
                                                                        }
                                                                        style={{ width: '100%' }}
                                                                    />
                                                                ) : (
                                                                    props.dataItem.SortOrder
                                                                )}
                                                            </td>
                                                        );
                                                    })}
                                                />
                                                <GridColumn
                                                    title="Actions"
                                                    width={120}
                                                    resizable={true}
                                                    headerCell={() => (
                                                        <th className="tmi-header-filter">
                                                            <div style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                padding: '4px 6px',
                                                                boxSizing: 'border-box',
                                                                fontSize: '12px',
                                                                lineHeight: '1.3',
                                                                whiteSpace: 'normal',
                                                                wordWrap: 'break-word',
                                                                minHeight: '66px'
                                                            }}>
                                                                Actions
                                                            </div>
                                                        </th>
                                                    )}
                                                    cell={(props) => (
                                                        <td style={{ padding: '4px', textAlign: 'center' }}>
                                                            {props.dataItem.inEdit ? (
                                                                <>
                                                                    <button
                                                                        className="k-button k-button-sm k-rounded-md k-button-solid-secondary tmi-dialog-action-button tmi-dialog-action-button-first"
                                                                        onClick={() => setState(prev => ({
                                                                            ...prev,
                                                                            clientQueriesEditingId: null,
                                                                            clientQueriesData: props.dataItem.PackListTypeClientQueryID === 0
                                                                                ? prev.clientQueriesData.filter(q => q.PackListTypeClientQueryID !== 0)
                                                                                : prev.clientQueriesData.map(q =>
                                                                                    q.PackListTypeClientQueryID === props.dataItem.PackListTypeClientQueryID
                                                                                        ? { ...q, inEdit: false }
                                                                                        : q
                                                                                )
                                                                        }))}
                                                                        title="Cancel"
                                                                        disabled={state.clientQueriesDlgLoading}
                                                                    >
                                                                        <span className="k-icon k-font-icon k-i-cancel" />
                                                                    </button>
                                                                    <button
                                                                        className="k-button k-button-sm k-rounded-md k-button-solid-success tmi-dialog-action-button tmi-dialog-action-button-next"
                                                                        onClick={() => saveClientQuery(props.dataItem.PackListTypeClientQueryID)}
                                                                        title="Save"
                                                                        disabled={state.clientQueriesDlgLoading}
                                                                    >
                                                                        <span className="k-icon k-font-icon k-i-check" />
                                                                    </button>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <button
                                                                        className="k-button k-button-sm k-rounded-md k-button-solid-base tmi-dialog-action-button tmi-dialog-action-button-first"
                                                                        onClick={() => setState(prev => ({
                                                                            ...prev,
                                                                            clientQueriesEditingId: props.dataItem.PackListTypeClientQueryID,
                                                                            clientQueriesData: prev.clientQueriesData.map(q =>
                                                                                q.PackListTypeClientQueryID === props.dataItem.PackListTypeClientQueryID
                                                                                    ? { ...q, inEdit: true }
                                                                                    : q
                                                                            )
                                                                        }))}
                                                                        title="Edit"
                                                                        disabled={state.clientQueriesDlgLoading}
                                                                    >
                                                                        <span className="k-icon k-font-icon k-i-edit" />
                                                                    </button>
                                                                    <button
                                                                        className="k-button k-button-sm k-rounded-md k-button-solid-error tmi-dialog-action-button tmi-dialog-action-button-next"
                                                                        onClick={() => openClientQueryDeleteConfirm(props.dataItem.PackListTypeClientQueryID)}
                                                                        title="Delete"
                                                                        disabled={state.clientQueriesDlgLoading}
                                                                    >
                                                                        <span className="k-icon k-font-icon k-i-trash" />
                                                                    </button>
                                                                </>
                                                            )}
                                                        </td>
                                                    )}
                                                />
                                            </Grid>
                                        );
                                    })()}
                                    <button
                                        disabled={state.clientQueriesData.some(q => q.inEdit) || state.clientQueriesDlgLoading}
                                        className="k-button k-button-sm k-rounded-md k-button-solid-primary tmi-dialog-add-button"
                                        onClick={() => {
                                            const newQuery = {
                                                PackListTypeClientQueryID: 0,
                                                ClientQueryID: null,
                                                display: '',
                                                DisplayText: '',
                                                SortOrder: 1,
                                                isSelected: true,
                                                inEdit: true,
                                            };
                                            setState(prev => ({ ...prev, clientQueriesData: [...prev.clientQueriesData, newQuery] }));
                                        }}
                                        title="Add Query"
                                        style={{ marginTop: '8px' }}
                                    >
                                        <span className="k-icon k-font-icon k-i-plus" /> Add Query
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                    <DialogActionsBar>
                        <button className="k-button k-button-md k-rounded-md" onClick={closeClientQueriesDialog} disabled={state.clientQueriesDlgLoading}>Close</button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.clientQueriesDeleteConfirmId !== null && (
                <Dialog
                    title="Confirm Delete"
                    onClose={closeClientQueryDeleteConfirm}
                    width={420}
                >
                    <p style={{ padding: '8px 0' }}>
                        Are you sure you want to delete this client query association?
                    </p>
                    {state.clientQueriesDeleteError && (
                        <div style={{ color: '#d32f2f', marginBottom: '12px', padding: '8px', backgroundColor: '#ffebee', borderRadius: '4px' }}>
                            {state.clientQueriesDeleteError}
                        </div>
                    )}
                    <DialogActionsBar>
                        <button 
                            className="k-button k-button-md k-rounded-md k-button-solid-error" 
                            onClick={confirmClientQueryDelete}
                            disabled={state.clientQueriesDeleteInProgress}
                        >
                            {state.clientQueriesDeleteInProgress ? 'Deleting...' : 'Delete'}
                        </button>
                        <button 
                            className="k-button k-button-md k-rounded-md" 
                            onClick={closeClientQueryDeleteConfirm}
                            disabled={state.clientQueriesDeleteInProgress}
                        >
                            Cancel
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.attributesDlgOpen && (
                <Dialog
                    title={`Attributes for ${state.attributesPacklistType?.packlistTypeCode}`}
                    onClose={closeAttributesDialog}
                    width={1100}
                    height={600}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0', height: '460px' }}>
                        <div className="tmi-dialog-list-container tmi-grid-container">
                            {state.attributesDlgError && (
                                <div className="tmi-dialog-list-error">
                                    {state.attributesDlgError}
                                </div>
                            )}
                            {state.attributesDlgLoading ? (
                                <div className="tmi-dialog-list-loading">
                                    <span className="k-icon k-font-icon k-i-loading" />
                                    Loading attributes...
                                </div>
                            ) : (
                                <>
                                    {(() => {
                                        // Apply filtering and sorting
                                        const filters = state.attributesFilterTexts;
                                        const filteredData = state.attributesData.filter(item => {
                                            return Object.entries(filters).every(([f, q]) => {
                                                if (!q) return true;
                                                const val = (item as any)[f];
                                                const ql = q.trim().toLowerCase();
                                                return String(val ?? '').toLowerCase().includes(ql);
                                            });
                                        });
                                        const sortedData = orderBy(filteredData, state.attributesSort);

                                        return (
                                            <Grid
                                                data={sortedData}
                                                className="Attributes-grid"
                                                style={{ height: '430px' }}
                                                onRowClick={(e) => setState(prev => ({ ...prev, attributesSelectedId: e.dataItem.PackListTypeAttributeID }))}
                                            >
                                                <GridColumn
                                                    field="display"
                                                    title="Attribute"
                                                    width={220}
                                                    resizable={true}
                                                    headerCell={buildAttributesHeaderCellRenderer('display', 'Attribute')}
                                                    cell={(props) => {
                                                        const isSelected = state.attributesSelectedId === props.dataItem.PackListTypeAttributeID;
                                                        return (
                                                            <td className={`tmi-dialog-grid-text-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}>
                                                                {props.dataItem.inEdit ? (
                                                                    <select
                                                                        className="k-input k-input-sm"
                                                                        value={props.dataItem.PackListAttributeID ?? ''}
                                                                        onChange={(e) => {
                                                                            const selectedAttributeId = e.target.value ? parseInt(e.target.value, 10) : null;
                                                                            const selectedAttribute = selectedAttributeId ? state.attributesDropdownData.find(a => parseInt(a.value, 10) === selectedAttributeId) : null;
                                                                            setState(prev => ({
                                                                                ...prev,
                                                                                attributesData: prev.attributesData.map(a =>
                                                                                    a.PackListTypeAttributeID === props.dataItem.PackListTypeAttributeID
                                                                                        ? { ...a, PackListAttributeID: selectedAttributeId, display: selectedAttribute?.display || '' }
                                                                                        : a
                                                                                ),
                                                                            }))
                                                                        }}
                                                                        style={{ width: '100%' }}
                                                                    >
                                                                        <option value="">-- Select an Attribute --</option>
                                                                        {state.attributesDropdownData
                                                                            .filter(a => {
                                                                                const attributeId = parseInt(a.value, 10);
                                                                                const isCurrentAttribute = props.dataItem.PackListAttributeID === attributeId;
                                                                                const isAlreadyAdded = state.attributesData.some(row => row.PackListAttributeID === attributeId && row.PackListTypeAttributeID !== props.dataItem.PackListTypeAttributeID);
                                                                                return isCurrentAttribute || !isAlreadyAdded;
                                                                            })
                                                                            .map((a) => (
                                                                                <option key={a.value} value={a.value}>
                                                                                    {a.display}
                                                                                </option>
                                                                            ))}
                                                                    </select>
                                                                ) : (
                                                                    props.dataItem.display
                                                                )}
                                                            </td>
                                                        );
                                                    }}
                                                />
                                                <GridColumn
                                                    field="Description"
                                                    title="Description"
                                                    resizable={true}
                                                    headerCell={buildAttributesHeaderCellRenderer('Description', 'Description')}
                                                    cell={(props) => {
                                                        const isSelected = state.attributesSelectedId === props.dataItem.PackListTypeAttributeID;
                                                        return (
                                                            <td className={`tmi-dialog-grid-text-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}>
                                                                {props.dataItem.inEdit ? (
                                                                    <input
                                                                        key={`desc-${props.dataItem.PackListTypeAttributeID}-edit`}
                                                                        type="text"
                                                                        className="k-input k-input-sm"
                                                                        defaultValue={props.dataItem.Description || ''}
                                                                        data-field="Description"
                                                                        style={{ width: '100%' }}
                                                                    />
                                                                ) : (
                                                                    props.dataItem.Description || ''
                                                                )}
                                                            </td>
                                                        );
                                                    }}
                                                />
                                                <GridColumn
                                                    field="GroupDesc"
                                                    title="Group"
                                                    width={140}
                                                    resizable={true}
                                                    headerCell={buildAttributesHeaderCellRenderer('GroupDesc', 'Group')}
                                                    cell={(props) => {
                                                        const isSelected = state.attributesSelectedId === props.dataItem.PackListTypeAttributeID;
                                                        return (
                                                            <td className={`tmi-dialog-grid-text-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}>
                                                                {props.dataItem.inEdit ? (
                                                                    <input
                                                                        key={`groupdesc-${props.dataItem.PackListTypeAttributeID}-edit`}
                                                                        type="text"
                                                                        className="k-input k-input-sm"
                                                                        defaultValue={props.dataItem.GroupDesc || ''}
                                                                        data-field="GroupDesc"
                                                                        style={{ width: '100%' }}
                                                                    />
                                                                ) : (
                                                                    props.dataItem.GroupDesc
                                                                )}
                                                            </td>
                                                        );
                                                    }}
                                                />
                                                <GridColumn
                                                    field="GroupOrder"
                                                    title="Group Order"
                                                    width={90}
                                                    resizable={true}
                                                    headerCell={buildAttributesHeaderCellRenderer('GroupOrder', 'Group Order')}
                                                    cell={getOrBuildBodyCellRenderer('attributesGroupOrderCell', () => (props: GridCellProps) => {
                                                        const isSelected = stateRef.current.attributesSelectedId === props.dataItem.PackListTypeAttributeID;
                                                        return (
                                                            <td className={`tmi-dialog-grid-center-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}>
                                                                {props.dataItem.inEdit ? (
                                                                    <input
                                                                        type="number"
                                                                        className="k-input k-input-sm"
                                                                        value={props.dataItem.GroupOrder ?? ''}
                                                                        onChange={(e) =>
                                                                            setState(prev => ({
                                                                                ...prev,
                                                                                attributesData: prev.attributesData.map(a =>
                                                                                    a.PackListTypeAttributeID === props.dataItem.PackListTypeAttributeID
                                                                                        ? { ...a, GroupOrder: e.target.value ? parseInt(e.target.value, 10) : null }
                                                                                        : a
                                                                                ),
                                                                            }))
                                                                        }
                                                                        style={{ width: '100%' }}
                                                                    />
                                                                ) : (
                                                                    props.dataItem.GroupOrder ?? ''
                                                                )}
                                                            </td>
                                                        );
                                                    })}
                                                />
                                                <GridColumn
                                                    field="SortOrder"
                                                    title="Sort Order"
                                                    width={80}
                                                    resizable={true}
                                                    headerCell={buildAttributesHeaderCellRenderer('SortOrder', 'Sort Order')}
                                                    cell={getOrBuildBodyCellRenderer('attributesSortOrderCell', () => (props: GridCellProps) => {
                                                        const isSelected = stateRef.current.attributesSelectedId === props.dataItem.PackListTypeAttributeID;
                                                        return (
                                                            <td className={`tmi-dialog-grid-center-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}>
                                                                {props.dataItem.inEdit ? (
                                                                    <input
                                                                        type="number"
                                                                        className="k-input k-input-sm"
                                                                        value={props.dataItem.SortOrder ?? ''}
                                                                        onChange={(e) =>
                                                                            setState(prev => ({
                                                                                ...prev,
                                                                                attributesData: prev.attributesData.map(a =>
                                                                                    a.PackListTypeAttributeID === props.dataItem.PackListTypeAttributeID
                                                                                        ? { ...a, SortOrder: e.target.value ? parseInt(e.target.value, 10) : 1 }
                                                                                        : a
                                                                                ),
                                                                            }))
                                                                        }
                                                                        style={{ width: '100%' }}
                                                                    />
                                                                ) : (
                                                                    props.dataItem.SortOrder || ''
                                                                )}
                                                            </td>
                                                        );
                                                    })}
                                                />
                                                <GridColumn
                                                    field="TabName"
                                                    title="Tab Name"
                                                    width={120}
                                                    resizable={true}
                                                    headerCell={buildAttributesHeaderCellRenderer('TabName', 'Tab Name')}
                                                    cell={(props) => {
                                                        const isSelected = state.attributesSelectedId === props.dataItem.PackListTypeAttributeID;
                                                        return (
                                                            <td className={`tmi-dialog-grid-text-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}>
                                                                {props.dataItem.inEdit ? (
                                                                    <input
                                                                        key={`tabname-${props.dataItem.PackListTypeAttributeID}-edit`}
                                                                        type="text"
                                                                        className="k-input k-input-sm"
                                                                        defaultValue={props.dataItem.TabName || ''}
                                                                        data-field="TabName"
                                                                        style={{ width: '100%' }}
                                                                    />
                                                                ) : (
                                                                    props.dataItem.TabName || ''
                                                                )}
                                                            </td>
                                                        );
                                                    }}
                                                />
                                                <GridColumn
                                                    field="MetaDataID"
                                                    title="MetaData"
                                                    width={120}
                                                    resizable={true}
                                                    headerCell={buildAttributesHeaderCellRenderer('MetaDataID', 'MetaData')}
                                                    cell={(props) => {
                                                        const isSelected = state.attributesSelectedId === props.dataItem.PackListTypeAttributeID;
                                                        return (
                                                            <td className={`tmi-dialog-grid-text-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}>
                                                                {props.dataItem.inEdit ? (
                                                                    <select
                                                                        className="k-input k-input-sm"
                                                                        value={props.dataItem.MetaDataID ?? ''}
                                                                        onChange={(e) =>
                                                                            setState(prev => ({
                                                                                ...prev,
                                                                                attributesData: prev.attributesData.map(a =>
                                                                                    a.PackListTypeAttributeID === props.dataItem.PackListTypeAttributeID
                                                                                        ? { ...a, MetaDataID: e.target.value ? parseInt(e.target.value, 10) : null }
                                                                                        : a
                                                                                ),
                                                                            }))
                                                                        }
                                                                        style={{ width: '100%' }}
                                                                    >
                                                                        <option value="">-- None --</option>
                                                                        {state.attributesMetadataDropdownData.map((m) => (
                                                                            <option key={m.value} value={m.value}>
                                                                                {m.display}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                ) : (
                                                                    props.dataItem.MetaDataID ? state.attributesMetadataDropdownData.find(m => m.value === props.dataItem.MetaDataID?.toString())?.display || '-' : '-'
                                                                )}
                                                            </td>
                                                        );
                                                    }}
                                                />
                                                <GridColumn
                                                    title=""
                                                    width={40}
                                                    sortable={false}
                                                    filterable={false}
                                                    cell={(props) => (
                                                        <td style={{ padding: '4px', textAlign: 'center' }}>
                                                            <button
                                                                className="k-button k-button-sm k-rounded-md k-button-solid-secondary"
                                                                style={{ display: 'inline-flex', alignItems: 'center', margin: 0, padding: '6px 8px' }}
                                                                onClick={() => {
                                                                    openAttributeEditorDialog(props.dataItem);
                                                                }}
                                                                disabled={state.attributesDlgLoading || props.dataItem.inEdit}
                                                                title="Edit User Roles for this Attribute"
                                                            >
                                                                <span className="k-icon k-font-icon k-i-menu" />
                                                            </button>
                                                        </td>
                                                    )}
                                                />
                                                <GridColumn
                                                    title="Actions"
                                                    width={80}
                                                    cell={(props) => (
                                                        <td style={{ padding: '4px', textAlign: 'center' }}>
                                                            {props.dataItem.inEdit ? (
                                                                <>
                                                                    <button
                                                                        className="k-button k-button-sm k-rounded-md k-button-solid-secondary tmi-dialog-action-button tmi-dialog-action-button-first"
                                                                        onClick={() => setState(prev => ({
                                                                            ...prev,
                                                                            attributesEditingId: null,
                                                                            attributesData: props.dataItem.PackListTypeAttributeID === 0
                                                                                ? prev.attributesData.filter(a => a.PackListTypeAttributeID !== 0)
                                                                                : prev.attributesData.map(a =>
                                                                                    a.PackListTypeAttributeID === props.dataItem.PackListTypeAttributeID
                                                                                        ? { ...a, inEdit: false }
                                                                                        : a
                                                                                )
                                                                        }))}
                                                                        disabled={state.attributesDlgLoading}
                                                                        title="Cancel"
                                                                    >
                                                                        <span className="k-icon k-font-icon k-i-cancel" />
                                                                    </button>
                                                                    <button
                                                                        className="k-button k-button-sm k-rounded-md k-button-solid-success tmi-dialog-action-button tmi-dialog-action-button-next"
                                                                        onClick={() => saveAttributeEdit(props.dataItem.PackListTypeAttributeID)}
                                                                        disabled={state.attributesDlgLoading}
                                                                        title="Save"
                                                                    >
                                                                        <span className="k-icon k-font-icon k-i-check" />
                                                                    </button>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <button
                                                                        className="k-button k-button-sm k-rounded-md k-button-solid-base tmi-dialog-action-button tmi-dialog-action-button-first"
                                                                        onClick={() => setState(prev => ({
                                                                            ...prev,
                                                                            attributesEditingId: props.dataItem.PackListTypeAttributeID,
                                                                            attributesData: prev.attributesData.map(a =>
                                                                                a.PackListTypeAttributeID === props.dataItem.PackListTypeAttributeID
                                                                                    ? { ...a, inEdit: true }
                                                                                    : a
                                                                            )
                                                                        }))}
                                                                        disabled={state.attributesDlgLoading}
                                                                        title="Edit"
                                                                    >
                                                                        <span className="k-icon k-font-icon k-i-edit" />
                                                                    </button>
                                                                    <button
                                                                        className="k-button k-button-sm k-rounded-md k-button-solid-error tmi-dialog-action-button tmi-dialog-action-button-next"
                                                                        onClick={() => deleteAttribute(props.dataItem.PackListTypeAttributeID)}
                                                                        disabled={state.attributesDlgLoading}
                                                                        title="Delete"
                                                                    >
                                                                        <span className="k-icon k-font-icon k-i-trash" />
                                                                    </button>
                                                                </>
                                                            )}
                                                        </td>
                                                    )}
                                                />
                                            </Grid>
                                        );
                                    })()}
                                    <button
                                        disabled={state.attributesData.some(a => a.inEdit) || state.attributesDlgLoading}
                                        className="k-button k-button-sm k-rounded-md k-button-solid-primary tmi-dialog-add-button"
                                        onClick={() => {
                                            const newAttribute = {
                                                PackListTypeAttributeID: 0,
                                                PackListTypeID: state.attributesPacklistType!.packlistTypeID,
                                                PackListAttributeID: null,
                                                display: '',
                                                Description: '',
                                                GroupDesc: '',
                                                GroupOrder: 0,
                                                SortOrder: 1,
                                                TabName: '',
                                                MetaDataID: null,
                                                LastChangedBy: '',
                                                LastChangedOn: null,
                                                isSelected: true,
                                                inEdit: true,
                                            };
                                            setState(prev => ({ ...prev, attributesData: [...prev.attributesData, newAttribute] }));
                                        }}
                                        title="Add Attribute"
                                        style={{ marginTop: '8px' }}
                                    >
                                        <span className="k-icon k-font-icon k-i-plus" /> Add Attribute
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                    <DialogActionsBar>
                        <button className="k-button k-button-md k-rounded-md" onClick={closeAttributesDialog} disabled={state.attributesDlgLoading}>Close</button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.attributesDeleteConfirmId !== null && (
                <Dialog
                    title="Confirm Delete"
                    onClose={closeAttributeDeleteConfirm}
                    width={420}
                >
                    <p style={{ padding: '8px 0' }}>
                        Are you sure you want to delete this attribute association?
                    </p>
                    {state.attributesDeleteError && (
                        <div style={{ color: '#d32f2f', marginBottom: '12px', padding: '8px', backgroundColor: '#ffebee', borderRadius: '4px' }}>
                            {state.attributesDeleteError}
                        </div>
                    )}
                    <DialogActionsBar>
                        <button 
                            className="k-button k-button-md k-rounded-md k-button-solid-error" 
                            onClick={confirmAttributeDelete}
                            disabled={state.attributesDeleteInProgress}
                        >
                            {state.attributesDeleteInProgress ? 'Deleting...' : 'Delete'}
                        </button>
                        <button 
                            className="k-button k-button-md k-rounded-md" 
                            onClick={closeAttributeDeleteConfirm}
                            disabled={state.attributesDeleteInProgress}
                        >
                            Cancel
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.attributeEditorDlgOpen && state.attributeEditorAttribute && (
                <Dialog
                    title={`Edit ${state.attributeEditorAttribute.display} - User Roles`}
                    onClose={closeAttributeEditorDialog}
                    width={700}
                    height={550}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0', height: '410px' }}>
                        {state.attributeEditorUserRolesDlgError && (
                            <div style={{ color: '#d32f2f', marginBottom: '8px', padding: '8px', backgroundColor: '#ffebee', borderRadius: '4px', fontSize: '12px' }}>
                                {state.attributeEditorUserRolesDlgError}
                            </div>
                        )}
                        {state.attributeEditorUserRolesDlgLoading ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                <span className="k-icon k-font-icon k-i-loading" />
                                <span style={{ marginLeft: '8px' }}>Loading...</span>
                            </div>
                        ) : (
                            <>
                                <div style={{ flex: 1, overflow: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
                                    {state.attributeEditorUserRolesData.length === 0 ? (
                                        <div style={{ padding: '12px', textAlign: 'center', color: '#666', fontSize: '12px' }}>
                                            No user roles assigned
                                        </div>
                                    ) : (
                                        <Grid data={state.attributeEditorUserRolesData} className="tmi-editor-grid">
                                            <GridColumn
                                                field="display"
                                                title="User Role"
                                                cell={(cellProps) => {
                                                    const isSelected = false;
                                                    return (
                                                        <td className={`tmi-dialog-grid-text-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}>
                                                            {cellProps.dataItem.inEdit ? (
                                                                <select
                                                                    className="k-input k-input-sm"
                                                                    value={cellProps.dataItem.UserRoleID ?? ''}
                                                                    onChange={(e) => {
                                                                        const selectedRoleId = e.target.value ? parseInt(e.target.value, 10) : null;
                                                                        const selectedRole = state.attributeEditorUserRolesDropdownData.find(r => r.value === e.target.value);
                                                                        setState(prev => ({
                                                                            ...prev,
                                                                            attributeEditorUserRolesData: prev.attributeEditorUserRolesData.map(item =>
                                                                                item.UserRolePackListAttributeID === cellProps.dataItem.UserRolePackListAttributeID
                                                                                    ? { ...item, UserRoleID: selectedRoleId, display: selectedRole?.display || '' }
                                                                                    : item
                                                                            ),
                                                                        }));
                                                                    }}
                                                                    style={{ width: '100%' }}
                                                                >
                                                                    <option value="">-- Select a User Role --</option>
                                                                    {state.attributeEditorUserRolesDropdownData.map(role => (
                                                                        <option key={role.value} value={role.value}>
                                                                            {role.display}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            ) : (
                                                                cellProps.dataItem.display
                                                            )}
                                                        </td>
                                                    );
                                                }}
                                            />
                                            <GridColumn
                                                title="Editable"
                                                width={80}
                                                cell={(cellProps) => (
                                                    <td style={{ padding: '6px', textAlign: 'center' }}>
                                                        {cellProps.dataItem.inEdit ? (
                                                            <input
                                                                type="checkbox"
                                                                checked={cellProps.dataItem.Editable}
                                                                onChange={(e) => {
                                                                    setState(prev => ({
                                                                        ...prev,
                                                                        attributeEditorUserRolesData: prev.attributeEditorUserRolesData.map(item =>
                                                                            item.UserRolePackListAttributeID === cellProps.dataItem.UserRolePackListAttributeID
                                                                                ? { ...item, Editable: e.target.checked }
                                                                                : item
                                                                        ),
                                                                    }));
                                                                }}
                                                            />
                                                        ) : (
                                                            <input
                                                                type="checkbox"
                                                                checked={cellProps.dataItem.Editable}
                                                                disabled={true}
                                                            />
                                                        )}
                                                    </td>
                                                )}
                                            />
                                            <GridColumn
                                                title="Actions"
                                                width={80}
                                                cell={(cellProps) => (
                                                    <td style={{ padding: '4px', textAlign: 'center' }}>
                                                        {cellProps.dataItem.inEdit ? (
                                                            <>
                                                                <button
                                                                    className="k-button k-button-sm k-rounded-md k-button-solid-secondary tmi-dialog-action-button tmi-dialog-action-button-first"
                                                                    onClick={() => {
                                                                        if (cellProps.dataItem.UserRolePackListAttributeID === 0) {
                                                                            // Cancel adding new row
                                                                            setState(prev => ({
                                                                                ...prev,
                                                                                attributeEditorUserRolesData: prev.attributeEditorUserRolesData.filter(
                                                                                    item => item.UserRolePackListAttributeID !== 0
                                                                                ),
                                                                            }));
                                                                        } else {
                                                                            // Cancel editing
                                                                            setState(prev => ({
                                                                                ...prev,
                                                                                attributeEditorUserRolesData: prev.attributeEditorUserRolesData.map(item =>
                                                                                    item.UserRolePackListAttributeID === cellProps.dataItem.UserRolePackListAttributeID
                                                                                        ? { ...item, inEdit: false }
                                                                                        : item
                                                                                ),
                                                                            }));
                                                                        }
                                                                    }}
                                                                    disabled={state.attributeEditorUserRolesDlgLoading}
                                                                    title="Cancel"
                                                                >
                                                                    <span className="k-icon k-font-icon k-i-cancel" />
                                                                </button>
                                                                <button
                                                                    className="k-button k-button-sm k-rounded-md k-button-solid-success tmi-dialog-action-button tmi-dialog-action-button-next"
                                                                    onClick={() => {
                                                                        // Save this role assignment
                                                                        setState(prev => ({ ...prev, attributeEditorUserRolesDlgLoading: true }));
                                                                        const payload = {
                                                                            UserRolePackListAttributeID: cellProps.dataItem.UserRolePackListAttributeID,
                                                                            PackListAttributeID: cellProps.dataItem.PackListAttributeID,
                                                                            UserRoleID: cellProps.dataItem.UserRoleID,
                                                                            Editable: cellProps.dataItem.Editable,
                                                                        };
                                                                        fetch(`${props.userInf.currProject.apiUrl}/api/TMI/AddOrUpdateUserRolePacklistAttribute`, {
                                                                            method: 'POST',
                                                                            headers: {
                                                                                'Content-Type': 'application/json',
                                                                                'Authorization': 'Bearer ' + props.userInf.token,
                                                                            },
                                                                            body: JSON.stringify(payload),
                                                                        })
                                                                            .then(resp => resp.json())
                                                                            .then(res => {
                                                                                if (res.callStatus === 'OK') {
                                                                                    setState(prev => ({
                                                                                        ...prev,
                                                                                        attributeEditorUserRolesData: prev.attributeEditorUserRolesData.map(item =>
                                                                                            item.UserRolePackListAttributeID === cellProps.dataItem.UserRolePackListAttributeID
                                                                                                ? { ...item, inEdit: false, UserRolePackListAttributeID: res.result || item.UserRolePackListAttributeID }
                                                                                                : item
                                                                                        ),
                                                                                        attributeEditorUserRolesDlgLoading: false,
                                                                                    }));
                                                                                } else {
                                                                                    setState(prev => ({
                                                                                        ...prev,
                                                                                        attributeEditorUserRolesDlgError: 'Error saving: ' + res.callStatusMessage,
                                                                                        attributeEditorUserRolesDlgLoading: false,
                                                                                    }));
                                                                                }
                                                                            })
                                                                            .catch(err => {
                                                                                setState(prev => ({
                                                                                    ...prev,
                                                                                    attributeEditorUserRolesDlgError: 'Error saving: ' + err.toString(),
                                                                                    attributeEditorUserRolesDlgLoading: false,
                                                                                }));
                                                                            });
                                                                    }}
                                                                    disabled={state.attributeEditorUserRolesDlgLoading}
                                                                    title="Save"
                                                                >
                                                                    <span className="k-icon k-font-icon k-i-check" />
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <button
                                                                    className="k-button k-button-sm k-rounded-md k-button-solid-base tmi-dialog-action-button tmi-dialog-action-button-first"
                                                                    onClick={() => {
                                                                        setState(prev => ({
                                                                            ...prev,
                                                                            attributeEditorUserRolesData: prev.attributeEditorUserRolesData.map(item =>
                                                                                item.UserRolePackListAttributeID === cellProps.dataItem.UserRolePackListAttributeID
                                                                                    ? { ...item, inEdit: true }
                                                                                    : item
                                                                            ),
                                                                        }));
                                                                    }}
                                                                    disabled={state.attributeEditorUserRolesDlgLoading}
                                                                    title="Edit"
                                                                >
                                                                    <span className="k-icon k-font-icon k-i-edit" />
                                                                </button>
                                                                <button
                                                                    className="k-button k-button-sm k-rounded-md k-button-solid-error tmi-dialog-action-button tmi-dialog-action-button-next"
                                                                    onClick={() => {
                                                                        if (cellProps.dataItem.UserRolePackListAttributeID === 0) {
                                                                            // Delete unsaved new row
                                                                            setState(prev => ({
                                                                                ...prev,
                                                                                attributeEditorUserRolesData: prev.attributeEditorUserRolesData.filter(
                                                                                    item => item.UserRolePackListAttributeID !== 0
                                                                                ),
                                                                            }));
                                                                        } else {
                                                                            // Show delete confirmation for saved row
                                                                            openAttributeEditorUserRoleDeleteConfirm(cellProps.dataItem.UserRolePackListAttributeID);
                                                                        }
                                                                    }}
                                                                    disabled={state.attributeEditorUserRolesDlgLoading}
                                                                    title="Delete"
                                                                >
                                                                    <span className="k-icon k-font-icon k-i-trash" />
                                                                </button>
                                                            </>
                                                        )}
                                                    </td>
                                                )}
                                            />
                                        </Grid>
                                    )}
                                </div>
                                <button
                                    disabled={state.attributeEditorUserRolesData.some(r => r.inEdit) || state.attributeEditorUserRolesDlgLoading}
                                    className="k-button k-button-sm k-rounded-md k-button-solid-primary tmi-dialog-add-button"
                                    onClick={() => {
                                        const newRole = {
                                            UserRolePackListAttributeID: 0,
                                            PackListAttributeID: state.attributeEditorAttribute!.PackListAttributeID || 0,
                                            UserRoleID: null,
                                            display: '',
                                            Editable: false,
                                            LastChangedBy: '',
                                            LastChangedOn: null,
                                            inEdit: true,
                                        };
                                        setState(prev => ({
                                            ...prev,
                                            attributeEditorUserRolesData: [...prev.attributeEditorUserRolesData, newRole],
                                        }));
                                    }}
                                    title="Add new User Role"
                                    style={{ marginTop: '8px' }}
                                >
                                    <span className="k-icon k-font-icon k-i-plus" /> Add User Role
                                </button>
                            </>
                        )}
                    </div>
                    <DialogActionsBar>
                        <button className="k-button k-button-md k-rounded-md" onClick={closeAttributeEditorDialog} disabled={state.attributeEditorUserRolesDlgLoading}>Close</button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.attributeEditorUserRolesDeleteConfirmId !== null && (
                <Dialog
                    title="Confirm Delete"
                    onClose={closeAttributeEditorUserRoleDeleteConfirm}
                    width={420}
                >
                    <p style={{ padding: '8px 0' }}>
                        Are you sure you want to delete this user role assignment?
                    </p>
                    {state.attributeEditorUserRolesDeleteError && (
                        <div style={{ color: '#d32f2f', marginBottom: '12px', padding: '8px', backgroundColor: '#ffebee', borderRadius: '4px' }}>
                            {state.attributeEditorUserRolesDeleteError}
                        </div>
                    )}
                    <DialogActionsBar>
                        <button 
                            className="k-button k-button-md k-rounded-md k-button-solid-error" 
                            onClick={confirmAttributeEditorUserRoleDelete}
                            disabled={state.attributeEditorUserRolesDeleteInProgress}
                        >
                            {state.attributeEditorUserRolesDeleteInProgress ? 'Deleting...' : 'Delete'}
                        </button>
                        <button 
                            className="k-button k-button-md k-rounded-md" 
                            onClick={closeAttributeEditorUserRoleDeleteConfirm}
                            disabled={state.attributeEditorUserRolesDeleteInProgress}
                        >
                            Cancel
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.packlistItemAttributesDlgOpen && (
                <Dialog
                    title={`Packlist Item Attributes for ${state.packlistItemAttributesPacklistType?.packlistTypeCode}`}
                    onClose={closePacklistItemAttributesDialog}
                    width={1100}
                    height={600}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0', height: '460px' }}>
                        <div className="tmi-dialog-list-container tmi-grid-container">
                            {state.packlistItemAttributesDlgError && (
                                <div className="tmi-dialog-list-error">
                                    {state.packlistItemAttributesDlgError}
                                </div>
                            )}
                            {state.packlistItemAttributesDlgLoading ? (
                                <div className="tmi-dialog-list-loading">
                                    <span className="k-icon k-font-icon k-i-loading" />
                                    Loading item attributes...
                                </div>
                            ) : (
                                <>
                                    {(() => {
                                        // Apply filtering and sorting
                                        const filters = state.packlistItemAttributesFilterTexts;
                                        const filteredData = state.packlistItemAttributesData.filter(item => {
                                            return Object.entries(filters).every(([f, q]) => {
                                                if (!q) return true;
                                                const val = (item as any)[f];
                                                const ql = q.trim().toLowerCase();
                                                return String(val ?? '').toLowerCase().includes(ql);
                                            });
                                        });
                                        const sortedData = orderBy(filteredData, state.packlistItemAttributesSort);

                                        return (
                                            <Grid
                                                data={sortedData}
                                                className="PacklistItemAttributes-grid"
                                                style={{ height: '430px' }}
                                                onRowClick={(e) => setState(prev => ({ ...prev, packlistItemAttributesSelectedId: e.dataItem.PackListTypeItemAttributeID }))}
                                            >
                                                <GridColumn
                                                    field="display"
                                                    title="Attribute"
                                                    width={220}
                                                    resizable={true}
                                                    cell={(props) => {
                                                        const isSelected = state.packlistItemAttributesSelectedId === props.dataItem.PackListTypeItemAttributeID;
                                                        return (
                                                            <td className={`tmi-dialog-grid-text-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}>
                                                                {props.dataItem.inEdit ? (
                                                                    <select
                                                                        className="k-input k-input-sm"
                                                                        value={props.dataItem.AttributeID ?? ''}
                                                                        onChange={(e) => {
                                                                            const selectedAttributeId = e.target.value ? parseInt(e.target.value, 10) : null;
                                                                            const selectedAttribute = selectedAttributeId ? state.packlistItemAttributesDropdownData.find(a => parseInt(a.value, 10) === selectedAttributeId) : null;
                                                                            setState(prev => ({
                                                                                ...prev,
                                                                                packlistItemAttributesData: prev.packlistItemAttributesData.map(a =>
                                                                                    a.PackListTypeItemAttributeID === props.dataItem.PackListTypeItemAttributeID
                                                                                        ? { ...a, AttributeID: selectedAttributeId, display: selectedAttribute?.display || '' }
                                                                                        : a
                                                                                ),
                                                                            }))
                                                                        }}
                                                                        style={{ width: '100%' }}
                                                                    >
                                                                        <option value="">-- Select an Attribute --</option>
                                                                        {state.packlistItemAttributesDropdownData
                                                                            .filter(a => {
                                                                                const attributeId = parseInt(a.value, 10);
                                                                                const isCurrentAttribute = props.dataItem.AttributeID === attributeId;
                                                                                const isAlreadyAdded = state.packlistItemAttributesData.some(row => row.AttributeID === attributeId && row.PackListTypeItemAttributeID !== props.dataItem.PackListTypeItemAttributeID);
                                                                                return isCurrentAttribute || !isAlreadyAdded;
                                                                            })
                                                                            .map((a) => (
                                                                                <option key={a.value} value={a.value}>
                                                                                    {a.display}
                                                                                </option>
                                                                            ))}
                                                                    </select>
                                                                ) : (
                                                                    props.dataItem.display
                                                                )}
                                                            </td>
                                                        );
                                                    }}
                                                />
                                                <GridColumn
                                                    field="Description"
                                                    title="Description"
                                                    resizable={true}
                                                    cell={(props) => {
                                                        const isSelected = state.packlistItemAttributesSelectedId === props.dataItem.PackListTypeItemAttributeID;
                                                        return (
                                                            <td className={`tmi-dialog-grid-text-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}>
                                                                {props.dataItem.inEdit ? (
                                                                    <input
                                                                        type="text"
                                                                        className="k-input k-input-sm"
                                                                        defaultValue={props.dataItem.Description || ''}
                                                                        data-item-id={props.dataItem.PackListTypeItemAttributeID}
                                                                        data-field="PIADescription"
                                                                        style={{ width: '100%' }}
                                                                    />
                                                                ) : (
                                                                    props.dataItem.Description || ''
                                                                )}
                                                            </td>
                                                        );
                                                    }}
                                                />
                                                <GridColumn
                                                    field="GroupDesc"
                                                    title="Group"
                                                    width={140}
                                                    resizable={true}
                                                    cell={(props) => {
                                                        const isSelected = state.packlistItemAttributesSelectedId === props.dataItem.PackListTypeItemAttributeID;
                                                        return (
                                                            <td className={`tmi-dialog-grid-text-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}>
                                                                {props.dataItem.inEdit ? (
                                                                    <input
                                                                        type="text"
                                                                        className="k-input k-input-sm"
                                                                        defaultValue={props.dataItem.GroupDesc || ''}
                                                                        data-item-id={props.dataItem.PackListTypeItemAttributeID}
                                                                        data-field="PIAGroupDesc"
                                                                        style={{ width: '100%' }}
                                                                    />
                                                                ) : (
                                                                    props.dataItem.GroupDesc
                                                                )}
                                                            </td>
                                                        );
                                                    }}
                                                />
                                                <GridColumn
                                                    field="GroupOrder"
                                                    title="Group Order"
                                                    width={90}
                                                    resizable={true}
                                                    cell={getOrBuildBodyCellRenderer('packlistItemAttributesGroupOrderCell', () => (props: GridCellProps) => {
                                                        const isSelected = stateRef.current.packlistItemAttributesSelectedId === props.dataItem.PackListTypeItemAttributeID;
                                                        return (
                                                            <td className={`tmi-dialog-grid-center-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}>
                                                                {props.dataItem.inEdit ? (
                                                                    <input
                                                                        type="number"
                                                                        className="k-input k-input-sm"
                                                                        value={props.dataItem.GroupOrder ?? ''}
                                                                        onChange={(e) =>
                                                                            setState(prev => ({
                                                                                ...prev,
                                                                                packlistItemAttributesData: prev.packlistItemAttributesData.map(a =>
                                                                                    a.PackListTypeItemAttributeID === props.dataItem.PackListTypeItemAttributeID
                                                                                        ? { ...a, GroupOrder: e.target.value ? parseInt(e.target.value, 10) : null }
                                                                                        : a
                                                                                ),
                                                                            }))
                                                                        }
                                                                        style={{ width: '100%' }}
                                                                    />
                                                                ) : (
                                                                    props.dataItem.GroupOrder ?? ''
                                                                )}
                                                            </td>
                                                        );
                                                    })}
                                                />
                                                <GridColumn
                                                    field="SortOrder"
                                                    title="Sort Order"
                                                    width={80}
                                                    resizable={true}
                                                    cell={getOrBuildBodyCellRenderer('packlistItemAttributesSortOrderCell', () => (props: GridCellProps) => {
                                                        const isSelected = stateRef.current.packlistItemAttributesSelectedId === props.dataItem.PackListTypeItemAttributeID;
                                                        return (
                                                            <td className={`tmi-dialog-grid-center-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}>
                                                                {props.dataItem.inEdit ? (
                                                                    <input
                                                                        type="number"
                                                                        className="k-input k-input-sm"
                                                                        value={props.dataItem.SortOrder ?? ''}
                                                                        onChange={(e) =>
                                                                            setState(prev => ({
                                                                                ...prev,
                                                                                packlistItemAttributesData: prev.packlistItemAttributesData.map(a =>
                                                                                    a.PackListTypeItemAttributeID === props.dataItem.PackListTypeItemAttributeID
                                                                                        ? { ...a, SortOrder: e.target.value ? parseInt(e.target.value, 10) : 1 }
                                                                                        : a
                                                                                ),
                                                                            }))
                                                                        }
                                                                        style={{ width: '100%' }}
                                                                    />
                                                                ) : (
                                                                    props.dataItem.SortOrder || ''
                                                                )}
                                                            </td>
                                                        );
                                                    })}
                                                />
                                                <GridColumn
                                                    field="TabName"
                                                    title="Tab Name"
                                                    width={120}
                                                    resizable={true}
                                                    cell={(props) => {
                                                        const isSelected = state.packlistItemAttributesSelectedId === props.dataItem.PackListTypeItemAttributeID;
                                                        return (
                                                            <td className={`tmi-dialog-grid-text-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}>
                                                                {props.dataItem.inEdit ? (
                                                                    <input
                                                                        type="text"
                                                                        className="k-input k-input-sm"
                                                                        defaultValue={props.dataItem.TabName || ''}
                                                                        data-item-id={props.dataItem.PackListTypeItemAttributeID}
                                                                        data-field="PIATabName"
                                                                        style={{ width: '100%' }}
                                                                    />
                                                                ) : (
                                                                    props.dataItem.TabName || ''
                                                                )}
                                                            </td>
                                                        );
                                                    }}
                                                />
                                                <GridColumn
                                                    field="ShowInDetails"
                                                    title="Show in Details"
                                                    width={100}
                                                    resizable={true}
                                                    cell={(props) => {
                                                        const isSelected = state.packlistItemAttributesSelectedId === props.dataItem.PackListTypeItemAttributeID;
                                                        return (
                                                            <td className={`tmi-dialog-grid-center-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}>
                                                                {props.dataItem.inEdit ? (
                                                                    <input
                                                                        type="checkbox"
                                                                        className="k-checkbox k-checkbox-sm"
                                                                        defaultChecked={props.dataItem.ShowInDetails}
                                                                        data-item-id={props.dataItem.PackListTypeItemAttributeID}
                                                                        data-field="PIAShowInDetails"
                                                                        style={{ width: '100%' }}
                                                                    />
                                                                ) : (
                                                                    props.dataItem.ShowInDetails ? '✓' : ''
                                                                )}
                                                            </td>
                                                        );
                                                    }}
                                                />
                                                <GridColumn
                                                    field="MetaDataID"
                                                    title="MetaData"
                                                    width={120}
                                                    resizable={true}
                                                    cell={(props) => {
                                                        const isSelected = state.packlistItemAttributesSelectedId === props.dataItem.PackListTypeItemAttributeID;
                                                        return (
                                                            <td className={`tmi-dialog-grid-text-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}>
                                                                {props.dataItem.inEdit ? (
                                                                    <select
                                                                        className="k-input k-input-sm"
                                                                        value={props.dataItem.MetaDataID ?? ''}
                                                                        onChange={(e) =>
                                                                            setState(prev => ({
                                                                                ...prev,
                                                                                packlistItemAttributesData: prev.packlistItemAttributesData.map(a =>
                                                                                    a.PackListTypeItemAttributeID === props.dataItem.PackListTypeItemAttributeID
                                                                                        ? { ...a, MetaDataID: e.target.value ? parseInt(e.target.value, 10) : null }
                                                                                        : a
                                                                                ),
                                                                            }))
                                                                        }
                                                                        style={{ width: '100%' }}
                                                                    >
                                                                        <option value="">-- None --</option>
                                                                        {state.packlistItemAttributesMetadataDropdownData.map((m) => (
                                                                            <option key={m.value} value={m.value}>
                                                                                {m.display}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                ) : (
                                                                    props.dataItem.MetaDataID ? state.packlistItemAttributesMetadataDropdownData.find(m => m.value === props.dataItem.MetaDataID?.toString())?.display || '-' : '-'
                                                                )}
                                                            </td>
                                                        );
                                                    }}
                                                />
                                                <GridColumn
                                                    title=""
                                                    width={40}
                                                    sortable={false}
                                                    filterable={false}
                                                    cell={(props) => (
                                                        <td style={{ padding: '4px', textAlign: 'center' }}>
                                                            <button
                                                                className="k-button k-button-sm k-rounded-md k-button-solid-secondary"
                                                                style={{ display: 'inline-flex', alignItems: 'center', margin: 0, padding: '6px 8px' }}
                                                                onClick={() => {
                                                                    openPacklistItemAttributesUserRolesDialog(props.dataItem);
                                                                }}
                                                                disabled={state.packlistItemAttributesDlgLoading || props.dataItem.inEdit}
                                                                title="Edit User Roles for this Item Attribute"
                                                            >
                                                                <span className="k-icon k-font-icon k-i-menu" />
                                                            </button>
                                                        </td>
                                                    )}
                                                />
                                                <GridColumn
                                                    title="Actions"
                                                    width={80}
                                                    cell={(props) => (
                                                        <td style={{ padding: '4px', textAlign: 'center' }}>
                                                            {props.dataItem.inEdit ? (
                                                                <>
                                                                    <button
                                                                        className="k-button k-button-sm k-rounded-md k-button-solid-secondary tmi-dialog-action-button tmi-dialog-action-button-first"
                                                                        onClick={() => setState(prev => ({
                                                                            ...prev,
                                                                            packlistItemAttributesEditingId: null,
                                                                            packlistItemAttributesData: props.dataItem.PackListTypeItemAttributeID === 0
                                                                                ? prev.packlistItemAttributesData.filter(a => a.PackListTypeItemAttributeID !== 0)
                                                                                : prev.packlistItemAttributesData.map(a =>
                                                                                    a.PackListTypeItemAttributeID === props.dataItem.PackListTypeItemAttributeID
                                                                                        ? { ...a, inEdit: false }
                                                                                        : a
                                                                                )
                                                                        }))}
                                                                        disabled={state.packlistItemAttributesDlgLoading}
                                                                        title="Cancel"
                                                                    >
                                                                        <span className="k-icon k-font-icon k-i-cancel" />
                                                                    </button>
                                                                    <button
                                                                        className="k-button k-button-sm k-rounded-md k-button-solid-success tmi-dialog-action-button tmi-dialog-action-button-next"
                                                                        onClick={() => savePacklistItemAttributeEdit(props.dataItem.PackListTypeItemAttributeID)}
                                                                        disabled={state.packlistItemAttributesDlgLoading}
                                                                        title="Save"
                                                                    >
                                                                        <span className="k-icon k-font-icon k-i-check" />
                                                                    </button>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <button
                                                                        className="k-button k-button-sm k-rounded-md k-button-solid-base tmi-dialog-action-button tmi-dialog-action-button-first"
                                                                        onClick={() => setState(prev => ({
                                                                            ...prev,
                                                                            packlistItemAttributesEditingId: props.dataItem.PackListTypeItemAttributeID,
                                                                            packlistItemAttributesData: prev.packlistItemAttributesData.map(a =>
                                                                                a.PackListTypeItemAttributeID === props.dataItem.PackListTypeItemAttributeID
                                                                                    ? { ...a, inEdit: true }
                                                                                    : a
                                                                            )
                                                                        }))}
                                                                        title="Edit"
                                                                        disabled={state.packlistItemAttributesDlgLoading}
                                                                    >
                                                                        <span className="k-icon k-font-icon k-i-edit" />
                                                                    </button>
                                                                    <button
                                                                        className="k-button k-button-sm k-rounded-md k-button-solid-error tmi-dialog-action-button tmi-dialog-action-button-next"
                                                                        onClick={() => {
                                                                            if (props.dataItem.PackListTypeItemAttributeID === 0) {
                                                                                // Delete unsaved new row
                                                                                setState(prev => ({
                                                                                    ...prev,
                                                                                    packlistItemAttributesData: prev.packlistItemAttributesData.filter(
                                                                                        item => item.PackListTypeItemAttributeID !== 0
                                                                                    ),
                                                                                }));
                                                                            } else {
                                                                                // Show delete confirmation for saved row
                                                                                openPacklistItemAttributeDeleteConfirm(props.dataItem.PackListTypeItemAttributeID);
                                                                            }
                                                                        }}
                                                                        title="Delete"
                                                                        disabled={state.packlistItemAttributesDlgLoading}
                                                                    >
                                                                        <span className="k-icon k-font-icon k-i-trash" />
                                                                    </button>
                                                                </>
                                                            )}
                                                        </td>
                                                    )}
                                                />
                                            </Grid>
                                        );
                                    })()}
                                    <button
                                        className="k-button k-button-md k-rounded-md k-button-solid-primary"
                                        onClick={() => {
                                            const newAttribute = {
                                                PackListTypeItemAttributeID: 0,
                                                AttributeID: null,
                                                display: '',
                                                Description: '',
                                                GroupDesc: '',
                                                GroupOrder: null,
                                                SortOrder: state.packlistItemAttributesData.length + 1,
                                                TabName: '',
                                                ShowInDetails: false,
                                                MetaDataID: null,
                                                isSelected: true,
                                                inEdit: true,
                                            };
                                            setState(prev => ({ ...prev, packlistItemAttributesData: [...prev.packlistItemAttributesData, newAttribute] }));
                                        }}
                                        title="Add Packlist Item Attribute"
                                        style={{ marginTop: '8px' }}
                                        disabled={state.packlistItemAttributesDlgLoading}
                                    >
                                        <span className="k-icon k-font-icon k-i-plus" /> Add Item Attribute
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                    <DialogActionsBar>
                        <button className="k-button k-button-md k-rounded-md" onClick={closePacklistItemAttributesDialog} disabled={state.packlistItemAttributesDlgLoading}>Close</button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.packlistItemAttributesDeleteConfirmId !== null && (
                <Dialog
                    title="Confirm Delete"
                    onClose={closePacklistItemAttributeDeleteConfirm}
                    width={420}
                >
                    <div style={{ padding: '16px', minHeight: '80px' }}>
                        <p>Are you sure you want to delete this packlist item attribute?</p>
                        {state.packlistItemAttributesDeleteError && (
                            <div className="tmi-dialog-list-error" style={{ marginTop: '12px', marginBottom: '12px' }}>
                                {state.packlistItemAttributesDeleteError}
                            </div>
                        )}
                    </div>
                    <DialogActionsBar>
                        <button
                            className="k-button k-button-md k-rounded-md k-button-solid-error"
                            onClick={confirmPacklistItemAttributeDelete}
                            disabled={state.packlistItemAttributesDeleteInProgress}
                        >
                            {state.packlistItemAttributesDeleteInProgress ? 'Deleting...' : 'Delete'}
                        </button>
                        <button
                            className="k-button k-button-md k-rounded-md"
                            onClick={closePacklistItemAttributeDeleteConfirm}
                            disabled={state.packlistItemAttributesDeleteInProgress}
                        >
                            Cancel
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.packlistItemAttributesUserRolesDlgOpen && (
                <Dialog
                    title={`User Roles for ${state.packlistItemAttributesUserRolesItemAttribute?.display}`}
                    onClose={closePacklistItemAttributesUserRolesDialog}
                    width={800}
                    height={600}
                >
                    <div className="tmi-dialog-list-container" style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0', height: '460px' }}>
                        {state.packlistItemAttributesUserRolesDlgError && (
                            <div className="tmi-dialog-list-error">
                                {state.packlistItemAttributesUserRolesDlgError}
                            </div>
                        )}
                        {state.packlistItemAttributesUserRolesDlgLoading ? (
                            <div className="tmi-dialog-list-loading">
                                <span className="k-icon k-font-icon k-i-loading" />
                                Loading user roles...
                            </div>
                        ) : (
                            <>
                                <Grid
                                    data={state.packlistItemAttributesUserRolesData}
                                    className="tmi-dialog-grid"
                                    style={{ height: '360px' }}
                                >
                                    <GridColumn
                                        field="display"
                                        title="User Role"
                                        width={300}
                                        cell={(cellProps) => (
                                            <td className="tmi-dialog-grid-text-cell">
                                                {cellProps.dataItem.inEdit ? (
                                                    <select
                                                        className="k-input k-input-sm"
                                                        value={cellProps.dataItem.UserRoleID ?? ''}
                                                        onChange={(e) => {
                                                            const selectedRoleId = e.target.value ? parseInt(e.target.value, 10) : null;
                                                            const selectedRole = state.packlistItemAttributesUserRolesDropdownData.find(r => r.value === e.target.value);
                                                            setState(prev => ({
                                                                ...prev,
                                                                packlistItemAttributesUserRolesData: prev.packlistItemAttributesUserRolesData.map(item =>
                                                                    item.UserRolePacklistItemAttributeID === cellProps.dataItem.UserRolePacklistItemAttributeID
                                                                        ? { ...item, UserRoleID: selectedRoleId, display: selectedRole?.display || '' }
                                                                        : item
                                                                ),
                                                            }));
                                                        }}
                                                        style={{ width: '100%' }}
                                                    >
                                                        <option value="">-- Select a User Role --</option>
                                                        {state.packlistItemAttributesUserRolesDropdownData.map(role => (
                                                            <option key={role.value} value={role.value}>
                                                                {role.display}
                                                            </option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    cellProps.dataItem.display
                                                )}
                                            </td>
                                        )}
                                    />
                                    <GridColumn
                                        field="Editable"
                                        title="Editable"
                                        width={100}
                                        cell={(cellProps) => (
                                            <td style={{ padding: '6px', textAlign: 'center' }}>
                                                {cellProps.dataItem.inEdit ? (
                                                    <input
                                                        type="checkbox"
                                                        checked={cellProps.dataItem.Editable}
                                                        onChange={(e) => {
                                                            setState(prev => ({
                                                                ...prev,
                                                                packlistItemAttributesUserRolesData: prev.packlistItemAttributesUserRolesData.map(item =>
                                                                    item.UserRolePacklistItemAttributeID === cellProps.dataItem.UserRolePacklistItemAttributeID
                                                                        ? { ...item, Editable: e.target.checked }
                                                                        : item
                                                                ),
                                                            }));
                                                        }}
                                                    />
                                                ) : (
                                                    <input
                                                        type="checkbox"
                                                        checked={cellProps.dataItem.Editable}
                                                        disabled={true}
                                                    />
                                                )}
                                            </td>
                                        )}
                                    />
                                    <GridColumn
                                        title="Actions"
                                        width={120}
                                        cell={(cellProps) => (
                                            <td style={{ padding: '4px', textAlign: 'center' }}>
                                                {cellProps.dataItem.inEdit ? (
                                                    <>
                                                        <button
                                                            className="k-button k-button-sm k-rounded-md k-button-solid-secondary tmi-dialog-action-button tmi-dialog-action-button-first"
                                                            onClick={() => {
                                                                if (cellProps.dataItem.UserRolePacklistItemAttributeID === 0) {
                                                                    // Delete unsaved new row
                                                                    setState(prev => ({
                                                                        ...prev,
                                                                        packlistItemAttributesUserRolesData: prev.packlistItemAttributesUserRolesData.filter(
                                                                            item => item.UserRolePacklistItemAttributeID !== 0
                                                                        ),
                                                                    }));
                                                                } else {
                                                                    setState(prev => ({
                                                                        ...prev,
                                                                        packlistItemAttributesUserRolesData: prev.packlistItemAttributesUserRolesData.map(item =>
                                                                            item.UserRolePacklistItemAttributeID === cellProps.dataItem.UserRolePacklistItemAttributeID
                                                                                ? { ...item, inEdit: false }
                                                                                : item
                                                                        ),
                                                                    }));
                                                                }
                                                            }}
                                                            disabled={state.packlistItemAttributesUserRolesDlgLoading}
                                                            title="Cancel"
                                                        >
                                                            <span className="k-icon k-font-icon k-i-cancel" />
                                                        </button>
                                                        <button
                                                            className="k-button k-button-sm k-rounded-md k-button-solid-success tmi-dialog-action-button tmi-dialog-action-button-next"
                                                            onClick={() => savePacklistItemAttributesUserRoleEdit(cellProps.dataItem.UserRolePacklistItemAttributeID)}
                                                            disabled={state.packlistItemAttributesUserRolesDlgLoading}
                                                            title="Save"
                                                        >
                                                            <span className="k-icon k-font-icon k-i-check" />
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <button
                                                            className="k-button k-button-sm k-rounded-md k-button-solid-base tmi-dialog-action-button tmi-dialog-action-button-first"
                                                            onClick={() => {
                                                                setState(prev => ({
                                                                    ...prev,
                                                                    packlistItemAttributesUserRolesData: prev.packlistItemAttributesUserRolesData.map(item =>
                                                                        item.UserRolePacklistItemAttributeID === cellProps.dataItem.UserRolePacklistItemAttributeID
                                                                            ? { ...item, inEdit: true }
                                                                            : item
                                                                    ),
                                                                }));
                                                            }}
                                                            disabled={state.packlistItemAttributesUserRolesDlgLoading}
                                                            title="Edit"
                                                        >
                                                            <span className="k-icon k-font-icon k-i-edit" />
                                                        </button>
                                                        <button
                                                            className="k-button k-button-sm k-rounded-md k-button-solid-error tmi-dialog-action-button tmi-dialog-action-button-next"
                                                            onClick={() => {
                                                                if (cellProps.dataItem.UserRolePacklistItemAttributeID === 0) {
                                                                    // Delete unsaved new row
                                                                    setState(prev => ({
                                                                        ...prev,
                                                                        packlistItemAttributesUserRolesData: prev.packlistItemAttributesUserRolesData.filter(
                                                                            item => item.UserRolePacklistItemAttributeID !== 0
                                                                        ),
                                                                    }));
                                                                } else {
                                                                    // Show delete confirmation for saved row
                                                                    openPacklistItemAttributesUserRoleDeleteConfirm(cellProps.dataItem.UserRolePacklistItemAttributeID);
                                                                }
                                                            }}
                                                            disabled={state.packlistItemAttributesUserRolesDlgLoading}
                                                            title="Delete"
                                                        >
                                                            <span className="k-icon k-font-icon k-i-trash" />
                                                        </button>
                                                    </>
                                                )}
                                            </td>
                                        )}
                                    />
                                </Grid>
                                <button
                                    disabled={state.packlistItemAttributesUserRolesData.some(r => r.inEdit) || state.packlistItemAttributesUserRolesDlgLoading}
                                    className="k-button k-button-sm k-rounded-md k-button-solid-primary tmi-dialog-add-button"
                                    onClick={() => {
                                        const newRole = {
                                            UserRolePacklistItemAttributeID: 0,
                                            AttributeID: state.packlistItemAttributesUserRolesItemAttribute?.AttributeID || 0,
                                            UserRoleID: null,
                                            display: '',
                                            Editable: false,
                                            LastChangedBy: '',
                                            LastChangedOn: null,
                                            inEdit: true,
                                        };
                                        setState(prev => ({
                                            ...prev,
                                            packlistItemAttributesUserRolesData: [...prev.packlistItemAttributesUserRolesData, newRole],
                                        }));
                                    }}
                                    title="Add new User Role"
                                    style={{ marginTop: '8px' }}
                                >
                                    <span className="k-icon k-font-icon k-i-plus" /> Add User Role
                                </button>
                            </>
                        )}
                    </div>
                    <DialogActionsBar>
                        <button className="k-button k-button-md k-rounded-md" onClick={closePacklistItemAttributesUserRolesDialog} disabled={state.packlistItemAttributesUserRolesDlgLoading}>Close</button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.packlistItemAttributesUserRolesDeleteConfirmId !== null && (
                <Dialog
                    title="Confirm Delete"
                    onClose={closePacklistItemAttributesUserRoleDeleteConfirm}
                    width={420}
                >
                    <div style={{ padding: '16px', minHeight: '80px' }}>
                        <p>Are you sure you want to delete this user role assignment?</p>
                        {state.packlistItemAttributesUserRolesDeleteError && (
                            <div className="tmi-dialog-list-error" style={{ marginTop: '12px', marginBottom: '12px' }}>
                                {state.packlistItemAttributesUserRolesDeleteError}
                            </div>
                        )}
                    </div>
                    <DialogActionsBar>
                        <button
                            className="k-button k-button-md k-rounded-md k-button-solid-error"
                            onClick={confirmPacklistItemAttributesUserRoleDelete}
                            disabled={state.packlistItemAttributesUserRolesDeleteInProgress}
                        >
                            {state.packlistItemAttributesUserRolesDeleteInProgress ? 'Deleting...' : 'Delete'}
                        </button>
                        <button
                            className="k-button k-button-md k-rounded-md"
                            onClick={closePacklistItemAttributesUserRoleDeleteConfirm}
                            disabled={state.packlistItemAttributesUserRolesDeleteInProgress}
                        >
                            Cancel
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.milestonesDlgOpen && (
                <Dialog
                    title={`Milestones for ${state.milestonesPacklistType?.packlistTypeCode}`}
                    onClose={closeMilestonesDialog}
                    width={500}
                    height={600}
                >
                    <div className="tmi-dialog-list-container" style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0', height: '460px' }}>
                        {state.milestoneDlgError && (
                            <div className="tmi-dialog-list-error">
                                {state.milestoneDlgError}
                            </div>
                        )}
                        {state.milestoneDlgLoading ? (
                            <div className="tmi-dialog-list-loading">
                                <span className="k-icon k-font-icon k-i-loading" />
                                Loading milestones...
                            </div>
                        ) : (
                            <>
                                <input
                                    type="text"
                                    className="k-input tmi-dialog-list-filter"
                                    placeholder="Filter milestones..."
                                    value={state.milestoneDlgFilterText}
                                    onChange={handleMilestonesFilterChange}
                                />
                                <div className="tmi-dialog-list-content" style={{ flex: 1, overflowY: 'auto' }}>
                                    {state.milestonesDropdownData
                                        .filter(milestone => milestone.display.toLowerCase().includes(state.milestoneDlgFilterText.toLowerCase()))
                                        .map((milestone) => (
                                            <div key={milestone.value} className="tmi-dialog-list-item">
                                                <input
                                                    type="checkbox"
                                                    id={`milestone-${milestone.value}`}
                                                    checked={state.milestonesSelected.has(milestone.value)}
                                                    onChange={() => toggleMilestoneSelection(milestone.value)}
                                                />
                                                <label htmlFor={`milestone-${milestone.value}`} style={{ marginLeft: '8px', cursor: 'pointer' }}>
                                                    {milestone.display}
                                                </label>
                                            </div>
                                        ))}
                                </div>
                            </>
                        )}
                    </div>
                    <DialogActionsBar>
                        <button className="k-button k-button-md k-rounded-md" onClick={closeMilestonesDialog}>Close</button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.statusDlgOpen && (
                <Dialog
                    title={`Status for ${state.statusPacklistType?.packlistTypeCode}`}
                    onClose={closeStatusDialog}
                    width={1100}
                    height={600}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0', height: '460px' }}>
                        <div className="tmi-dialog-list-container tmi-grid-container">
                            {state.statusDlgError && (
                                <div className="tmi-dialog-list-error">
                                    {state.statusDlgError}
                                </div>
                            )}
                            {state.statusDlgLoading ? (
                                <div className="tmi-dialog-list-loading">
                                    <span className="k-icon k-font-icon k-i-loading" />
                                    Loading statuses...
                                </div>
                            ) : (
                                <>
                                    {(() => {
                                        // Apply filtering and sorting
                                        const filters = state.statusFilterTexts;
                                        const filteredData = state.statusData.filter(item => {
                                            return Object.entries(filters).every(([f, q]) => {
                                                if (!q) return true;
                                                const val = (item as any)[f];
                                                const ql = q.trim().toLowerCase();
                                                return String(val ?? '').toLowerCase().includes(ql);
                                            });
                                        });
                                        const sortedData = orderBy(filteredData, state.statusSort);

                                        return (
                                            <Grid
                                                className="Status-grid"
                                                data={sortedData}
                                                onRowClick={(e) => setState(prev => ({ ...prev, statusSelectedId: e.dataItem.PackListTypeStatusID }))}
                                            >
                                                <GridColumn
                                                    field="display"
                                                    title="Status"
                                                    width={250}
                                                    resizable={true}
                                                    headerCell={buildStatusHeaderCellRenderer('display', 'Status')}
                                                    cell={(props) => {
                                                        const isSelected = state.statusSelectedId === props.dataItem.PackListTypeStatusID;
                                                        return (
                                                            <td className={`tmi-dialog-grid-text-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}>
                                                                {props.dataItem.inEdit ? (
                                                                    <select
                                                                        className="k-input k-input-sm"
                                                                        value={props.dataItem.PackListStatusID ?? ''}
                                                                        onChange={(e) => {
                                                                            const selectedStatusId = e.target.value ? parseInt(e.target.value, 10) : null;
                                                                            const selectedStatus = selectedStatusId ? state.statusDropdownData.find(d => parseInt(d.value, 10) === selectedStatusId) : null;
                                                                            setState(prev => ({
                                                                                ...prev,
                                                                                statusData: prev.statusData.map(s =>
                                                                                    s.PackListTypeStatusID === props.dataItem.PackListTypeStatusID
                                                                                        ? { ...s, PackListStatusID: selectedStatusId, display: selectedStatus?.display || `Status ${selectedStatusId}` }
                                                                                        : s
                                                                                ),
                                                                            }))
                                                                        }}
                                                                        style={{ width: '100%' }}
                                                                    >
                                                                        <option value="">-- Select a Status --</option>
                                                                        {state.statusDropdownData
                                                                            .filter(d => {
                                                                                const statusId = parseInt(d.value, 10);
                                                                                const isCurrentStatus = props.dataItem.PackListStatusID === statusId;
                                                                                const isAlreadyAdded = state.statusData.some(row => row.PackListStatusID === statusId && row.PackListTypeStatusID !== props.dataItem.PackListTypeStatusID);
                                                                                return isCurrentStatus || !isAlreadyAdded;
                                                                            })
                                                                            .map((d) => (
                                                                                <option key={d.value} value={d.value}>
                                                                                    {d.display}
                                                                                </option>
                                                                            ))}
                                                                    </select>
                                                                ) : (
                                                                    props.dataItem.display
                                                                )}
                                                            </td>
                                                        );
                                                    }}
                                                />
                                                <GridColumn
                                                    field="PacklistTypeStatusOrder"
                                                    title="Order"
                                                    width={150}
                                                    resizable={true}
                                                    headerCell={buildStatusHeaderCellRenderer('PacklistTypeStatusOrder', 'Order')}
                                                    cell={(props) => {
                                                        const isSelected = state.statusSelectedId === props.dataItem.PackListTypeStatusID;
                                                        return (
                                                            <td className={`tmi-dialog-grid-center-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}>
                                                                {props.dataItem.inEdit ? (
                                                                    <input
                                                                        type="number"
                                                                        className="k-input k-input-sm"
                                                                        defaultValue={props.dataItem.PacklistTypeStatusOrder ?? ''}
                                                                        data-field="PacklistTypeStatusOrder"
                                                                        style={{ width: '100%' }}
                                                                    />
                                                                ) : (
                                                                    props.dataItem.PacklistTypeStatusOrder || ''
                                                                )}
                                                            </td>
                                                        );
                                                    }}
                                                />
                                                <GridColumn
                                                    field="LastChangedBy"
                                                    title="Changed By"
                                                    width={250}
                                                    resizable={true}
                                                    headerCell={buildStatusHeaderCellRenderer('LastChangedBy', 'Changed By')}
                                                    cell={(props) => (
                                                        <td className="tmi-dialog-grid-text-cell">
                                                            {props.dataItem.LastChangedBy}
                                                        </td>
                                                    )}
                                                />
                                                <GridColumn
                                                    field="LastChangedOn"
                                                    title="Changed On"
                                                    width={150}
                                                    resizable={true}
                                                    headerCell={buildStatusHeaderCellRenderer('LastChangedOn', 'Changed On')}
                                                    cell={(props) => (
                                                        <td className="tmi-dialog-grid-text-cell">
                                                            {props.dataItem.LastChangedOn ? new Date(props.dataItem.LastChangedOn).toLocaleDateString() : ''}
                                                        </td>
                                                    )}
                                                />
                                                <GridColumn
                                                    title="Actions"
                                                    width={100}
                                                    cell={(props) => (
                                                        <td style={{ padding: '4px', textAlign: 'center' }}>
                                                            {props.dataItem.inEdit ? (
                                                                <>
                                                                    <button
                                                                        className="k-button k-button-sm k-rounded-md k-button-solid-secondary tmi-dialog-action-button tmi-dialog-action-button-first"
                                                                        onClick={() => setState(prev => ({
                                                                            ...prev,
                                                                            statusEditingId: null,
                                                                            statusData: props.dataItem.PackListTypeStatusID === 0
                                                                                ? prev.statusData.filter(s => s.PackListTypeStatusID !== 0)
                                                                                : prev.statusData.map(s =>
                                                                                    s.PackListTypeStatusID === props.dataItem.PackListTypeStatusID
                                                                                        ? { ...s, inEdit: false }
                                                                                        : s
                                                                                )
                                                                        }))}
                                                                        disabled={state.statusDlgLoading}
                                                                        title="Cancel"
                                                                    >
                                                                        <span className="k-icon k-font-icon k-i-cancel" />
                                                                    </button>
                                                                    <button
                                                                        className="k-button k-button-sm k-rounded-md k-button-solid-success tmi-dialog-action-button tmi-dialog-action-button-next"
                                                                        onClick={() => saveStatusEdit(props.dataItem.PackListTypeStatusID)}
                                                                        disabled={state.statusDlgLoading}
                                                                        title="Save"
                                                                    >
                                                                        <span className="k-icon k-font-icon k-i-check" />
                                                                    </button>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <button
                                                                        className="k-button k-button-sm k-rounded-md k-button-solid-base tmi-dialog-action-button tmi-dialog-action-button-first"
                                                                        onClick={() => setState(prev => ({
                                                                            ...prev,
                                                                            statusEditingId: props.dataItem.PackListTypeStatusID,
                                                                            statusData: prev.statusData.map(s =>
                                                                                s.PackListTypeStatusID === props.dataItem.PackListTypeStatusID
                                                                                    ? { ...s, inEdit: true }
                                                                                    : s
                                                                            )
                                                                        }))}
                                                                        disabled={state.statusDlgLoading}
                                                                        title="Edit"
                                                                    >
                                                                        <span className="k-icon k-font-icon k-i-edit" />
                                                                    </button>
                                                                    <button
                                                                        className="k-button k-button-sm k-rounded-md k-button-solid-error tmi-dialog-action-button tmi-dialog-action-button-next"
                                                                        onClick={() => openStatusDeleteConfirm(props.dataItem.PackListTypeStatusID)}
                                                                        disabled={state.statusDlgLoading}
                                                                        title="Delete"
                                                                    >
                                                                        <span className="k-icon k-font-icon k-i-trash" />
                                                                    </button>
                                                                </>
                                                            )}
                                                        </td>
                                                    )}
                                                />
                                            </Grid>
                                        );
                                    })()}
                                    <button
                                        disabled={state.statusData.some(s => s.inEdit) || state.statusDlgLoading}
                                        className="k-button k-button-sm k-rounded-md k-button-solid-primary tmi-dialog-add-button"
                                        onClick={() => {
                                            const newStatus = {
                                                PackListTypeStatusID: 0,
                                                PackListStatusID: null,
                                                display: '',
                                                PackListTypeID: state.statusPacklistType!.packlistTypeID,
                                                PacklistTypeStatusOrder: null,
                                                LastChangedBy: '',
                                                LastChangedOn: null,
                                                isSelected: true,
                                                inEdit: true,
                                            };
                                            setState(prev => ({ ...prev, statusData: [...prev.statusData, newStatus] }));
                                        }}
                                        title="Add Status"
                                        style={{ marginTop: '8px' }}
                                    >
                                        <span className="k-icon k-font-icon k-i-plus" /> Add Status
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    <DialogActionsBar>
                        <button
                            className="k-button k-button-md k-rounded-md"
                            onClick={closeStatusDialog}
                        >
                            Close
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.statusDeleteConfirmId !== null && (
                <Dialog
                    title="Confirm Delete"
                    onClose={closeStatusDeleteConfirm}
                    width={420}
                >
                    <p style={{ padding: '8px 0' }}>
                        Are you sure you want to delete this status association?
                    </p>
                    {state.statusDeleteError && (
                        <div style={{ color: '#d32f2f', marginBottom: '12px', padding: '8px', backgroundColor: '#ffebee', borderRadius: '4px' }}>
                            {state.statusDeleteError}
                        </div>
                    )}
                    <DialogActionsBar>
                        <button 
                            className="k-button k-button-md k-rounded-md k-button-solid-error" 
                            onClick={confirmStatusDelete}
                            disabled={state.statusDeleteInProgress}
                        >
                            {state.statusDeleteInProgress ? 'Deleting...' : 'Delete'}
                        </button>
                        <button 
                            className="k-button k-button-md k-rounded-md" 
                            onClick={closeStatusDeleteConfirm}
                            disabled={state.statusDeleteInProgress}
                        >
                            Cancel
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.allowedItemTypesDlgOpen && state.allowedItemTypesPacklistType && (
                <Dialog
                    title={`Allowed Item Types for ${state.allowedItemTypesPacklistType.packlistTypeCode}`}
                    onClose={closeAllowedItemTypesDialog}
                    width={420}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0' }}>
                        <input
                            type="text"
                            placeholder="Filter item types..."
                            value={state.allowedItemTypesDlgFilterText}
                            onChange={handleAllowedItemTypesFilterChange}
                            className="tmi-dialog-list-filter"
                        />
                        <div className="tmi-dialog-list-container">
                            {state.allowedItemTypesDlgError && (
                                <div className="tmi-dialog-list-error">
                                    {state.allowedItemTypesDlgError}
                                </div>
                            )}
                            {state.allowedItemTypesDlgLoading && (
                                <div className="tmi-dialog-list-loading">
                                    <span className="k-icon k-font-icon k-i-loading" />
                                    <span>Loading item types...</span>
                                </div>
                            )}
                            {!state.allowedItemTypesDlgLoading && state.allowedItemTypesDropdownData.length === 0 && (
                                <div className="tmi-dialog-list-empty">No item types available</div>
                            )}
                            {!state.allowedItemTypesDlgLoading && state.allowedItemTypesDropdownData
                                .filter(itemType => itemType.display.toLowerCase().includes(state.allowedItemTypesDlgFilterText.toLowerCase()))
                                .map((itemType) => (
                                    <div key={itemType.value} className="tmi-dialog-list-item">
                                        <input
                                            type="checkbox"
                                            checked={state.allowedItemTypesSelected.has(itemType.value)}
                                            onChange={() => toggleItemTypeSelection(itemType.value)}
                                            id={`itemtype-${itemType.value}`}
                                        />
                                        <label htmlFor={`itemtype-${itemType.value}`}>
                                            {itemType.display}
                                        </label>
                                    </div>
                                ))}
                        </div>
                    </div>
                    <DialogActionsBar>
                        <button
                            className="k-button k-button-md k-rounded-md"
                            onClick={closeAllowedItemTypesDialog}
                        >
                            Close
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.uniqueGroupsDlgOpen && state.uniqueGroupsPacklistType && (
                <Dialog
                    title={`Unique Groups for ${state.uniqueGroupsPacklistType.packlistTypeCode}`}
                    onClose={closeUniqueGroupsDialog}
                    width={420}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0' }}>
                        <input
                            type="text"
                            placeholder="Filter unique groups..."
                            value={state.uniqueGroupsDlgFilterText}
                            onChange={handleUniqueGroupsFilterChange}
                            className="tmi-dialog-list-filter"
                        />
                        <div className="tmi-dialog-list-container">
                            {state.uniqueGroupsDlgError && (
                                <div className="tmi-dialog-list-error">
                                    {state.uniqueGroupsDlgError}
                                </div>
                            )}
                            {state.uniqueGroupsDlgLoading && (
                                <div className="tmi-dialog-list-loading">
                                    <span className="k-icon k-font-icon k-i-loading" />
                                    <span>Loading unique groups...</span>
                                </div>
                            )}
                            {!state.uniqueGroupsDlgLoading && state.uniqueGroupsDropdownData.length === 0 && (
                                <div className="tmi-dialog-list-empty">No unique groups available</div>
                            )}
                            {!state.uniqueGroupsDlgLoading && state.uniqueGroupsDropdownData
                                .filter(group => group.display.toLowerCase().includes(state.uniqueGroupsDlgFilterText.toLowerCase()))
                                .map((group) => (
                                    <div key={group.value} className="tmi-dialog-list-item">
                                        <input
                                            type="checkbox"
                                            checked={state.uniqueGroupsSelected.has(group.value)}
                                            onChange={() => toggleUniqueGroupSelection(group.value)}
                                            id={`uniquegroup-${group.value}`}
                                        />
                                        <label htmlFor={`uniquegroup-${group.value}`}>
                                            {group.display}
                                        </label>
                                    </div>
                                ))}
                        </div>
                    </div>
                    <DialogActionsBar>
                        <button
                            className="k-button k-button-md k-rounded-md"
                            onClick={closeUniqueGroupsDialog}
                        >
                            Close
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.userRolesDlgOpen && state.userRolesPacklistType && (
                <Dialog
                    title={`User Roles for ${state.userRolesPacklistType.packlistTypeCode}`}
                    onClose={closeUserRolesDialog}
                    width={420}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0' }}>
                        <input
                            type="text"
                            placeholder="Filter user roles..."
                            value={state.userRolesDlgFilterText}
                            onChange={handleUserRolesFilterChange}
                            className="tmi-dialog-list-filter"
                        />
                        <div className="tmi-dialog-list-container">
                            {state.userRolesDlgError && (
                                <div className="tmi-dialog-list-error">
                                    {state.userRolesDlgError}
                                </div>
                            )}
                            {state.userRolesDlgLoading && (
                                <div className="tmi-dialog-list-loading">
                                    <span className="k-icon k-font-icon k-i-loading" />
                                    <span>Loading user roles...</span>
                                </div>
                            )}
                            {!state.userRolesDlgLoading && state.userRolesDropdownData.length === 0 && (
                                <div className="tmi-dialog-list-empty">No user roles available</div>
                            )}
                            {!state.userRolesDlgLoading && state.userRolesDropdownData
                                .filter(role => role.display.toLowerCase().includes(state.userRolesDlgFilterText.toLowerCase()))
                                .map((role) => (
                                    <div key={role.value} className="tmi-dialog-list-item">
                                        <input
                                            type="checkbox"
                                            checked={state.userRolesSelected.has(role.value)}
                                            onChange={() => toggleUserRoleSelection(role.value)}
                                            id={`userrole-${role.value}`}
                                        />
                                        <label htmlFor={`userrole-${role.value}`}>
                                            {role.display}
                                        </label>
                                    </div>
                                ))}
                        </div>
                    </div>
                    <DialogActionsBar>
                        <button
                            className="k-button k-button-md k-rounded-md"
                            onClick={closeUserRolesDialog}
                        >
                            Close
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.allowedPacklistTypesDlgOpen && state.allowedPacklistTypesPacklistType && (
                <Dialog
                    title={`Allowed Packlist Types for ${state.allowedPacklistTypesPacklistType.packlistTypeCode}`}
                    onClose={closeAllowedPacklistTypesDialog}
                    width={1100}
                    height={600}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0', height: '460px' }}>
                        {state.allowedPacklistTypesDlgError && (
                            <div style={{ color: '#d32f2f', marginBottom: '8px', padding: '8px', backgroundColor: '#ffebee', borderRadius: '4px' }}>
                                {state.allowedPacklistTypesDlgError}
                            </div>
                        )}
                        {state.allowedPacklistTypesDlgLoading ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
                                <span className="k-icon k-font-icon k-i-loading" />
                            </div>
                        ) : (
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                {(() => {
                                    const filters = state.allowedPacklistTypesFilterTexts;
                                    const filteredData = state.allowedPacklistTypesData.filter(item => {
                                        return Object.entries(filters).every(([f, q]) => {
                                            if (!q) return true;
                                            const val = (item as any)[f];
                                            const ql = q.trim().toLowerCase();
                                            return String(val ?? '').toLowerCase().includes(ql);
                                        });
                                    });
                                    const sortedData = orderBy(filteredData, state.allowedPacklistTypesSort);

                                    return (
                                        <Grid
                                            style={{ flex: 1 }}
                                            data={sortedData}
                                            className="AllowedPacklistTypes-grid"
                                            dataItemKey="PackListTypeAllowedPackListTypesID"
                                            onRowClick={(e) => setState(prev => ({ ...prev, allowedPacklistTypesSelectedId: e.dataItem.PackListTypeAllowedPackListTypesID }))}
                                        >
                                    <GridColumn
                                        field="display"
                                        title="Packlist Type"
                                        width={300}
                                        cell={(props) => {
                                            const isSelected = state.allowedPacklistTypesSelectedId === props.dataItem.PackListTypeAllowedPackListTypesID;
                                            return (
                                                <td className={`tmi-dialog-grid-text-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}>
                                                    {props.dataItem.inEdit ? (
                                                        <select
                                                            className="k-input k-input-sm"
                                                            value={props.dataItem.AllowedPackListTypeID ?? ''}
                                                            onChange={(e) => {
                                                                const selectedPacklistTypeId = e.target.value ? parseInt(e.target.value, 10) : null;
                                                                const selectedPacklistType = selectedPacklistTypeId ? state.allowedPacklistTypesDropdownData.find(p => parseInt(p.value, 10) === selectedPacklistTypeId) : null;
                                                                setState(prev => ({
                                                                    ...prev,
                                                                    allowedPacklistTypesData: prev.allowedPacklistTypesData.map(r =>
                                                                        r.PackListTypeAllowedPackListTypesID === props.dataItem.PackListTypeAllowedPackListTypesID
                                                                            ? { ...r, AllowedPackListTypeID: selectedPacklistTypeId, display: selectedPacklistType?.display || '' }
                                                                            : r
                                                                    )
                                                                }))
                                                            }}
                                                            style={{ width: '100%' }}
                                                        >
                                                            <option value="">-- Select a Packlist Type --</option>
                                                            {state.allowedPacklistTypesDropdownData
                                                                .filter(p => {
                                                                    const id = parseInt(p.value, 10);
                                                                    const isCurrent = id === props.dataItem.AllowedPackListTypeID;
                                                                    const isAlreadyAdded = state.allowedPacklistTypesData.some(r => r.AllowedPackListTypeID === id && r.PackListTypeAllowedPackListTypesID !== props.dataItem.PackListTypeAllowedPackListTypesID);
                                                                    return isCurrent || !isAlreadyAdded;
                                                                })
                                                                .map((p) => (
                                                                    <option key={p.value} value={p.value}>
                                                                        {p.display}
                                                                    </option>
                                                                ))}
                                                        </select>
                                                    ) : (
                                                        props.dataItem.display
                                                    )}
                                                </td>
                                            );
                                        }}
                                    />
                                    <GridColumn
                                        field="AllowMultipleParentsOfSameType"
                                        title="Allow Multiple"
                                        width={120}
                                        cell={(props) => {
                                            const isSelected = state.allowedPacklistTypesSelectedId === props.dataItem.PackListTypeAllowedPackListTypesID;
                                            return (
                                                <td className={`tmi-dialog-grid-center-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}>
                                                    {props.dataItem.inEdit ? (
                                                        <input type="checkbox" defaultChecked={props.dataItem.AllowMultipleParentsOfSameType || false} />
                                                    ) : (
                                                        props.dataItem.AllowMultipleParentsOfSameType ? '✓' : ''
                                                    )}
                                                </td>
                                            );
                                        }}
                                    />
                                    <GridColumn
                                        width={80}
                                        filterable={false}
                                        sortable={false}
                                        cell={(props) => (
                                            <td style={{ padding: 0 }}>
                                                {props.dataItem.inEdit ? (
                                                    <>
                                                        <button
                                                            className="k-button k-button-sm k-rounded-md k-button-solid-secondary tmi-dialog-action-button tmi-dialog-action-button-first"
                                                            onClick={() => {
                                                                const updated = props.dataItem.PackListTypeAllowedPackListTypesID === 0
                                                                    ? state.allowedPacklistTypesData.filter(r => r.PackListTypeAllowedPackListTypesID !== 0)
                                                                    : state.allowedPacklistTypesData.map(r =>
                                                                        r.PackListTypeAllowedPackListTypesID === props.dataItem.PackListTypeAllowedPackListTypesID 
                                                                            ? { ...r, inEdit: false }
                                                                            : r
                                                                    );
                                                                setState(prev => ({ ...prev, allowedPacklistTypesEditingId: null, allowedPacklistTypesData: updated }));
                                                            }}
                                                            title="Cancel"
                                                        >
                                                            <span className="k-icon k-font-icon k-i-cancel" />
                                                        </button>
                                                        <button
                                                            className="k-button k-button-sm k-rounded-md k-button-solid-success tmi-dialog-action-button tmi-dialog-action-button-next"
                                                            onClick={(e) => {
                                                                const row = e.currentTarget.closest('tr');
                                                                const checkbox = row?.querySelector('input[type="checkbox"]') as HTMLInputElement;
                                                                
                                                                const allowMultiple = checkbox?.checked || false;
                                                                
                                                                setState(prev => ({
                                                                    ...prev,
                                                                    allowedPacklistTypesEditingId: null,
                                                                    allowedPacklistTypesData: prev.allowedPacklistTypesData.map(r =>
                                                                        r.PackListTypeAllowedPackListTypesID === props.dataItem.PackListTypeAllowedPackListTypesID
                                                                            ? {
                                                                                ...r,
                                                                                AllowMultipleParentsOfSameType: allowMultiple,
                                                                                inEdit: false
                                                                            }
                                                                            : r
                                                                    )
                                                                }));
                                                                saveAllowedPacklistType(props.dataItem.PackListTypeAllowedPackListTypesID, props.dataItem.AllowedPackListTypeID, allowMultiple);
                                                            }}
                                                            title="Save"
                                                        >
                                                            <span className="k-icon k-font-icon k-i-check" />
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <button
                                                            className="k-button k-button-sm k-rounded-md k-button-solid-base"
                                                            style={{ display: 'inline-flex', alignItems: 'center', margin: 0, padding: '6px 8px' }}
                                                            onClick={() => {
                                                                const updated = state.allowedPacklistTypesData.map(r =>
                                                                    r.PackListTypeAllowedPackListTypesID === props.dataItem.PackListTypeAllowedPackListTypesID 
                                                                        ? { ...r, inEdit: true }
                                                                        : r
                                                                );
                                                                setState(prev => ({ ...prev, allowedPacklistTypesEditingId: props.dataItem.PackListTypeAllowedPackListTypesID, allowedPacklistTypesData: updated }));
                                                            }}
                                                            title="Edit"
                                                        >
                                                            <span className="k-icon k-font-icon k-i-edit" />
                                                        </button>
                                                        <button
                                                            className="k-button k-button-sm k-rounded-md k-button-solid-error"
                                                            style={{ display: 'inline-flex', alignItems: 'center', margin: '0 0 0 4px', padding: '6px 8px' }}
                                                            onClick={() => deleteAllowedPacklistType(props.dataItem.PackListTypeAllowedPackListTypesID)}
                                                            title="Delete"
                                                        >
                                                            <span className="k-icon k-font-icon k-i-trash" />
                                                        </button>
                                                    </>
                                                )}
                                            </td>
                                        )}
                                    />
                                </Grid>
                                    );
                                })()}
                                <button
                                    disabled={state.allowedPacklistTypesData.some(a => a.inEdit)}
                                    className="k-button k-button-sm k-rounded-md k-button-solid-primary tmi-dialog-add-button"
                                    style={{ marginTop: '8px' }}
                                    onClick={() => {
                                        const newRecord = {
                                            PackListTypeAllowedPackListTypesID: 0,
                                            PackListTypeID: state.allowedPacklistTypesPacklistType!.packlistTypeID,
                                            AllowedPackListTypeID: null,
                                            display: '',
                                            AllowMultipleParentsOfSameType: false,
                                            LastChangedBy: '',
                                            LastChangedOn: null,
                                            inEdit: true
                                        };
                                        setState(prev => ({ ...prev, allowedPacklistTypesData: [...prev.allowedPacklistTypesData, newRecord] }));
                                    }}
                                >
                                    <span className="k-icon k-font-icon k-i-plus" style={{ marginRight: '4px' }} />
                                    Add Record
                                </button>
                            </div>
                        )}
                    </div>
                    <DialogActionsBar>
                        <button className="k-button k-button-md k-rounded-md" onClick={closeAllowedPacklistTypesDialog}>Close</button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.allowedPacklistTypesDeleteConfirmId !== null && (
                <Dialog
                    title="Confirm Delete"
                    onClose={closeAllowedPacklistTypeDeleteConfirm}
                    width={420}
                >
                    <p style={{ padding: '8px 0' }}>
                        Are you sure you want to delete this allowed packlist type association?
                    </p>
                    {state.allowedPacklistTypesDeleteError && (
                        <div style={{ color: '#d32f2f', marginBottom: '12px', padding: '8px', backgroundColor: '#ffebee', borderRadius: '4px' }}>
                            {state.allowedPacklistTypesDeleteError}
                        </div>
                    )}
                    <DialogActionsBar>
                        <button 
                            className="k-button k-button-md k-rounded-md k-button-solid-error" 
                            onClick={confirmAllowedPacklistTypeDelete}
                            disabled={state.allowedPacklistTypesDeleteInProgress}
                        >
                            {state.allowedPacklistTypesDeleteInProgress ? 'Deleting...' : 'Delete'}
                        </button>
                        <button 
                            className="k-button k-button-md k-rounded-md" 
                            onClick={closeAllowedPacklistTypeDeleteConfirm}
                            disabled={state.allowedPacklistTypesDeleteInProgress}
                        >
                            Cancel
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.hierarchyDlgOpen && state.hierarchyPacklistType && (
                <Dialog
                    title="Packlist Type Hierarchy"
                    onClose={closeHierarchyDialog}
                    width={1100}
                    height={680}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '8px 0', height: '540px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#fafafa' }}>
                            <div>
                                <strong>Root:</strong> {state.hierarchyPacklistType.description || `PackList Type ${state.hierarchyPacklistType.packlistTypeID}`}
                            </div>
                            <div style={{ display: 'flex', gap: '6px' }}>
                                <button
                                    type="button"
                                    className="k-button k-button-sm k-rounded-md"
                                    onClick={expandAllHierarchyNodes}
                                    disabled={state.hierarchyLoading}
                                >
                                    Expand All
                                </button>
                                <button
                                    type="button"
                                    className="k-button k-button-sm k-rounded-md"
                                    onClick={collapseAllHierarchyNodes}
                                    disabled={state.hierarchyLoading}
                                >
                                    Collapse All
                                </button>
                            </div>
                        </div>

                        {state.hierarchyError && (
                            <div style={{ color: '#d32f2f', marginBottom: '8px', padding: '8px', backgroundColor: '#ffebee', borderRadius: '4px' }}>
                                {state.hierarchyError}
                            </div>
                        )}

                        {state.hierarchyLoading ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
                                <span className="k-icon k-font-icon k-i-loading" />
                            </div>
                        ) : (
                            <div style={{ display: 'flex', gap: '12px', flex: 1, minHeight: 0 }}>
                                <div style={{ flex: 1, border: '1px solid #ddd', borderRadius: '4px', padding: '10px', overflow: 'auto' }}>
                                    <div style={{ fontWeight: 600, marginBottom: '8px' }}>Ancestors</div>
                                    {(() => {
                                        const rootId = state.hierarchyPacklistType?.packlistTypeID;
                                        if (!rootId) return null;
                                        const tree = renderHierarchyTree(rootId, 'ancestors', [rootId]);
                                        return tree || <div style={{ color: '#666' }}>No ancestors found.</div>;
                                    })()}
                                </div>
                                <div style={{ flex: 1, border: '1px solid #ddd', borderRadius: '4px', padding: '10px', overflow: 'auto' }}>
                                    <div style={{ fontWeight: 600, marginBottom: '8px' }}>Descendants</div>
                                    {(() => {
                                        const rootId = state.hierarchyPacklistType?.packlistTypeID;
                                        if (!rootId) return null;
                                        const tree = renderHierarchyTree(rootId, 'descendants', [rootId]);
                                        return tree || <div style={{ color: '#666' }}>No descendants found.</div>;
                                    })()}
                                </div>
                            </div>
                        )}
                    </div>
                    <DialogActionsBar>
                        <button className="k-button k-button-md k-rounded-md" onClick={closeHierarchyDialog}>Close</button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {/* Row Context Menu */}
            {state.menuDataItem && (
                <div
                    ref={menuRef}
                    onMouseLeave={handleMenuBlur}
                    onMouseEnter={() => {
                        if (menuBlurTimeoutRef.current) clearTimeout(menuBlurTimeoutRef.current);
                    }}
                    style={{
                        position: 'fixed',
                        left: state.menuPosition ? `${state.menuPosition.left}px` : '-9999px',
                        top: state.menuPosition ? `${state.menuPosition.top}px` : '-9999px',
                        backgroundColor: '#fff',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        zIndex: 1000,
                        minWidth: '200px',
                    }}
                >
                    <div
                        style={{
                            padding: '6px 12px',
                            fontSize: '14px',
                            cursor: 'pointer',
                        }}
                        onClick={() => handleMenuItemClick('editIcon')}
                        onMouseDown={e => e.preventDefault()}
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                        Edit Icon
                    </div>
                    <div
                        style={{
                            padding: '6px 12px',
                            fontSize: '14px',
                            cursor: 'pointer',
                        }}
                        onClick={() => handleMenuItemClick('attributes')}
                        onMouseDown={e => e.preventDefault()}
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                        Attributes
                    </div>
                    <div
                        style={{
                            padding: '6px 12px',
                            fontSize: '14px',
                            cursor: 'pointer',
                        }}
                        onClick={() => handleMenuItemClick('clientQueries')}
                        onMouseDown={e => e.preventDefault()}
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                        Client Queries
                    </div>
                    <div
                        style={{
                            padding: '6px 12px',
                            fontSize: '14px',
                            cursor: 'pointer',
                        }}
                        onClick={() => handleMenuItemClick('milestones')}
                        onMouseDown={e => e.preventDefault()}
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                        Milestones
                    </div>
                    <div
                        style={{
                            padding: '6px 12px',
                            fontSize: '14px',
                            cursor: 'pointer',
                        }}
                        onClick={() => handleMenuItemClick('status')}
                        onMouseDown={e => e.preventDefault()}
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                        Status
                    </div>
                    <div
                        style={{
                            padding: '6px 12px',
                            fontSize: '14px',
                            cursor: 'pointer',
                        }}
                        onClick={() => handleMenuItemClick('allowedItemTypes')}
                        onMouseDown={e => e.preventDefault()}
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                        Allowed Item Types
                    </div>
                    <div
                        style={{
                            padding: '6px 12px',
                            fontSize: '14px',
                            cursor: 'pointer',
                        }}
                        onClick={() => handleMenuItemClick('allowedPacklistTypes')}
                        onMouseDown={e => e.preventDefault()}
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                        Allowed Packlist Types
                    </div>
                    <div
                        style={{
                            padding: '6px 12px',
                            fontSize: '14px',
                            cursor: 'pointer',
                        }}
                        onClick={() => handleMenuItemClick('showHierarchy')}
                        onMouseDown={e => e.preventDefault()}
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                        Show Hierarchy
                    </div>
                    <div
                        style={{
                            padding: '6px 12px',
                            fontSize: '14px',
                            cursor: 'pointer',
                        }}
                        onClick={() => handleMenuItemClick('uniqueGroups')}
                        onMouseDown={e => e.preventDefault()}
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                        Unique Groups
                    </div>
                    <div
                        style={{
                            padding: '6px 12px',
                            fontSize: '14px',
                            cursor: 'pointer',
                        }}
                        onClick={() => handleMenuItemClick('userRoles')}
                        onMouseDown={e => e.preventDefault()}
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                        User Roles
                    </div>
                    <div
                        style={{
                            padding: '6px 12px',
                            fontSize: '14px',
                            cursor: 'pointer',
                        }}
                        onClick={() => handleMenuItemClick('packlistItemAttributes')}
                        onMouseDown={e => e.preventDefault()}
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                        Packlist Item Attributes
                    </div>
                </div>
            )}
        </div>
    );
}

export default connector(PacklistTypes);
